import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Switch, Button, IconButton } from '@/components/ui';
import {
  X,
  Calendar,
  Clock,
  User,
  Search,
  Check,
  AlertCircle,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Star,
  CheckCircle,
  Users,
  Navigation2,
  MapPin,
  Settings,
  Heart,
  Info,
  HelpCircle,
  Map as MapIcon,
  Car,
  Route,
  CalendarCheck,
  Sun,
  Sunrise,
  Sunset,
  Moon,
  LayoutGrid,
  MessageSquare,
  Mail,
  Bell,
  Send,
  Eye,
  EyeOff,
  Building2,
  CircleDot,
  Home,
  ArrowRight,
  FileText,
  KeyRound,
  Pencil,
  Loader2,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { ContactService } from '../../api/services/ContactService';
import { UserService } from '../../api/services/UserService';
import { contactAddressManager } from '../../business/contacts/ContactAddressManager';
import { isCustomerPreferredSlot, isProximitySlot } from '../../business/booking/slotType';
import { getDetourWarning, readDetourLimitMiles } from '../../business/booking/slotDetour';
import { resolveTeamAvailabilityEligibility } from '../../business/availability/teamAvailabilityEligibility';
import {
  getClosestNeighbor,
  proximityBadgePhrase,
  proximityBadgeTitle,
} from '../../business/booking/slotProximity';
import { AppointmentService } from '../../api/services/AppointmentService';
import { RequestsService } from '../../api/services/RequestsService';
import { PublicBookingFormService } from '../../api/services/PublicBookingFormService';
import { BookingFormService } from '../../api/services/BookingFormService';
import { Routemize_Constants_BookingFormStatus } from '../../api/models/Routemize_Constants_BookingFormStatus';
import { BookingFormSettingService } from '../../api/services/BookingFormSettingService';
import {
  clipPreferredTimeRangesToCompanyHours,
  filterPastTimeRanges,
  getCompanyDayScheduleForDate,
  getCompanyHoursForDate,
  getExactTimePickerBounds,
  getAssigneeHoursViolation,
  clampHHmmToRange,
  intersectHourRanges,
  isFlexiblePreferredTimeCode,
  resolvePreferredTimeOption,
} from '../../utils/preferredTimeRanges';
import { BookingFormUserService } from '../../api/services/BookingFormUserService';
import { BookingFormQuestionService } from '../../api/services/BookingFormQuestionService';
import type { Routemize_DTOs_BookingFormQuestionDto } from '../../api/models/Routemize_DTOs_BookingFormQuestionDto';
import { isQuestionVisible, getVisibleQuestionsInDisplayOrder } from '../../utils/questionConditionalLogic';
import { CustomerPreferencesService } from '../../api/services/CustomerPreferencesService';
import { googleMapsDistanceService } from '../../services/googleMapsDistanceService';
import { useTenantData, useTeamMembers } from '../../contexts/TenantDataContext';
import { formatUserAddressDisplay, resolveHomeUserAddress } from '../../utils/userAddressUtils';
import type { Routemize_DTOs_CreateContactDTO } from '../../api/models/Routemize_DTOs_CreateContactDTO';
import type { Routemize_DTOs_CreateAppointmentDTO } from '../../api/models/Routemize_DTOs_CreateAppointmentDTO';
import ModernProgressBar from '../shared/ModernProgressBar';
import SlotDiscoveryAnimation from './SlotDiscoveryAnimation';
import type { Routemize_DTOs_CreateRequestsDTO } from '../../api/models/Routemize_DTOs_CreateRequestsDTO';
import type { Routemize_DTOs_ContactDTO } from '../../api/models/Routemize_DTOs_ContactDTO';

/** A contact search hit as this modal displays it: the contact plus its addresses, under the shape the picker renders. */
type ContactSearchResult = {
  id: string;
  name: string;
  extraProperties?: { contactInfo?: Routemize_DTOs_ContactDTO; addresses?: Routemize_DTOs_ContactDTO['addresses'] };
};
import type { Routemize_DTOs_SlotSuggestionDTO } from '../../api/models/Routemize_DTOs_SlotSuggestionDTO';
import type { Routemize_DTOs_AppointmentTimelineResponse } from '../../api/models/Routemize_DTOs_AppointmentTimelineResponse';
import type { Routemize_DTOs_RequestsDTO } from '../../api/models/Routemize_DTOs_RequestsDTO';
import type { Routemize_DTOs_UpdateRequestsDTO } from '../../api/models/Routemize_DTOs_UpdateRequestsDTO';
import type { Routemize_DTOs_BookingFormDetailExternalDTO } from '../../api/models/Routemize_DTOs_BookingFormDetailExternalDTO';
import type { Routemize_DTOs_BookingFormSettingDto } from '../../api/models/Routemize_DTOs_BookingFormSettingDto';
import type { Routemize_DTOs_UserSmallDTO } from '../../api/models/Routemize_DTOs_UserSmallDTO';
import type { Routemize_DTOs_MasterData } from '../../api/models/Routemize_DTOs_MasterData';
import { useToast } from '../../contexts/ToastContext';
import GooglePlacesAddress from '../shared/GooglePlacesAddress';
import AddressInputWithMap from '../shared/AddressInputWithMap';
import { Routemize_Constants_ScheduleMethod } from '../../api/models/Routemize_Constants_ScheduleMethod';
import { Routemize_Constants_RequestStatus } from '../../api/models/Routemize_Constants_RequestStatus';
import { Routemize_Constants_SlotRejectionReason } from '../../api/models/Routemize_Constants_SlotRejectionReason';
import { Routemize_Constants_AppointmentSource } from '../../api/models/Routemize_Constants_AppointmentSource';
import { Routemize_Constants_FieldType } from '../../api/models/Routemize_Constants_FieldType';
import { Routemize_Constants_RequestOrigin } from '../../api/models/Routemize_Constants_RequestOrigin';
import { Routemize_Constants_EventNature } from '../../api/models/Routemize_Constants_EventNature';
import { OpenAPI as RoutemizeOpenAPI } from '../../api/core/OpenAPI';
import { request as routemizeRequest } from '../../api/core/request';
import type { InstantBookingPreferences } from '../../types/instantBookingPreferences';
import { resolveEventTypePreferences } from '../settings/smartAssignmentConfig';
import type { Territory } from '../settings/territory';
import { getTerritoryDisplayName } from '../settings/territory';
import {
  PreferredProviderDropdown,
  ProviderAvatar,
  formatProviderName,
  type PreferredProviderOption,
} from './PreferredProviderDropdown';
import { AssigneeChip, getAssigneeColors } from './AssigneeChip';
import TimeOfDayPicker from './TimeOfDayPicker';
import { BookingAlert } from './BookingAlert';
import { TeamMemberAvailabilityCards } from './TeamMemberAvailabilityCards';
import {
  teamMemberAvailabilityManager,
  type TeamMemberAvailabilityVM,
} from '../../business/availability/TeamMemberAvailabilityManager';
import {
  assigneeWorkWindowsManager,
  type AssigneeWorkDayVM,
} from '../../business/availability/AssigneeWorkWindowsManager';
import AvailabilityHelpModal, { type AvailabilityHelpChoice } from './AvailabilityHelpModal';
import { useRouty } from '../../contexts/RoutyContext';
import RoutyMark from '../routy/RoutyMark';
import {
  SmartAssignmentExplanationModal,
  type ExplanationKey as SmartAssignmentExplanationKey,
} from './SmartAssignmentExplanationModal';
import type { Routemize_DTOs_SchedulingResponse } from '../../api/models/Routemize_DTOs_SchedulingResponse';
import { MasterDataService } from '../../api/services/MasterDataService';
import {
  formatTimeString,
  formatWeekdayShort,
  formatWeekdayLong,
  formatMonthShort,
  formatMonthDayLong,
  formatDateLong,
  formatDateMedium,
  formatDateFull,
  formatDateKey,
  addMinutes,
  subtractMinutes,
  getTimeDifferenceInMinutes,
  addMinutesToTimeString,
  formatTime as formatDateTime,
  formatDurationMinutes,
  parseBackendDate,
  timeStringToMinutes,
  isCalendarToday,
  calendarDaysFromToday,
} from '../../utils/timeUtils';
import { tenantLocalToUtcDate, toIanaTimezone } from '../../utils/tenantTime';
import type { Routemize_DTOs_AppointmentListItem } from '../../api/models/Routemize_DTOs_AppointmentListItem';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import { getAvatarColor, getSpacedAvatarColors } from '../../utils/colorUtils';
import { isInternalLeadSource, stripInternalPrefix } from '../../utils/leadSourceUtils';
import { isValidEmail, isValidName, isValidNameLength, MAX_NAME_LENGTH } from '../../utils/validation';
import { resolveSchedulingErrorMessage, resolveSchedulingAlert, isRawTechnicalError, isRequestAlreadyBookedError, isExactTimeConstraintCode } from '../../utils/bookingErrorMessage';
import type { SchedulingAlert } from '../../utils/bookingErrorMessage';
import { useSmsProviderStatus } from '../../hooks/useSmsProviderStatus';
import { getBookingFormEventColor } from '../timeline/utils/constants';
import { distanceUnitForCountry, formatDistance } from '../../utils/geoDistance';
import { normalizeServiceAreaCode, isValidServiceAreaCode } from '../../utils/addressRegions';
import { isValidEntityId, resolveRequestAddressFields, resolveContactAddressFromDto, type RequestAddressFields } from '../../utils/requestAddressUtils';


interface CreateBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (bookingData: any) => void;
  prefilledDate?: string;
  prefilledStartTime?: string;
  prefilledEndTime?: string;
  prefilledPreferredTime?: string; // Preferred time of day ("09:00", "13:00", "17:00", or "")
  assignedUserId?: string; // Team member ID for manual bookings
  slotConstraints?: import('../timeline/types').SlotConstraints; // Drive time constraints for the slot
  prevAppointment?: any; // Previous appointment for drive time calculation
  nextAppointment?: any; // Next appointment for drive time calculation
  prefilledEventTypeId?: string; // Booking form ID to auto-select (skips event selection step)
  existingRequestId?: string; // ID of request being scheduled (from Smart Booking)
  // Pre-filled customer data from request
  prefilledCustomer?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    preferredSecondaryDate?: string;
    contactId?: string; // Existing contact ID to use (skips contact creation)
    contactAddressId?: string; // Existing address ID to use
  };
  // When true, skip the customer step and start at event selection (for requests with existing customer data)
  skipCustomerStep?: boolean;
  // When true, skip directly to slot loading (customer, service, and preferences already known)
  skipToSlotSelection?: boolean;
}

// Format phone number as (XXX) XXX-XXXX
const formatUSPhone = (value: string): string => {
  let digits = value.replace(/\D/g, '');
  // Strip leading country code '1' if more than 10 digits
  if (digits.length > 10 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  const limited = digits.slice(0, 10);
  if (limited.length === 0) return '';
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
};

// Helper function to add minutes to a time string
const addMinutesToTime = (timeStr: string, minutes: number): string => {
  const [hours, mins] = timeStr.split(':').map(Number);
  const totalMinutes = hours * 60 + mins + minutes;
  const newHours = Math.floor(totalMinutes / 60) % 24;
  const newMins = totalMinutes % 60;
  return `${String(newHours).padStart(2, '0')}:${String(newMins).padStart(2, '0')}`;
};

// Helper to parse date strings as local dates (not UTC)
// This fixes the timezone issue where "2025-12-11" would show as Dec 10 in local time
const parseLocalDate = (dateString: string | undefined | null): Date | null => {
  if (!dateString) return null;
  
  // If it's a date-only string (YYYY-MM-DD), parse it as local time
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }
  
  // For ISO strings with time, let the browser handle it
  return new Date(dateString);
};

// Helper to format a Date object to YYYY-MM-DD string using local timezone
// This prevents the off-by-one-day issue caused by toISOString() converting to UTC
const formatToLocalDateString = (date: Date | null): string => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Conflict result returned by the exact-time picker. There are two
 * distinct failure modes we want to surface to the admin so they can pick
 * a different time intelligently:
 *
 *  - `overlap`: the picked window literally intersects an existing
 *    appointment's [start, end) window. Hard conflict.
 *  - `drive-time`: the picked window doesn't overlap, but a neighbouring
 *    appointment is too far away — driving there (or driving out) would
 *    eat into the booked time. Reported with the deficit in minutes and
 *    the neighbour's name so the admin understands the geography.
 */
interface ExactTimeConflict {
  kind: 'overlap' | 'drive-time';
  withName: string;
  withStartIso: string;
  withEndIso: string;
  /** Minutes of drive deficit when `kind === 'drive-time'`. */
  deficitMinutes?: number;
  /** Which side ("from" / "to" the neighbour) is too tight. */
  side?: 'before' | 'after';
}

/**
 * Real driving minutes between two stops, from the same Google Maps service
 * that supplies the travel times shown on the slot cards. Returns null when
 * Maps can't answer, which callers must treat as "no opinion" rather than
 * substituting an estimate — a straight-line guess here disagrees with the
 * scheduling engine badly enough to reject slots the engine recommended.
 */
const fetchDriveMinutes = async (
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): Promise<number | null> => {
  const leg = await googleMapsDistanceService.calculateDrivingDistance(a, b);
  return leg ? Math.round(leg.duration) : null;
};

// Find-or-create for contact addresses lives in ContactAddressManager so the
// create and edit flows share the same duplicate-safe logic.
const findOrCreateContactAddress = (params: {
  contactId: string;
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  zipCode: string;
  country?: string | null;
}): Promise<string | null> => contactAddressManager.findOrCreateContactAddress(params);


type ProximityTier = {
  key: 'excellent' | 'great' | 'good';
  badgeClass: string;
  hookTextClass: string;
  borderClass: string;
  bgClass: string;
  radioBorderClass: string;
};

/**
 * Map a closest-neighbor distance to a visual tier so slots that sit
 * meaningfully close to other appointments get highlighted instead of
 * blending into the default styling.
 *
 * Tiers:
 *   < 5 mi   → emerald (a real win — call it out)
 *   5–10 mi  → green (still notably close)
 *   10–15 mi → amber (worth flagging, but no longer a win)
 *   > 15 mi  → null (let the caller fall back to its default style)
 */
const getProximityTier = (closestMiles: number | null | undefined): ProximityTier | null => {
  if (closestMiles == null || !Number.isFinite(closestMiles) || closestMiles <= 0) return null;
  if (closestMiles < 5) {
    return {
      key: 'excellent',
      badgeClass: 'text-emerald-700 bg-emerald-100 border border-emerald-200',
      hookTextClass: 'text-emerald-700/90',
      borderClass: 'border-emerald-300 hover:border-emerald-400',
      bgClass: 'bg-emerald-50/40',
      radioBorderClass: 'border-emerald-300',
    };
  }
  if (closestMiles < 10) {
    return {
      key: 'great',
      badgeClass: 'text-green-700 bg-green-50 border border-green-200',
      hookTextClass: 'text-green-700/90',
      borderClass: 'border-green-300 hover:border-green-400',
      bgClass: 'bg-green-50/30',
      radioBorderClass: 'border-green-300',
    };
  }
  if (closestMiles < 15) {
    return {
      key: 'good',
      badgeClass: 'text-amber-700 bg-amber-50 border border-amber-200',
      hookTextClass: 'text-amber-700/90',
      borderClass: 'border-amber-300 hover:border-amber-400',
      bgClass: 'bg-amber-50/30',
      radioBorderClass: 'border-amber-300',
    };
  }
  return null;
};

/**
 * One identity color per provider on a booking form, spaced across the
 * palette rather than hashed. A hashed palette can hand teammates
 * near-identical blues, which is how an operator ends up booking the wrong
 * person's calendar. Every surface that names a provider (service picker,
 * dropdown, slot chips, confirmation) reads from this map so a given
 * teammate keeps the same color for the whole flow.
 */
const buildAssigneeColorMap = (
  users: ReadonlyArray<{ id?: string }> | null | undefined
): Map<string, string> => {
  const ids = (users ?? []).map(u => u.id).filter((id): id is string => Boolean(id));
  const colors = getSpacedAvatarColors(ids.length);
  return new Map(ids.map((id, i) => [id, colors[i]]));
};

/**
 * Calls `GET /api/app/routemization/getSlotsWithResponse/{requestId}` like
 * the generated `RoutemizationService.getApiAppRoutemizationGetSlotsWithResponse`
 * does, but additionally forwards a `PreferredUserId` query param so the
 * backend can filter slots to a specific provider when the operator picks
 * a manual override on a smart-assignment form (mirrors the equivalent
 * param on `getRescheduleSlots`). The generated client doesn't yet expose
 * this param; once the backend's OpenAPI spec exposes it on this endpoint
 * and the proxies are regenerated, this helper can collapse back to the
 * typed call.
 */
async function fetchSlotsWithPreferredUser(
  requestId: string,
  options?: { preferredUserId?: string; bypassServiceArea?: boolean }
): Promise<Routemize_DTOs_SchedulingResponse> {
  return routemizeRequest<Routemize_DTOs_SchedulingResponse>(RoutemizeOpenAPI, {
    method: 'GET',
    url: '/api/app/routemization/getSlotsWithResponse/{requestId}',
    path: { 'requestId': requestId },
    query: {
      'bypassServiceArea': options?.bypassServiceArea ?? false,
      // Only send when truthy — undefined values are dropped by the
      // OpenAPI request layer, matching how the typed generator handles
      // optional query params elsewhere.
      'PreferredUserId': options?.preferredUserId || undefined,
    },
    errors: {
      400: 'Bad Request',
      401: 'Unauthorized',
      403: 'Forbidden',
      404: 'Not Found',
      500: 'Internal Server Error',
      501: 'Not Implemented',
    },
  });
}

const CreateBookingModal: React.FC<CreateBookingModalProps> = ({
  isOpen,
  onClose,
  onSave,
  prefilledDate,
  prefilledStartTime,
  prefilledEndTime,
  prefilledPreferredTime,
  assignedUserId,
  slotConstraints,
  prevAppointment,
  nextAppointment,
  prefilledEventTypeId,
  existingRequestId,
  prefilledCustomer,
  skipCustomerStep = false,
  skipToSlotSelection = false
}) => {
  const { success: showSuccess, error: showError, info: showInfo } = useToast();
  const { isConnected: smsProviderConnected } = useSmsProviderStatus();
  const { companyLocations, companySettings, workSchedule, companyTimezone: companyTzRaw, country } = useTenantData();
  // Lets the slot picker hand "why isn't my day showing?" straight to Routy.
  const { askRouty, isAvailable: isRoutyAvailable } = useRouty();
  const { assignableTeamMembers } = useTeamMembers();
  const distUnit = distanceUnitForCountry(country);
  // Resolve once to an IANA zone so the exact-time picker (and any other
  // tenant-aware conversions) always have a stable, valid timezone string.
  const companyTimezone = useMemo(() => toIanaTimezone(companyTzRaw), [companyTzRaw]);
  const customerPrefsRef = useRef<any>(null);
  const leadSourceSectionRef = useRef<HTMLDivElement>(null);

  // Load company communication defaults when modal opens
  useEffect(() => {
    if (!isOpen) return;
    userModifiedPrefsRef.current = false;
    let cancelled = false;
    CustomerPreferencesService.getApiAppCustomerPreferencesGet()
      .then((prefs) => {
        if (cancelled || userModifiedPrefsRef.current) return;
        customerPrefsRef.current = prefs;
        setSendSmsConfirmation(prefs.customer_SendSmsConfirmation ?? false);
        setSendEmailConfirmation(prefs.customer_SendEmailConfirmation ?? true);
        setSendSmsReminders(prefs.customer_SendSmsReminders ?? false);
        setSendEmailReminders(prefs.customer_SendEmailReminders ?? true);
        // Capture the day's start/end anchors so the route overview can backfill
        // the home/office legs the backend leaves blank (see computedHomeLegs).
        const routePrefs = prefs.routePreferences;
        if (routePrefs) {
          setRouteStartLocation((routePrefs.defaultStartingLocation as 'home' | 'office') || 'office');
          setRouteEndLocation((routePrefs.defaultEndingLocation as 'home' | 'office') || 'home');
        }
      }).catch((error) => {
      });
    return () => { cancelled = true; };
  }, [isOpen]);
  // Step management - start at appropriate step based on skip props
  const [currentStep, setCurrentStep] = useState<'customer' | 'event' | 'preferences' | 'questions' | 'booking' | 'confirmation'>(
    skipToSlotSelection ? 'booking' : (skipCustomerStep ? 'event' : 'customer')
  );
  const [completedSteps, setCompletedSteps] = useState<string[]>(
    skipToSlotSelection ? ['customer', 'event', 'preferences'] : (skipCustomerStep ? ['customer'] : [])
  );
  const [isCarMoving, setIsCarMoving] = useState(false);
  const prevStepRef = useRef(currentStep);
  const userModifiedPrefsRef = useRef(false);

  // Detect step changes and trigger smoke effect
  useEffect(() => {
    if (prevStepRef.current !== currentStep) {
      setIsCarMoving(true);
      prevStepRef.current = currentStep;
      
      // Stop the smoke after the transition completes
      const timer = setTimeout(() => {
        setIsCarMoving(false);
      }, 500); // Match the car transition duration
      
      return () => clearTimeout(timer);
    }
  }, [currentStep]);
  
  // Drive time validation for slot constraints
  const [driveTimeWarning, setDriveTimeWarning] = useState<string | null>(null);

  // Customer details - initialize with prefilled data if available
  const [firstName, setFirstName] = useState(prefilledCustomer?.firstName || '');
  const [lastName, setLastName] = useState(prefilledCustomer?.lastName || '');
  const [email, setEmail] = useState(prefilledCustomer?.email || '');
  const [phone, setPhone] = useState(formatUSPhone(prefilledCustomer?.phone || ''));
  const [isBusiness, setIsBusiness] = useState(false);
  const [businessName, setBusinessName] = useState('');
  const [address, setAddress] = useState(prefilledCustomer?.address || '');
  const [address2, setAddress2] = useState(prefilledCustomer?.address2 || '');
  const [city, setCity] = useState(prefilledCustomer?.city || '');
  const [state, setState] = useState(prefilledCustomer?.state || '');
  const [zipCode, setZipCode] = useState(prefilledCustomer?.zipCode || '');
  
  // Update customer details when props change (for when modal is opened with pre-filled data)
  const prefilledCustomerKey = prefilledCustomer ? JSON.stringify(prefilledCustomer) : '';
  useEffect(() => {
    if (isOpen && prefilledCustomer) {
      setFirstName(prefilledCustomer.firstName || '');
      setLastName(prefilledCustomer.lastName || '');
      setEmail(prefilledCustomer.email || '');
      setPhone(formatUSPhone(prefilledCustomer.phone || ''));
      setAddress(prefilledCustomer.address || '');
      setAddress2(prefilledCustomer.address2 || '');
      setCity(prefilledCustomer.city || '');
      setState(prefilledCustomer.state || '');
      setZipCode(prefilledCustomer.zipCode || '');
      if (prefilledCustomer.contactId) {
        setCreatedContactId(prefilledCustomer.contactId);
      }
      if (isValidEntityId(prefilledCustomer.contactAddressId)) {
        setCreatedContactAddressId(prefilledCustomer.contactAddressId);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, prefilledCustomerKey]);
  
  // Contact search and selection
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [searchingContacts, setSearchingContacts] = useState(false);
  const [searchedContacts, setSearchedContacts] = useState<ContactSearchResult[]>([]);
  const [selectedContact, setSelectedContact] = useState<ContactSearchResult | null>(null);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  
  // Event type details. `nextUpUserId` / `nextUpUserName` come from the
  // backend's `getList` response on equal-rotation forms — they identify
  // who would be assigned if the next booking landed right now. Used to
  // surface "Next up: …" copy in the rotation summary card.
  const [eventType, setEventType] = useState<{
    id: string;
    name: string;
    duration: string;
    durationMinutes: number;
    description: string;
    isRoutemize: boolean;
    eventNature?: string;
    assignedUsers?: Routemize_DTOs_UserSmallDTO[];
    nextUpUserId?: string | null;
    nextUpUserName?: string | null;
  } | null>(null);
  
  
  // Helper function to convert time codes to text labels
  const convertTimeCodeToLabel = (timeCode: string): string => {
    if (timeCode === '09:00') return 'Morning';
    if (timeCode === '13:00') return 'Afternoon';
    if (timeCode === '17:00') return 'Evening';
    return timeCode; // Return as-is if already a text label or empty string
  };
  
  // Routemize preferences - initialize with prefilled values
  const [preferredDate, setPreferredDate] = useState(prefilledDate || '');
  const [preferredSecondaryDate, setPreferredSecondaryDate] = useState(prefilledCustomer?.preferredSecondaryDate || '');
  const [preferredTime, setPreferredTime] = useState(prefilledPreferredTime || prefilledStartTime || '');
  
  // Update preferences when props change (for when modal is opened with pre-filled data)
  useEffect(() => {
    if (isOpen) {
      if (prefilledDate) {
        setPreferredDate(prefilledDate);
      }
      if (prefilledCustomer?.preferredSecondaryDate) {
        setPreferredSecondaryDate(prefilledCustomer.preferredSecondaryDate);
      }
      if (prefilledPreferredTime) {
        setPreferredTime(prefilledPreferredTime);
      }
    }
  }, [isOpen, prefilledDate, prefilledCustomer?.preferredSecondaryDate, prefilledPreferredTime]);

  // Raw preferred time ranges from master data (clipped to form hours below)
  const [baseTimeRangeOptions, setBaseTimeRangeOptions] = useState<
    Array<{ code: string; text: string; helpText: string; start?: string; end?: string }>
  >([]);
  const [loadingTimeRanges, setLoadingTimeRanges] = useState(true);

  const [notes, setNotes] = useState('');
  const [accessCode, setAccessCode] = useState('');
  // Notes + access code live behind a disclosure on the confirmation step.
  const [confirmationExtrasOpen, setConfirmationExtrasOpen] = useState(false);
  
  // Communication preferences — initialized to sensible defaults, overridden by company settings on load
  const [sendSmsConfirmation, setSendSmsConfirmation] = useState(false);
  const [sendEmailConfirmation, setSendEmailConfirmation] = useState(true);
  const [sendSmsReminders, setSendSmsReminders] = useState(false);
  const [sendEmailReminders, setSendEmailReminders] = useState(true);

  const [selectedSlot, setSelectedSlot] = useState<Routemize_DTOs_SlotSuggestionDTO | null>(null);
  const [bookingForms, setBookingForms] = useState<any[]>([]);
  const [bookingFormSettings, setBookingFormSettings] = useState<{[key: string]: Routemize_DTOs_BookingFormSettingDto}>({});
  const [bookingFormUsers, setBookingFormUsers] = useState<{[key: string]: Routemize_DTOs_UserSmallDTO[]}>({});
  const [assignableUserIds, setAssignableUserIds] = useState<Set<string>>(new Set());
  const [bookingFormDetails, setBookingFormDetails] = useState<{[key: string]: Routemize_DTOs_BookingFormDetailExternalDTO}>({});

  useEffect(() => {
    if (!isOpen) return;
    // Assignable roster comes from the cached TeamMembersContext; this only
    // needs the set of ids (home addresses are still loaded lazily per user).
    setAssignableUserIds(
      new Set(assignableTeamMembers.map((m) => m.id).filter((id): id is string => Boolean(id)))
    );
  }, [isOpen, assignableTeamMembers]);

  useEffect(() => {
    if (assignableUserIds.size === 0) return;
    setBookingFormUsers((prev) => {
      const next: typeof prev = {};
      for (const [formId, users] of Object.entries(prev)) {
        next[formId] = users.filter(
          (u) => u.id && assignableUserIds.has(u.id)
        );
      }
      return next;
    });
  }, [assignableUserIds]);
  const [bookingFormQuestions, setBookingFormQuestions] = useState<{[key: string]: Routemize_DTOs_BookingFormQuestionDto[]}>({});
  const [isLoadingBookingForms, setIsLoadingBookingForms] = useState(false);
  const bookingFormsLoadedRef = useRef(false);

  // Lead source state
  const [selectedLeadSource, setSelectedLeadSource] = useState('');
  const [availableLeadSources, setAvailableLeadSources] = useState<Routemize_DTOs_MasterData[]>([]);
  const [loadingLeadSources, setLoadingLeadSources] = useState(false);

  // Track original preferences to detect changes
  const [originalPreferences, setOriginalPreferences] = useState<{
    date: string;
    secondaryDate: string;
    time: string;
  }>({ date: '', secondaryDate: '', time: '' });
  
  // Show/hide preferences editor
  const [showPreferencesEditor, setShowPreferencesEditor] = useState(false);
  
  // Show/hide ultimate slot details
  const [showUltimateDetails, setShowUltimateDetails] = useState(false);

  // Determine if this is a manual booking (user picked a specific slot + team member)
  const isManualBooking = Boolean(assignedUserId && prefilledDate && prefilledStartTime && prefilledEndTime);

  // Routemize flow states - start loading if skipping to slot selection
  const [isRoutemizing, setIsRoutemizing] = useState(skipToSlotSelection);
  const [userChangedPreferences, setUserChangedPreferences] = useState(false);
  // Preferred provider for smart-assignment (round-robin) forms. When set, the
  // request's `assignedUserId` is updated to this id so GetSlots targets that
  // user only — same semantics as the reschedule flow's preferred provider.
  const [preferredAssigneeId, setPreferredAssigneeId] = useState<string>('');
  // Tracks whether the operator has manually changed the preferred provider so
  // the auto-default effect doesn't clobber their pick when the form's user
  // list refreshes.
  const userChangedPreferredAssignee = useRef(false);
  // Currently-open smart-assignment rule explanation (clicking a Smart
  // override / Proximity badge opens a mini modal mirroring the one in
  // SmartAssignmentPanel). null = closed.
  const [openSmartAssignmentExplanation, setOpenSmartAssignmentExplanation] =
    useState<SmartAssignmentExplanationKey | null>(null);
  const [showAvailableSlots, setShowAvailableSlots] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<Routemize_DTOs_SlotSuggestionDTO[]>([]);

  // ── Exact-time override state ──
  // Lets the admin bypass the recommended grid and book a specific start
  // time (e.g. "1:00 PM exactly"). The picker validates against the
  // assignee's calendar in real time and feeds the chosen time back
  // through the existing slot-selection pipeline.
  const [exactExpanded, setExactExpanded] = useState(false);
  const [exactDate, setExactDate] = useState<string>('');
  const [exactTime, setExactTime] = useState<string>('');
  // End time is editable too, but defaults to start + event duration.
  // Once the admin touches it we stop auto-shifting it so a manual choice
  // like "shorten the visit by 15 minutes" sticks across start-time tweaks.
  const [exactEndTime, setExactEndTime] = useState<string>('');
  const exactEndTouchedRef = useRef(false);
  const [exactConflict, setExactConflict] = useState<ExactTimeConflict | null>(null);
  const [exactValidating, setExactValidating] = useState(false);
  const exactValidateRequestRef = useRef(0);
  // Per-member availability shown inside the exact-time picker so the operator can see who is free at
  // the chosen time and pick an available salesperson without leaving the booking flow (ClickUp 86bc8867f).
  const [teamAvailability, setTeamAvailability] = useState<TeamMemberAvailabilityVM[]>([]);
  const [teamAvailabilityLoading, setTeamAvailabilityLoading] = useState(false);
  const teamAvailabilityRequestRef = useRef(0);
  // The exact-time assignee's effective hours for the picked date (per-date availability override →
  // recurring hours → company schedule). Bounds and validates the picker so an extended day can be
  // booked (ClickUp 86bcj7m6e). Stored with the `assignee|date` key it was resolved for; `day` is null
  // when the fetch failed, which falls back to company hours.
  const [exactAssigneeDayResult, setExactAssigneeDayResult] = useState<{
    key: string;
    day: AssigneeWorkDayVM | null;
  } | null>(null);
  const exactAssigneeDayRequestRef = useRef(0);
  /**
   * Slots returned by GetSlots are staged here while the "Slot Discovery"
   * animation runs through its phases. The slots screen is only swapped in
   * once the animation's `onComplete` fires (via `applyPendingSlots`). This
   * mirrors how `BulkRouteModal` gates its review step on `animationDone`
   * so the counter tick-up gets to play.
   */
  const [pendingSlotsData, setPendingSlotsData] = useState<{
    slots: Routemize_DTOs_SlotSuggestionDTO[];
    preferences: { date: string; secondaryDate: string; time: string };
    toastInfo?: string;
    afterServiceAreaOverride?: boolean;
    schedulingResponse?: Routemize_DTOs_SchedulingResponse;
  } | null>(null);
  const [createdRequestId, setCreatedRequestId] = useState<string | null>(null);
  const [createdContactId, setCreatedContactId] = useState<string | null>(
    skipCustomerStep && prefilledCustomer?.contactId ? prefilledCustomer.contactId : null
  );
  const [createdContactAddressId, setCreatedContactAddressId] = useState<string | null>(
    skipCustomerStep && prefilledCustomer?.contactAddressId ? prefilledCustomer.contactAddressId : null
  );
  const [appointmentCreated, setAppointmentCreated] = useState(false); // Track if appointment was successfully created

  // Track whether we modified the existing request's scheduleMethod so we can revert on close
  const [requestModifiedForRoutemize, setRequestModifiedForRoutemize] = useState(false);
  const [originalScheduleMethod, setOriginalScheduleMethod] = useState<Routemize_Constants_ScheduleMethod | null>(null);
  // Status the request had before we ran the Routemize flow. GetSlots/preview advance
  // the request into granular "live booking" statuses server-side; if the operator
  // abandons without booking we restore this so the request doesn't appear as a live
  // customer booking on the Requests board.
  const [originalStatus, setOriginalStatus] = useState<Routemize_Constants_RequestStatus | null>(null);
  // True once we've run a slot-generation pass against an existing request. GetSlots
  // can advance the request's status regardless of whether we also PUT preference
  // updates, so this (not requestModifiedForRoutemize) gates the status restore.
  const [requestStatusMayBeAdvanced, setRequestStatusMayBeAdvanced] = useState(false);

  // Existing request detection (for reusing pending requests like public flow)
  const [existingRequestForContact, setExistingRequestForContact] = useState<Routemize_DTOs_RequestsDTO | null>(null);
  const [checkingExistingRequest, setCheckingExistingRequest] = useState(false);

  // Debug section toggle
  const [showDebugSection, setShowDebugSection] = useState(false);
  const [routeOverviewExpanded, setRouteOverviewExpanded] = useState<Record<string, boolean>>({});
  const [slotDetailsExpanded, setSlotDetailsExpanded] = useState<Record<string, boolean>>({});

  // Quick Audit modal state
  const [auditModal, setAuditModal] = useState<{
    isOpen: boolean;
    slotDate: string;
    slotDateFormatted: string;
    userId: string;
    teamMemberName: string;
    slotType: 'business' | 'preference' | 'closest';
    slotTypeLabel: string;
    slotTypeDescription: string;
    startingAddress: string;
    endingAddress: string;
    hypotheticalSlot: { 
      startTime: string; 
      endTime: string; 
      address: string;
      driveTimeFromPrev: number | null;
      driveTimeToPrev: number | null;
      distanceFromPrev: number | null;
      distanceToNext: number | null;
    };
    // Full slot data for building timeline from slot when API fails
    slotData: Routemize_DTOs_SlotSuggestionDTO;
  } | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [timelineData, setTimelineData] = useState<Routemize_DTOs_AppointmentTimelineResponse | null>(null);

  // Slot type configurations for audit modal
  const slotTypeConfig = {
    business: {
      label: 'Top Choice',
      description: 'Optimizes route efficiency by fitting near existing appointments.',
      color: 'green'
    },
    preference: {
      label: "Customer Preference",
      description: "Matches your customer's preferred date and time window.",
      color: 'purple'
    },
    closest: {
      label: 'Closest Next',
      description: 'Minimizes drive time by fitting near an existing appointment.',
      color: 'teal'
    }
  };

  // Open audit modal and fetch timeline data
  const openAuditModal = async (slot: Routemize_DTOs_SlotSuggestionDTO, slotType: 'business' | 'preference' | 'closest') => {
    const slotDateStr = slot.suggestedDate?.split('T')[0] || '';
    const slotDateObj = slot.suggestedDate ? new Date(slot.suggestedDate) : new Date();
    const slotDateFormatted = slotDateObj.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric' 
    });
    
    const fullAddr = [
      slot.addressLine1,
      slot.addressLine2,
      slot.city,
      slot.state,
      slot.zipCode
    ].filter(Boolean).join(', ') || 'Address not available';
    
    const tmName = slot.userDetail
      ? `${slot.userDetail.firstName || ''} ${slot.userDetail.lastName || ''}`.trim() || 'Unassigned'
      : 'Unassigned';
    
    const config = slotTypeConfig[slotType];
    
    // Set initial state with loading
    setAuditModal({
      isOpen: true,
      slotDate: slotDateStr,
      slotDateFormatted,
      userId: slot.assignedUserId || '',
      teamMemberName: tmName,
      slotType,
      slotTypeLabel: config.label,
      slotTypeDescription: config.description,
      startingAddress: 'Loading...',
      endingAddress: 'Loading...',
      hypotheticalSlot: (() => {
        const auditPrevLeg = resolvePrevLeg(slot);
        const auditNextLeg = resolveNextLeg(slot);
        return {
          startTime: slot.startTime || '',
          endTime: slot.endTime || '',
          address: fullAddr,
          driveTimeFromPrev: auditPrevLeg.minutes ?? null,
          driveTimeToPrev: auditNextLeg.minutes ?? null,
          distanceFromPrev: auditPrevLeg.miles ?? null,
          distanceToNext: auditNextLeg.miles ?? null,
        };
      })(),
      slotData: slot
    });
    
    setAuditLoading(true);
    setTimelineData(null);
    
    try {
      // Fetch timeline, route preferences, office locations, and user details in parallel
      // Each call has its own error handling to prevent one failure from blocking others
      const [timelineResponse, customerPrefs, userDetails] = await Promise.all([
        AppointmentService.getApiAppAppointmentGetUserTimeline(
          slot.assignedUserId || undefined,
          undefined, // startLocation
          undefined, // startLatitude
          undefined, // startLongitude
          slotDateStr // date
        ).catch((err) => {
          console.error('Failed to fetch timeline:', err);
          return null;
        }),
        // Reuse cached prefs from modal open, or fetch fresh
        customerPrefsRef.current
          ? Promise.resolve(customerPrefsRef.current)
          : CustomerPreferencesService.getApiAppCustomerPreferencesGet().catch(() => null),
        slot.assignedUserId ? UserService.getApiAppUserGetUserById(slot.assignedUserId).catch(() => null) : Promise.resolve(null)
      ]);
      // Use company locations from context (no extra API call)
      const officeLocations = { items: companyLocations };
      
      // Only set timeline data if we got a valid response
      if (timelineResponse) {
        setTimelineData(timelineResponse);
      }
      
      
      // Use typed route preferences directly from API (no JSON parsing needed)
      let defaultStartingLocation: 'home' | 'office' = 'office';
      let defaultEndingLocation: 'home' | 'office' = 'home';
      
      if (customerPrefs?.routePreferences) {
        const prefs = customerPrefs.routePreferences;
        defaultStartingLocation = (prefs.defaultStartingLocation as 'home' | 'office') || 'office';
        defaultEndingLocation = (prefs.defaultEndingLocation as 'home' | 'office') || 'home';
      }
      
      // Get default office address
      let officeAddress = 'Office address not available';
      const locations = (officeLocations as any)?.items || [];
      const defaultOffice = locations.find((loc: any) => loc.isDefault) || locations[0];
      if (defaultOffice) {
        officeAddress = [
          defaultOffice.addressLine1,
          defaultOffice.city,
          defaultOffice.state,
          defaultOffice.zipCode || defaultOffice.postalCode
        ].filter(Boolean).join(', ');
      }
      
      // Get user's home address
      let homeAddress = 'Home address not available';
      if (userDetails?.addresses) {
        homeAddress = formatUserAddressDisplay(resolveHomeUserAddress(userDetails.addresses));
      }
      
      // Resolve starting address based on preference
      const resolvedStartingAddress = defaultStartingLocation === 'office' ? officeAddress : homeAddress;
      
      // Resolve ending address based on preference
      const resolvedEndingAddress = defaultEndingLocation === 'office' ? officeAddress : homeAddress;
      
      // Update audit modal with resolved addresses
      setAuditModal(prev => prev ? {
        ...prev,
        startingAddress: resolvedStartingAddress,
        endingAddress: resolvedEndingAddress
      } : null);
      
    } catch (err) {
      console.error('Failed to fetch timeline data:', err);
      // Set fallback addresses on error
      setAuditModal(prev => prev ? {
        ...prev,
        startingAddress: 'Unable to load address',
        endingAddress: 'Unable to load address'
      } : null);
    } finally {
      setAuditLoading(false);
    }
  };

  // Form validation
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [bookingAlert, setBookingAlert] = useState<SchedulingAlert | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Belt-and-suspenders guard against double-submitting a create request (double-click,
  // slow network retry). `isSubmitting` state alone doesn't protect the manual-booking
  // path, whose trigger button isn't wired to it — see handleNextStep/handleManualBookingCreate.
  const isCreatingRef = useRef(false);

  // Non-blocking flag for internal staff: when GetSlots reports the address is
  // outside the configured service area we still let staff book, but we surface
  // a clear indicator on the slot-selection screen so they know the booking is
  // out-of-area before confirming.
  const [isOutsideServiceArea, setIsOutsideServiceArea] = useState(false);
  const [outsideServiceAreaDetail, setOutsideServiceAreaDetail] = useState<string | null>(null);

  // No availability tracking - graceful empty state when assigned user/team has no open slots
  const [noAvailabilityInfo, setNoAvailabilityInfo] = useState<{
    isFull: boolean;
    dateLabel: string;
    timeLabel: string;
    userName?: string;
    detail?: string;
    /** True when the operator already used "Book Anyway" but GetSlots still returned nothing. */
    afterServiceAreaOverride?: boolean;
  } | null>(null);

  // Custom questions answers
  const [customAnswers, setCustomAnswers] = useState<{[key: string]: string}>({});
  
  // Address verification
  const [addressCoordinates, setAddressCoordinates] = useState<{lat: number, lng: number} | null>(null);

  // Auto-geocode prefilled address so the map preview loads immediately
  const geocodedPrefillRef = useRef(false);
  useEffect(() => {
    if (!isOpen || addressCoordinates || geocodedPrefillRef.current) return;
    const addr = address?.trim();
    const cty = city?.trim();
    const st = state?.trim();
    if (!addr || !cty || !st) return;

    const fullAddress = `${addr}, ${cty}, ${st} ${zipCode || ''}`.trim();
    geocodedPrefillRef.current = true;

    if (typeof google === 'undefined' || !google.maps?.Geocoder) return;
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: fullAddress }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results?.[0]?.geometry?.location) {
        const loc = results[0].geometry.location;
        setAddressCoordinates({ lat: loc.lat(), lng: loc.lng() });
      }
    });
  }, [isOpen, address, city, state, zipCode, addressCoordinates]);

  // -------------------------------------------------------------------------
  // Home/office route-leg backfill
  //
  // The backend omits drive distance + time for the day's *start* (home/office)
  // and *end* legs, so a slot that's first/last of the day renders its route
  // overview and proximity hook as "— mi · — min". Because every suggested slot
  // is for the same customer address, we compute those legs client-side once
  // per resolved start/end location (home varies per assigned user; office is
  // shared) and reuse them everywhere the route is shown.
  // -------------------------------------------------------------------------
  const [routeStartLocation, setRouteStartLocation] = useState<'home' | 'office'>('office');
  const [routeEndLocation, setRouteEndLocation] = useState<'home' | 'office'>('home');
  // Keyed `${direction}:${addressLowercase}` → computed leg.
  const [computedHomeLegs, setComputedHomeLegs] = useState<Record<string, { miles: number; minutes: number }>>({});
  const userHomeAddressRef = useRef<Record<string, string>>({});
  const geocodeCacheRef = useRef<Record<string, { lat: number; lng: number } | null>>({});

  const defaultOfficeAddress = useMemo(() => {
    const locs = (companyLocations || []) as any[];
    const def = locs.find((l) => l.isDefault) || locs[0];
    if (!def) return '';
    return [def.addressLine1, def.city, def.state, def.zipCode || def.postalCode]
      .filter(Boolean).join(', ');
  }, [companyLocations]);

  const geocodeAddress = useCallback((rawAddress: string): Promise<{ lat: number; lng: number } | null> => {
    const address = rawAddress.trim();
    const key = address.toLowerCase();
    if (key in geocodeCacheRef.current) return Promise.resolve(geocodeCacheRef.current[key]);
    return new Promise((resolve) => {
      if (!address || typeof google === 'undefined' || !google.maps?.Geocoder) {
        resolve(null);
        return;
      }
      new google.maps.Geocoder().geocode({ address }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results?.[0]?.geometry?.location) {
          const loc = results[0].geometry.location;
          const coords = { lat: loc.lat(), lng: loc.lng() };
          geocodeCacheRef.current[key] = coords;
          resolve(coords);
        } else {
          geocodeCacheRef.current[key] = null;
          resolve(null);
        }
      });
    });
  }, []);

  // Resolve the day-start address for a slot's assigned user (office is shared;
  // home comes from that user's profile, cached in userHomeAddressRef).
  const getStartLegAddress = useCallback((slot: Routemize_DTOs_SlotSuggestionDTO): string => {
    if (routeStartLocation === 'office') return defaultOfficeAddress;
    return slot.assignedUserId ? (userHomeAddressRef.current[slot.assignedUserId] || '') : '';
  }, [routeStartLocation, defaultOfficeAddress]);

  const getEndLegAddress = useCallback((slot: Routemize_DTOs_SlotSuggestionDTO): string => {
    if (routeEndLocation === 'office') return defaultOfficeAddress;
    return slot.assignedUserId ? (userHomeAddressRef.current[slot.assignedUserId] || '') : '';
  }, [routeEndLocation, defaultOfficeAddress]);

  // A leg renders as start/end of day when the backend gives no neighboring
  // appointment time on that side.
  const isPrevStartOfDay = (slot: Routemize_DTOs_SlotSuggestionDTO): boolean =>
    !slot.previousAppointmentStartTime && !slot.previousAppointmentStartTimeUtc;
  const isNextEndOfDay = (slot: Routemize_DTOs_SlotSuggestionDTO): boolean =>
    !slot.nextAppointmentStartTime && !slot.nextAppointmentStartTimeUtc;

  // Effective previous leg: backend value when present, else the computed
  // home/office start leg. Falls back to nulls until the async compute lands.
  const resolvePrevLeg = useCallback((slot: Routemize_DTOs_SlotSuggestionDTO): { miles: number | null; minutes: number | null } => {
    if (slot.previousAppointmentDistanceMiles != null || slot.previousAppointmentTravelTimeMinutes != null) {
      return {
        miles: slot.previousAppointmentDistanceMiles ?? null,
        minutes: slot.previousAppointmentTravelTimeMinutes ?? null,
      };
    }
    if (!isPrevStartOfDay(slot)) return { miles: null, minutes: null };
    const addr = getStartLegAddress(slot);
    if (!addr) return { miles: null, minutes: null };
    const leg = computedHomeLegs[`start:${addr.trim().toLowerCase()}`];
    return leg ? { miles: leg.miles, minutes: leg.minutes } : { miles: null, minutes: null };
  }, [computedHomeLegs, getStartLegAddress]);

  const resolveNextLeg = useCallback((slot: Routemize_DTOs_SlotSuggestionDTO): { miles: number | null; minutes: number | null } => {
    if (slot.nextAppointmentDistanceMiles != null || slot.nextAppointmentTravelTimeMinutes != null) {
      return {
        miles: slot.nextAppointmentDistanceMiles ?? null,
        minutes: slot.nextAppointmentTravelTimeMinutes ?? null,
      };
    }
    if (!isNextEndOfDay(slot)) return { miles: null, minutes: null };
    const addr = getEndLegAddress(slot);
    if (!addr) return { miles: null, minutes: null };
    const leg = computedHomeLegs[`end:${addr.trim().toLowerCase()}`];
    return leg ? { miles: leg.miles, minutes: leg.minutes } : { miles: null, minutes: null };
  }, [computedHomeLegs, getEndLegAddress]);

  const neighborForSlot = useCallback((slot: Routemize_DTOs_SlotSuggestionDTO) => (
    getClosestNeighbor(slot, {
      prevMiles: resolvePrevLeg(slot).miles,
      nextMiles: resolveNextLeg(slot).miles,
      startAnchor: routeStartLocation,
      endAnchor: routeEndLocation,
    })
  ), [resolvePrevLeg, resolveNextLeg, routeStartLocation, routeEndLocation]);

  useEffect(() => {
    if (!isOpen || availableSlots.length === 0) return;
    let cancelled = false;

    (async () => {
      // Resolve the (shared) booking-address coordinates once.
      let bookingCoords = addressCoordinates;
      if (!bookingCoords) {
        const first = availableSlots[0];
        const firstAddr = [first.addressLine1, first.addressLine2, first.city, first.state, first.zipCode]
          .filter(Boolean).join(', ');
        bookingCoords = await geocodeAddress(firstAddr);
      }
      if (cancelled || !bookingCoords) return;

      // Prefetch home addresses for users whose start/end leg resolves to "home".
      if (routeStartLocation === 'home' || routeEndLocation === 'home') {
        const neededUserIds = new Set<string>();
        for (const slot of availableSlots) {
          if (!slot.assignedUserId) continue;
          if (userHomeAddressRef.current[slot.assignedUserId] !== undefined) continue;
          if ((routeStartLocation === 'home' && isPrevStartOfDay(slot))
            || (routeEndLocation === 'home' && isNextEndOfDay(slot))) {
            neededUserIds.add(slot.assignedUserId);
          }
        }
        for (const userId of neededUserIds) {
          try {
            const user = await UserService.getApiAppUserGetUserById(userId);
            userHomeAddressRef.current[userId] = user?.addresses
              ? formatUserAddressDisplay(resolveHomeUserAddress(user.addresses))
              : '';
          } catch {
            userHomeAddressRef.current[userId] = '';
          }
          if (cancelled) return;
        }
      }

      // Compute each distinct leg we still need.
      const updates: Record<string, { miles: number; minutes: number }> = {};
      const computeLeg = async (direction: 'start' | 'end', address: string) => {
        if (!address) return;
        const key = `${direction}:${address.trim().toLowerCase()}`;
        if (computedHomeLegs[key] || updates[key]) return;
        const coords = await geocodeAddress(address);
        if (!coords || cancelled) return;
        const result = direction === 'start'
          ? await googleMapsDistanceService.calculateDrivingDistance(coords, bookingCoords!)
          : await googleMapsDistanceService.calculateDrivingDistance(bookingCoords!, coords);
        if (result) updates[key] = { miles: result.distance, minutes: result.duration };
      };

      for (const slot of availableSlots) {
        if (isPrevStartOfDay(slot) && slot.previousAppointmentDistanceMiles == null) {
          await computeLeg('start', getStartLegAddress(slot));
        }
        if (isNextEndOfDay(slot) && slot.nextAppointmentDistanceMiles == null) {
          await computeLeg('end', getEndLegAddress(slot));
        }
        if (cancelled) return;
      }

      if (!cancelled && Object.keys(updates).length > 0) {
        setComputedHomeLegs((prev) => ({ ...prev, ...updates }));
      }
    })();

    return () => { cancelled = true; };
    // computedHomeLegs intentionally omitted: it's read for dedupe only and
    // including it would re-run the effect on every successful compute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, availableSlots, addressCoordinates, routeStartLocation, routeEndLocation, defaultOfficeAddress, geocodeAddress, getStartLegAddress, getEndLegAddress]);

  // Sync prefilled values when modal opens
  useEffect(() => {
    if (isOpen) {
      if (prefilledDate) {
        setPreferredDate(prefilledDate);
      }
      if (prefilledPreferredTime !== undefined) {
        setPreferredTime(convertTimeCodeToLabel(prefilledPreferredTime));
      }
    }
  }, [isOpen, prefilledDate, prefilledPreferredTime]);
  
  // Check if preferences have changed
  const preferencesChanged = useMemo(() => {
    if (!showAvailableSlots) return false;
    return (
      preferredDate !== originalPreferences.date ||
      preferredSecondaryDate !== originalPreferences.secondaryDate ||
      preferredTime !== originalPreferences.time
    );
  }, [preferredDate, preferredSecondaryDate, preferredTime, originalPreferences, showAvailableSlots]);

  // Get the selected booking form
  const selectedForm = bookingForms.find(form => form.id === eventType?.id);

  // Determine if lead source dropdown should be shown (from booking form config)
  // Hide the picker if the underlying request already carries a lead source —
  // whether it was passed in via the `existingRequestId` prop or discovered on
  // the fly by `checkForExistingRequest` (some requests are created on the fly
  // and already have a lead source we shouldn't force the operator to re-pick).
  const requestHasLeadSource = Boolean(existingRequestForContact?.leadSource);
  const showLeadSourceField = selectedForm?.showLeadSourceToCustomer === true && !requestHasLeadSource;

  const effectiveLeadSource = requestHasLeadSource
    ? (existingRequestForContact?.leadSource || '')
    : showLeadSourceField
      ? selectedLeadSource
      : (selectedForm?.leadSource || '');

  // Load lead sources when a form with showLeadSourceToCustomer is selected
  useEffect(() => {
    if (!showLeadSourceField || !selectedForm) {
      setAvailableLeadSources([]);
      return;
    }

    let cancelled = false;
    const loadLeadSources = async () => {
      setLoadingLeadSources(true);
      try {
        const response = await MasterDataService.getApiAppMasterDataGet('ReferralType');
        if (cancelled) return;

        let allSources: Routemize_DTOs_MasterData[] = [];
        if (response && response.length > 0 && response[0].items) {
          allSources = response[0].items;
        }

        // Customer-choice forms show every source. Internal-only ones stay
        // available to staff creating a booking on a customer's behalf, sorted
        // to the bottom so the customer-visible options appear first.
        allSources.sort((a, b) => {
          const aInternal = isInternalLeadSource(a.helpText) ? 1 : 0;
          const bInternal = isInternalLeadSource(b.helpText) ? 1 : 0;
          return aInternal - bInternal;
        });

        setAvailableLeadSources(allSources);
      } catch (error) {
        setAvailableLeadSources([]);
      } finally {
        if (!cancelled) setLoadingLeadSources(false);
      }
    };

    loadLeadSources();
    return () => { cancelled = true; };
  }, [showLeadSourceField, selectedForm?.id]);

  // Get loaded questions for the selected form.
  //
  // Photo/Attachments uploads are a public-flow-only feature (handled by the
  // public booking page via PublicBookingFlowManager). The internal booking
  // flow has no upload UI, so we strip any Attachments-type questions out
  // here. This prevents them from rendering as a label with no input, and
  // from blocking submission when marked required (see bug: required photo
  // upload question cannot be completed in the app).
  const formQuestions = useMemo(() => {
    if (!eventType?.id) return [];
    const all = bookingFormQuestions[eventType.id] || [];
    return all.filter(q => q.type !== Routemize_Constants_FieldType.ATTACHMENTS);
  }, [eventType?.id, bookingFormQuestions]);

  // Check if the selected booking form has custom questions
  const hasCustomQuestions = useMemo(() => formQuestions.length > 0,
    [formQuestions]
  );

  // Conditional show/hide: a question may be revealed only when a controlling
  // radio answer targets it — same behavior as the public booking form. Each
  // revealed follow-up is anchored inline directly beneath the answer that
  // reveals it (not at its stored order) so it appears on the fly in place.
  const visibleQuestions = useMemo(
    () => getVisibleQuestionsInDisplayOrder(formQuestions, customAnswers),
    [formQuestions, customAnswers]
  );

  // Clear a custom question's "required" error the moment it's answered, so the
  // validation banner/highlight resolves live as the user fills things in
  // (rather than lingering until the next Continue click). Keyed by questionId,
  // so this never touches the preferences/general error keys.
  useEffect(() => {
    setErrors(prev => {
      let changed = false;
      const next = { ...prev };
      Object.keys(next).forEach(qid => {
        if (qid !== 'general' && customAnswers[qid]) {
          delete next[qid];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [customAnswers]);

  // Persist custom-question answers to the request using the SAME endpoint and
  // DTO shape the public booking form uses (POST publicBookingForm/saveServiceDetails
  // with `dynamicAnswers` keyed by questionId). This keeps the internal flow
  // and public flow in lock-step so answers always render in the appointment
  // detail view's "Form Responses" section regardless of how the booking was
  // created. Safe to call even when the request is brand new — backend treats
  // it as an upsert keyed by (requestId, questionId).
  const persistAnswersToRequest = async (requestId: string | null | undefined) => {
    if (!requestId) return;
    const dynamicAnswers: Record<string, string> = {};
    // Only persist answers for questions that are actually shown (skip ones
    // hidden by conditional logic, including stale answers from branches the
    // user navigated away from).
    formQuestions.forEach((question) => {
      const qid = question.id || '';
      const answer = customAnswers[qid];
      if (qid && answer && answer.trim() !== '' && isQuestionVisible(question, formQuestions, customAnswers)) {
        dynamicAnswers[qid] = answer;
      }
    });
    if (Object.keys(dynamicAnswers).length === 0) return;
    try {
      await PublicBookingFormService.postApiAppPublicBookingFormSaveServiceDetails({
        requestId,
        dynamicAnswers,
      });
    } catch (err) {
      // Non-fatal: don't block the booking flow if answer persistence fails;
      // the request itself was created/updated successfully. We still log so
      // unexpected failures surface in the console.
      console.error('Failed to save form answers to request:', err);
    }
  };
  
  const skipEventStep = !!(skipToSlotSelection && existingRequestId && prefilledEventTypeId);

  const steps = useMemo(() => {
    const allSteps = [
      { id: 'customer', label: 'Customer Details', shortLabel: 'Customer', description: 'Who are we scheduling for?' },
      { id: 'event', label: 'Service Type', shortLabel: 'Service', description: 'What service do they need?' },
      ...(hasCustomQuestions ? [{ id: 'questions', label: 'Questions', shortLabel: 'Questions', description: 'Additional details needed' }] : []),
      { id: 'preferences', label: 'Preferences', shortLabel: 'Preference', description: 'When would they prefer?' },
      { id: 'booking', label: 'Select Time', shortLabel: 'When', description: 'Choose the best time slot' },
      { id: 'confirmation', label: 'Confirm & Book', shortLabel: 'Confirm', description: 'Review and confirm your booking' }
    ];
    if (skipToSlotSelection) {
      return allSteps.filter(step => step.id === 'booking' || step.id === 'confirmation');
    }
    if (skipCustomerStep) {
      return allSteps.filter(step => step.id !== 'customer');
    }
    if (skipEventStep) {
      return allSteps.filter(step => step.id !== 'event');
    }
    return allSteps;
  }, [skipCustomerStep, skipToSlotSelection, skipEventStep, hasCustomQuestions]);

  // Map booking forms to event types format for UI
  // Filter to only published forms. Forms with an assigned lead source are normally
  // hidden because they are reserved for that channel, but when scheduling from an
  // existing request we allow the request's own form (matched via prefilledEventTypeId
  // or the loaded request's bookingFormId) so the Service step isn't empty.
  const allowedLeadSourceFormId = prefilledEventTypeId || existingRequestForContact?.bookingFormId || null;
  const eventTypes = useMemo(() => {
    // Diagnostic — confirms the list response is carrying the new
    // nextUpUserId / nextUpUserName fields. Visible in DevTools console.
    // Safe to remove once the "Next up" copy is verified rendering.
    if (bookingForms.length > 0) {
      // eslint-disable-next-line no-console
      console.debug('[Routemize] BookingFormDto nextUpUser snapshot', bookingForms.map(f => ({
        id: f.id,
        name: f.formName,
        eventNature: f.eventNature,
        nextUpUserId: f.nextUpUserId,
        nextUpUserName: f.nextUpUserName,
      })));
    }
    const filtered = bookingForms.filter(form => {
    if (form.status !== Routemize_Constants_BookingFormStatus.PUBLISHED) return false;
    if (!form.leadSource) return true;
    return Boolean(allowedLeadSourceFormId && form.id === allowedLeadSourceFormId);
  }).map(form => {
    const settings = bookingFormSettings[form.id || ''];
    const details = bookingFormDetails[form.id || ''];
    const users = bookingFormUsers[form.id || ''] || form.assignedUsers || [];
    const durationMinutes = settings?.timeAtLocationMinutes || 30;
    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;
    const duration = hours > 0 
      ? `${hours}hr ${minutes > 0 ? `${minutes}min` : ''}`.trim()
      : `${minutes}min`;
    
    // `nextUpUserId` / `nextUpUserName` come from the BookingFormDto on
    // both the list endpoint and the per-form detail endpoint. Read from
    // detail first (more likely to be fresh after a tenant changes their
    // smart-assignment config), fall back to the list value.
    const nextUpUserId = details?.formDetail?.nextUpUserId ?? form.nextUpUserId ?? null;
    const nextUpUserName = details?.formDetail?.nextUpUserName ?? form.nextUpUserName ?? null;
    return {
      id: form.id || '',
      name: form.formName || details?.formDetail?.formName || 'Unknown Form',
      duration: duration,
      durationMinutes: durationMinutes,
      description: form.formDescription || details?.formDetail?.formDescription || 'No description available',
      isRoutemize: true,
      eventNature: form.eventNature || details?.formDetail?.eventNature,
      assignedUsers: users,
      eventColor: settings?.eventColor || null,
      nextUpUserId,
      nextUpUserName,
      // Surfaced so the picker can group "customer picks lead source" forms
      // at the top of the list (operators usually book those first when the
      // caller volunteered a channel). Driven by the booking form's
      // `showLeadSourceToCustomer` flag — same one that gates the lead source
      // dropdown later in the flow.
      showLeadSourceToCustomer: form.showLeadSourceToCustomer === true,
    };
  });
    // Stable sort: forms that prompt the customer for a lead source bubble
    // to the top, while preserving the original API order within each
    // group. `Array.prototype.sort` is stable in modern engines (V8, JSC,
    // SpiderMonkey) so a simple key compare is enough.
    return filtered.sort((a, b) => {
      const aFirst = a.showLeadSourceToCustomer ? 0 : 1;
      const bFirst = b.showLeadSourceToCustomer ? 0 : 1;
      return aFirst - bFirst;
    });
  }, [bookingForms, bookingFormSettings, bookingFormDetails, bookingFormUsers, allowedLeadSourceFormId]);

  // Identity colors for every form in the picker, so the Service step can
  // tint provider names before a form is selected. Built from the same user
  // list the rest of the flow uses, so a name keeps its color from the
  // service card all the way through to the confirmation screen.
  const assigneeColorsByFormId = useMemo(
    () => new Map(eventTypes.map(type => [type.id, buildAssigneeColorMap(type.assignedUsers)])),
    [eventTypes]
  );

  // Smart-assignment helpers — only show the preferred provider picker when
  // the chosen form is round-robin and has assigned users. Mirrors the same
  // helper names used in EditEventModal so behavior reads identically.
  const isSmartAssignmentForm =
    eventType?.eventNature === Routemize_Constants_EventNature.ROUND_ROBIN;
  const smartAssignmentUsers = useMemo<Routemize_DTOs_UserSmallDTO[]>(
    () => {
      if (!isSmartAssignmentForm || !eventType?.id) return [];
      return bookingFormUsers[eventType.id] ?? eventType.assignedUsers ?? [];
    },
    [isSmartAssignmentForm, eventType?.id, bookingFormUsers, eventType?.assignedUsers]
  );

  // Smart Assignment rules for the selected form's event type (rotation mode +
  // override toggles + regional assignments). Rules are scoped per event type
  // in `companySettings.smartAssignmentConfigJson`, the same JSON the Smart
  // Assignment panel reads/writes, so this must resolve the form's own event
  // type rather than treating the blob as one tenant-wide rule set. We surface
  // it on the preferences step so admins see exactly what Routemize is going
  // to do before they decide whether to override the rotation manually.
  const selectedFormEventType: string | null =
    typeof selectedForm?.eventType === 'string' ? selectedForm.eventType : null;
  const smartAssignmentPrefs = useMemo<InstantBookingPreferences>(
    () => resolveEventTypePreferences(companySettings?.smartAssignmentConfigJson, selectedFormEventType),
    [companySettings?.smartAssignmentConfigJson, selectedFormEventType]
  );

  // Territory definitions and the zip→territory map used by Routemize when
  // `roundRobinMode === 'territories'`. Only valid when service area is in
  // zipcode mode; otherwise we have no territories to render.
  const territoriesData = useMemo<{ territories: Territory[]; zipTerritoryMap: Record<string, string[]> }>(() => {
    const empty = { territories: [] as Territory[], zipTerritoryMap: {} as Record<string, string[]> };
    if (companySettings?.serviceAreaMode !== 'zipcode' || !companySettings.serviceAreaZipCodes) {
      return empty;
    }
    try {
      const parsed = JSON.parse(companySettings.serviceAreaZipCodes);
      if (parsed && typeof parsed === 'object' && parsed.zipTerritoryMap) {
        return {
          territories: Array.isArray(parsed.territories) ? (parsed.territories as Territory[]) : [],
          zipTerritoryMap: (parsed.zipTerritoryMap as Record<string, string[] | string | null>) as unknown as Record<string, string[]>,
        };
      }
    } catch {
      // ignore parse errors
    }
    return empty;
  }, [companySettings?.serviceAreaMode, companySettings?.serviceAreaZipCodes]);

  const isTerritoriesMode = smartAssignmentPrefs.roundRobinMode === 'territories';
  const smartOverrideActive = isTerritoriesMode
    ? smartAssignmentPrefs.territoryCustomerPreferenceOverrideEnabled
    : smartAssignmentPrefs.equalRotationCustomerPreferenceOverrideEnabled;
  const proximityOverrideActive =
    !isTerritoriesMode && smartAssignmentPrefs.enableProximityOverride;

  // Normalize the customer's zip for territory lookups. The map is keyed by
  // 5-digit US zip codes, but the input may be a ZIP+4 (with hyphen, em-dash,
  // or just extra digits) or contain stray whitespace — strip everything that
  // isn't a digit and use the first 5. Returns an empty string when fewer
  // than 5 digits are present so callers know the input is incomplete.
  // Country-aware service-area key for the customer's zip/postal (US 5-digit ZIP,
  // CA 3-char FSA). Returns '' until a complete code is entered, matching how the
  // territory map keys are stored and how GetSlots reduces the code at booking time.
  const normalizedCustomerZip5 = useMemo(
    () => (isValidServiceAreaCode(zipCode || '', country) ? normalizeServiceAreaCode(zipCode || '', country) : ''),
    [zipCode, country]
  );

  // For territories: predict who the customer's zip would route to, scoped to
  // the currently-selected booking form's user list (a tenant may have many
  // smart-assignment subscribers, but only the ones attached to *this* form
  // can take the booking).
  const territoryRouting = useMemo(() => {
    if (!isSmartAssignmentForm || !isTerritoriesMode) return null;
    if (!normalizedCustomerZip5) return null;
    const rawTerritoryIds = territoriesData.zipTerritoryMap[normalizedCustomerZip5];
    const matchingTerritoryIds = Array.isArray(rawTerritoryIds)
      ? rawTerritoryIds.filter(Boolean)
      : (typeof rawTerritoryIds === 'string' && rawTerritoryIds ? [rawTerritoryIds] : []);
    if (matchingTerritoryIds.length === 0) return null;

    const matchingTerritories = territoriesData.territories.filter(t =>
      matchingTerritoryIds.includes(t.id)
    );

    const formUserIds = new Set(smartAssignmentUsers.map(u => u.id).filter(Boolean));
    const matchingUsers: Routemize_DTOs_UserSmallDTO[] = [];
    for (const assignment of smartAssignmentPrefs.regionalAssignments) {
      if (!assignment.userId || !formUserIds.has(assignment.userId)) continue;
      const subs = assignment.territories ?? [];
      if (subs.some(tid => matchingTerritoryIds.includes(tid))) {
        const user = smartAssignmentUsers.find(u => u.id === assignment.userId);
        if (user && !matchingUsers.some(m => m.id === user.id)) {
          matchingUsers.push(user);
        }
      }
    }
    return { territories: matchingTerritories, matchingUsers, customerZip: normalizedCustomerZip5 };
  }, [
    isSmartAssignmentForm,
    isTerritoriesMode,
    normalizedCustomerZip5,
    territoriesData,
    smartAssignmentUsers,
    smartAssignmentPrefs.regionalAssignments,
  ]);

  // Resolve the "automatic" preferred provider for the current form. The
  // empty string is a sentinel meaning "Auto-assign — let the backend rotate
  // / pick by territory" (sent as undefined assignedUserId on the request so
  // the backend's own rotation logic runs). Precedence:
  //   1. Territories mode with exactly ONE matching subscriber → that user
  //      (deterministic — same as backend would route to).
  //   2. '' = Auto. Used for equal rotation (always — admins want the
  //      backend rotation to advance, not get pinned to whoever the
  //      request previously had assigned), territories with 2+ matching
  //      subscribers (round-robin among them), or territories mode with no
  //      zip match (zip incomplete or unmapped — backend falls back to its
  //      own rotation).
  // We deliberately do NOT fall back to existingRequest.assignedUserId here:
  // the picker shows "Currently assigned: X" as a passive badge so the
  // operator can see who was previously picked, but the default behavior on
  // re-open is still Auto so equal rotation actually rotates instead of
  // sticking on the prior pick.
  // Used both by the auto-sync effect below and by the "Use automatic"
  // reset handler so "what is automatic?" is defined in exactly one place.
  const autoPreferredAssigneeId = useMemo<string>(() => {
    const ids = smartAssignmentUsers.map(u => u.id).filter(Boolean) as string[];
    if (ids.length === 0) return '';
    if (isTerritoriesMode && territoryRouting && territoryRouting.matchingUsers.length === 1) {
      const uid = territoryRouting.matchingUsers[0].id;
      if (uid && ids.includes(uid)) return uid;
    }
    return '';
  }, [smartAssignmentUsers, isTerritoriesMode, territoryRouting]);

  // Keep the preferred provider in sync with the selected form. Clears when
  // the form is not a smart-assignment form. The ref guard means the auto-
  // sync effect won't override an explicit operator selection (including
  // an explicit "Auto-assign" pick).
  useEffect(() => {
    if (!isSmartAssignmentForm) {
      if (preferredAssigneeId) setPreferredAssigneeId('');
      userChangedPreferredAssignee.current = false;
      return;
    }
    if (userChangedPreferredAssignee.current) return;
    if (preferredAssigneeId !== autoPreferredAssigneeId) {
      setPreferredAssigneeId(autoPreferredAssigneeId);
    }
  }, [isSmartAssignmentForm, autoPreferredAssigneeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // "Manual override" is a derived signal: the picker resolves to a
  // specific user that the backend's automatic logic wouldn't have
  // picked on its own. Empty string (Auto) is never an override.
  // For equal rotation we additionally treat the backend-resolved
  // "next up" user as in-line with auto, since picking them is the
  // same outcome as letting the rotation run. For territories with a
  // single deterministic match, autoPreferredAssigneeId is that user,
  // so the equality check below already covers it.
  const isManualPreferredAssignee = (() => {
    if (!isSmartAssignmentForm) return false;
    if (preferredAssigneeId === '') return false;
    if (preferredAssigneeId === autoPreferredAssigneeId) return false;
    if (
      !isTerritoriesMode &&
      eventType?.nextUpUserId &&
      preferredAssigneeId === eventType.nextUpUserId
    ) {
      return false;
    }
    return true;
  })();

  // Reset the manual-override ref when the form changes. The auto-sync
  // effect will then populate the new form's default in the same render.
  useEffect(() => {
    userChangedPreferredAssignee.current = false;
  }, [eventType?.id]);

  // Reset the picker back to the form's automatic default. Lets the
  // operator undo a manual override without having to re-pick.
  const handleResetPreferredAssigneeToAuto = () => {
    userChangedPreferredAssignee.current = false;
    setPreferredAssigneeId(autoPreferredAssigneeId);
    setUserChangedPreferences(true);
  };

  // Human label for the "Auto-assign" dropdown option. Phrasing depends on
  // mode and territory match state so the operator knows exactly what the
  // backend is going to do when they press Find Slots. For equal rotation
  // we additionally surface the backend-resolved "next up" user (from
  // BookingFormDto.nextUpUserName) when available, so admins can see
  // exactly who Auto-assign is about to pick.
  const autoAssignOptionLabel = useMemo(() => {
    const formatName = (u?: Routemize_DTOs_UserSmallDTO | null) =>
      (u?.name || `${u?.firstName ?? ''} ${u?.lastName ?? ''}`.trim() || u?.email || 'Unnamed');
    if (isTerritoriesMode && territoryRouting && territoryRouting.matchingUsers.length > 1) {
      return `Auto-assign — round-robin between ${territoryRouting.matchingUsers.map(formatName).join(', ')}`;
    }
    if (isTerritoriesMode && territoryRouting && territoryRouting.matchingUsers.length === 1) {
      return `Auto-assign — routes to ${formatName(territoryRouting.matchingUsers[0])}`;
    }
    if (isTerritoriesMode) {
      return 'Auto-assign — fall back to standard rotation';
    }
    if (eventType?.nextUpUserName) {
      return `Auto-assign — next up: ${eventType.nextUpUserName}`;
    }
    return 'Auto-assign — rotates among the team';
  }, [isTerritoriesMode, territoryRouting, eventType?.nextUpUserName]);

  // Identity colors for the providers on the currently selected form.
  const assigneeColorById = useMemo(() => buildAssigneeColorMap(
    (eventType?.id ? bookingFormUsers[eventType.id] : undefined)
      ?? eventType?.assignedUsers
      ?? []
  ), [eventType?.id, eventType?.assignedUsers, bookingFormUsers]);

  // Provider options for the Preferred / Assigned Provider dropdown, with
  // territory names pre-resolved per user (the dropdown component is dumb
  // about territories — it just renders chips for whatever names we hand it).
  const preferredProviderOptions = useMemo<PreferredProviderOption[]>(() => {
    const users = (eventType?.id ? bookingFormUsers[eventType.id] : []) ?? eventType?.assignedUsers ?? [];
    return users
      .filter((u): u is Routemize_DTOs_UserSmallDTO => Boolean(u && u.id))
      .filter((u) => assignableUserIds.size === 0 || assignableUserIds.has(u.id!))
      .map(u => {
        let territoryNames: string[] | undefined;
        if (isTerritoriesMode && u.id) {
          const userAssignment = smartAssignmentPrefs.regionalAssignments.find(a => a.userId === u.id);
          const userTerritoryIds = userAssignment?.territories ?? [];
          if (userTerritoryIds.length > 0) {
            const names = userTerritoryIds
              .map(tid => {
                const idx = territoriesData.territories.findIndex(t => t.id === tid);
                if (idx < 0) return null;
                return getTerritoryDisplayName(territoriesData.territories[idx], idx);
              })
              .filter((n): n is string => Boolean(n));
            if (names.length > 0) territoryNames = names;
          }
        }
        return {
          id: u.id || '',
          name: u.name,
          email: u.email,
          firstName: u.firstName,
          lastName: u.lastName,
          photoUrl: u.photoUrl,
          territoryNames,
          color: u.id ? assigneeColorById.get(u.id) : undefined,
        };
      });
  }, [
    eventType?.id,
    eventType?.assignedUsers,
    bookingFormUsers,
    isTerritoriesMode,
    smartAssignmentPrefs.regionalAssignments,
    territoriesData,
    assignableUserIds,
    assigneeColorById,
  ]);

  // Get allowed days from the selected booking form settings
  const selectedFormSettings = useMemo(() => {
    if (!eventType?.id) return null;
    return bookingFormSettings[eventType.id] || null;
  }, [eventType?.id, bookingFormSettings]);

  // The form's detour cap hides over-budget times from customers booking themselves.
  // Staff still see every slot; the badge below just tells them which ones the public
  // never got offered, so a long drive is a deliberate choice rather than a surprise.
  const detourLimitMiles = readDetourLimitMiles(selectedFormSettings);

  // A long drive is worth knowing about whether or not the form caps it, so the badge
  // fires on the cap and on its own mileage threshold, for any slot type.
  const renderDetourBadge = (slot: Routemize_DTOs_SlotSuggestionDTO) => {
    const warning = getDetourWarning(slot, detourLimitMiles);
    if (!warning) return null;
    const added = formatDistance(warning.miles, distUnit);
    const approx = warning.basis === 'estimated' ? 'roughly ' : '';
    return (
      <span
        className="text-2xs font-semibold px-1.5 py-0.5 rounded flex items-center gap-1 text-warning-800 bg-warning-50 border border-warning-200"
        title={
          `This appointment adds ${approx}${added} of driving to this day's existing route.`
          + (warning.hiddenFromCustomers
            ? ` That is past the ${formatDistance(detourLimitMiles!, distUnit)} detour limit on `
              + 'this booking form, so customers booking themselves are never offered this time '
              + '— you can still choose it.'
            : '')
        }
      >
        <AlertTriangle className="h-2.5 w-2.5" />
        Long detour · {warning.basis === 'estimated' ? '~' : ''}{added}
      </span>
    );
  };

  // Day-of-week work hours come from CompanyWorkSchedule, which lets a tenant
  // configure different hours per weekday (e.g. Sat 9–2 even though weekdays
  // run 8–5). We intersect those with the booking form's own first/last
  // appointment window so the time-of-day picker honours both — operators on
  // Wednesday with 9am–8pm see a real evening option, while a tighter
  // Saturday window trims morning/afternoon accordingly. Falls back to the
  // form bounds alone when the schedule isn't loaded yet or the picked date
  // has no entry (e.g. a non-work day the form still allows).
  const effectiveBookingHours = useMemo(() => {
    const dayHours = getCompanyHoursForDate(
      workSchedule?.dailySchedules,
      preferredDate
    );
    return intersectHourRanges(
      {
        start: selectedFormSettings?.firstAppointmentTime,
        end: selectedFormSettings?.lastAppointmentTime,
      },
      dayHours
    );
  }, [
    workSchedule?.dailySchedules,
    preferredDate,
    selectedFormSettings?.firstAppointmentTime,
    selectedFormSettings?.lastAppointmentTime,
  ]);

  const timeRangeOptions = useMemo(
    () =>
      filterPastTimeRanges(
        clipPreferredTimeRangesToCompanyHours(
          baseTimeRangeOptions,
          effectiveBookingHours.start,
          effectiveBookingHours.end
        ),
        preferredDate
      ),
    [
      baseTimeRangeOptions,
      effectiveBookingHours.start,
      effectiveBookingHours.end,
      preferredDate,
    ]
  );

  // Resolve the operator's preferred-time selection to a concrete option using
  // the same code/text/alias resolution the normalizer uses
  // (resolvePreferredTimeOption). A strict `code === || text ===` .find silently
  // misses on a lowercase public-form alias ("morning"), a mixed-case value, or
  // while a normalizer pass is still in flight — nulling preferredStartTime/
  // preferredEndTime on the write and tripping the backend's required-field
  // validation ("Preferred time is required").
  const resolveSelectedTimeOption = (value: string | null | undefined) =>
    resolvePreferredTimeOption(value, timeRangeOptions) ?? undefined;

  // Calculate max date based on maxSchedulingDaysFromToday setting (default: 60 days)
  const maxDate = useMemo(() => {
    const maxDaysOut = selectedFormSettings?.maxSchedulingDaysFromToday ?? 60;
    const date = new Date();
    date.setDate(date.getDate() + maxDaysOut);
    return date;
  }, [selectedFormSettings?.maxSchedulingDaysFromToday]);

  // Helper to check if a day is allowed - handles null, undefined, and explicit boolean values
  // Returns true if the value is explicitly true, null, or undefined (default to allowed)
  // Returns false only if the value is explicitly false
  const isDayAllowed = (allowValue: boolean | null | undefined): boolean => {
    // Only return false if explicitly set to false
    // null, undefined, and true all mean the day is allowed
    return allowValue !== false;
  };

  // Check if ALL days are disabled in settings - this is likely a misconfiguration
  // If all days are disabled, we should fallback to allowing all days
  const areAllDaysDisabled = useMemo(() => {
    if (!selectedFormSettings) return false;
    
    const allDisabled = 
      selectedFormSettings.allowMonday === false &&
      selectedFormSettings.allowTuesday === false &&
      selectedFormSettings.allowWednesday === false &&
      selectedFormSettings.allowThursday === false &&
      selectedFormSettings.allowFriday === false &&
      selectedFormSettings.allowSaturday === false &&
      selectedFormSettings.allowSunday === false;
    
    return allDisabled;
  }, [selectedFormSettings]);

  useEffect(() => {
    if (selectedFormSettings) {
      setSendSmsConfirmation(selectedFormSettings.smsConfirmation ?? true);
      setSendEmailConfirmation(selectedFormSettings.emailConfirmation ?? true);
      setSendSmsReminders(selectedFormSettings.smsReminder ?? true);
      setSendEmailReminders(selectedFormSettings.emailReminder ?? true);
    }
  }, [selectedFormSettings]);

  // Filter function for DatePicker to exclude disallowed days and dates beyond maxDaysOut
  const filterAllowedDates = (date: Date) => {
    // Check if date is beyond maxDaysOut
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDaysOut = selectedFormSettings?.maxSchedulingDaysFromToday ?? 60;
    const daysDiff = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > maxDaysOut) {
      return false;
    }
    
    // If no settings, allow all days
    if (!selectedFormSettings) return true;
    
    // If all days are disabled (misconfiguration), allow all days as fallback
    if (areAllDaysDisabled) return true;
    
    const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
    
    // Check each day - default to true (allowed) if not explicitly set to false
    switch (dayOfWeek) {
      case 0: // Sunday
        return isDayAllowed(selectedFormSettings.allowSunday);
      case 1: // Monday
        return isDayAllowed(selectedFormSettings.allowMonday);
      case 2: // Tuesday
        return isDayAllowed(selectedFormSettings.allowTuesday);
      case 3: // Wednesday
        return isDayAllowed(selectedFormSettings.allowWednesday);
      case 4: // Thursday
        return isDayAllowed(selectedFormSettings.allowThursday);
      case 5: // Friday
        return isDayAllowed(selectedFormSettings.allowFriday);
      case 6: // Saturday
        return isDayAllowed(selectedFormSettings.allowSaturday);
      default:
        return true;
    }
  };

  // Build a compact list of upcoming allowed dates for the horizontal date card
  // strip on the preferences step. We surface up to 14 cards so the strip stays
  // scannable; users can open the full picker via "More dates" for anything
  // further out (subject to maxSchedulingDaysFromToday).
  const dateCardOptions = useMemo(() => {
    const cards: Date[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDaysOut = selectedFormSettings?.maxSchedulingDaysFromToday ?? 60;
    const targetCount = 14;

    const cursor = new Date(today);
    let safety = 0;
    while (cards.length < targetCount && safety <= maxDaysOut) {
      if (filterAllowedDates(cursor)) {
        cards.push(new Date(cursor));
      }
      cursor.setDate(cursor.getDate() + 1);
      safety += 1;
    }
    return cards;
  }, [selectedFormSettings, areAllDaysDisabled]);

  const [showMoreDatesPopup, setShowMoreDatesPopup] = useState(false);

  // Quick presets surfaced inside the "More dates" popup so admins can jump to
  // common targets without navigating the calendar manually. Each preset is
  // resolved to the next allowed date (skipping disallowed weekdays) and is
  // dropped entirely if no allowed date exists within `maxDate`.
  const datePresets = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const findAllowedFrom = (start: Date): Date | null => {
      const cursor = new Date(start);
      let safety = 0;
      while (safety < 60) {
        if (cursor > maxDate) return null;
        if (filterAllowedDates(cursor)) return new Date(cursor);
        cursor.setDate(cursor.getDate() + 1);
        safety += 1;
      }
      return null;
    };

    const presets: Array<{ key: string; label: string; date: Date }> = [];

    if (filterAllowedDates(today) && today <= maxDate) {
      presets.push({ key: 'today', label: 'Today', date: new Date(today) });
    }

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (filterAllowedDates(tomorrow) && tomorrow <= maxDate) {
      presets.push({ key: 'tomorrow', label: 'Tomorrow', date: new Date(tomorrow) });
    }

    const inAWeek = new Date(today);
    inAWeek.setDate(inAWeek.getDate() + 7);
    const inAWeekDate = findAllowedFrom(inAWeek);
    if (inAWeekDate) {
      presets.push({ key: 'week', label: 'In a week', date: inAWeekDate });
    }

    const inTwoWeeks = new Date(today);
    inTwoWeeks.setDate(inTwoWeeks.getDate() + 14);
    const inTwoWeeksDate = findAllowedFrom(inTwoWeeks);
    if (inTwoWeeksDate) {
      presets.push({ key: '2weeks', label: 'In 2 weeks', date: inTwoWeeksDate });
    }

    return presets;
  }, [selectedFormSettings, areAllDaysDisabled, maxDate]);

  // Find the "Closest Next" slot.
  // Prefer the slot the scheduling engine itself tagged as the proximity pick, so
  // the UI reflects its actual selection. Fall back to a local "lowest total drive
  // time" heuristic only if the backend didn't tag one.
  const closestSlotData = useMemo(() => {
    if (availableSlots.length === 0) return null;

    const taggedProximitySlot = availableSlots.find(isProximitySlot);

    if (taggedProximitySlot) {
      const taggedIndex = availableSlots.findIndex((s) => s === taggedProximitySlot);
      return {
        slot: taggedProximitySlot,
        totalTravelTime: (taggedProximitySlot.previousAppointmentTravelTimeMinutes || 0)
          + (taggedProximitySlot.nextAppointmentTravelTimeMinutes || 0),
        totalDistance: (taggedProximitySlot.previousAppointmentDistanceMiles || 0)
          + (taggedProximitySlot.nextAppointmentDistanceMiles || 0),
        index: taggedIndex
      };
    }

    if (availableSlots.length <= 2) return null;

    const slotsWithProximity = availableSlots.map((slot, index) => {
      const prevTravel = slot.previousAppointmentTravelTimeMinutes || 0;
      const nextTravel = slot.nextAppointmentTravelTimeMinutes || 0;
      const prevDistance = slot.previousAppointmentDistanceMiles || 0;
      const nextDistance = slot.nextAppointmentDistanceMiles || 0;

      const hasAdjacent = (prevTravel > 0 || nextTravel > 0);
      const totalTravelTime = prevTravel + nextTravel;
      const totalDistance = prevDistance + nextDistance;

      return {
        slot,
        index,
        totalTravelTime,
        totalDistance,
        hasAdjacent,
        proximityScore: hasAdjacent ? totalTravelTime : Infinity
      };
    });

    const candidateSlots = slotsWithProximity.filter(s =>
      s.hasAdjacent && s.proximityScore < Infinity && s.index >= 2
    );

    if (candidateSlots.length === 0) return null;

    const closestSlot = candidateSlots.reduce((min, current) =>
      current.proximityScore < min.proximityScore ? current : min
    );

    return {
      slot: closestSlot.slot,
      totalTravelTime: closestSlot.totalTravelTime,
      totalDistance: closestSlot.totalDistance,
      index: closestSlot.index
    };
  }, [availableSlots]);

  const doSlotsMatchExactly = (
    firstSlot: Routemize_DTOs_SlotSuggestionDTO | undefined,
    secondSlot: Routemize_DTOs_SlotSuggestionDTO | undefined
  ): boolean => {
    if (!firstSlot || !secondSlot) return false;

    const firstDate = (firstSlot.suggestedDate || '').split('T')[0];
    const secondDate = (secondSlot.suggestedDate || '').split('T')[0];
    const firstUserKey = (firstSlot.assignedUserId || `${firstSlot.userDetail?.firstName || ''} ${firstSlot.userDetail?.lastName || ''}`.trim() || '').toLowerCase();
    const secondUserKey = (secondSlot.assignedUserId || `${secondSlot.userDetail?.firstName || ''} ${secondSlot.userDetail?.lastName || ''}`.trim() || '').toLowerCase();
    const userMatches = firstUserKey && secondUserKey ? firstUserKey === secondUserKey : true;

    return (
      firstDate === secondDate &&
      (firstSlot.startTime || '') === (secondSlot.startTime || '') &&
      (firstSlot.endTime || '') === (secondSlot.endTime || '') &&
      userMatches
    );
  };

  const isSameSlot = (
    firstSlot: Routemize_DTOs_SlotSuggestionDTO | undefined,
    secondSlot: Routemize_DTOs_SlotSuggestionDTO | undefined
  ): boolean => {
    if (!firstSlot || !secondSlot) return false;
    return (firstSlot.id && secondSlot.id && firstSlot.id === secondSlot.id) || doSlotsMatchExactly(firstSlot, secondSlot);
  };

  const bestForBusinessSlot = availableSlots[0];

  const effectivePreferredDate = useMemo(
    () => preferredDate || originalPreferences.date || existingRequestForContact?.preferredDate || prefilledDate || '',
    [preferredDate, originalPreferences.date, existingRequestForContact?.preferredDate, prefilledDate]
  );

  const effectivePreferredTime = useMemo(
    () => preferredTime || originalPreferences.time || existingRequestForContact?.preferredTimeRange || prefilledPreferredTime || '',
    [preferredTime, originalPreferences.time, existingRequestForContact?.preferredTimeRange, prefilledPreferredTime]
  );

  const doesSlotMatchPreferredTimeWindow = (slot: Routemize_DTOs_SlotSuggestionDTO): boolean => {
    if (!effectivePreferredTime || !slot.startTime) return true;
    const [hourStr = '0', minStr = '0'] = (slot.startTime || '').split(':');
    const slotMinutes = parseInt(hourStr, 10) * 60 + parseInt(minStr, 10);
    if (Number.isNaN(slotMinutes)) return true;

    const pref = effectivePreferredTime.toLowerCase();
    if (pref === 'morning' || pref === '09:00' || pref === 'mrn') return slotMinutes >= 360 && slotMinutes < 720;
    if (pref === 'afternoon' || pref === '13:00' || pref === 'aft') return slotMinutes >= 720 && slotMinutes < 1020;
    if (pref === 'evening' || pref === '17:00' || pref === 'eve') return slotMinutes >= 1020 && slotMinutes < 1260;

    const matchedOption = timeRangeOptions.find(
      (option) => option.code === effectivePreferredTime || option.text === effectivePreferredTime
    );
    if (matchedOption?.start && matchedOption?.end) {
      const [startH = 0, startM = 0] = matchedOption.start.split(':').map(Number);
      const [endH = 0, endM = 0] = matchedOption.end.split(':').map(Number);
      const windowStart = startH * 60 + startM;
      const windowEnd = endH * 60 + endM;
      if (!Number.isNaN(windowStart) && !Number.isNaN(windowEnd) && windowEnd > windowStart) {
        return slotMinutes >= windowStart && slotMinutes < windowEnd;
      }
    }
    if (matchedOption?.start) {
      const [optHour] = matchedOption.start.split(':').map(Number);
      if (!Number.isNaN(optHour)) {
        if (optHour < 12) return slotMinutes >= 360 && slotMinutes < 720;
        if (optHour < 17) return slotMinutes >= 720 && slotMinutes < 1020;
        return slotMinutes >= 1020 && slotMinutes < 1260;
      }
    }

    return true;
  };

  const doesSlotMatchPreferredDate = (slot: Routemize_DTOs_SlotSuggestionDTO): boolean => {
    if (!effectivePreferredDate || !slot.suggestedDate) return false;
    const prefDate = (effectivePreferredDate || '').split('T')[0];
    const slotDate = (slot.suggestedDate || '').split('T')[0];
    return prefDate === slotDate;
  };

  const doesSlotMatchCustomerPreferenceFully = (slot: Routemize_DTOs_SlotSuggestionDTO): boolean => {
    return doesSlotMatchPreferredDate(slot) && doesSlotMatchPreferredTimeWindow(slot);
  };

  const generateSlotInsights = (
    slot: Routemize_DTOs_SlotSuggestionDTO,
    slotType: 'business' | 'closest' | 'preference'
  ): string[] => {
    const insights: string[] = [];
    const distFrom = slot.previousAppointmentDistanceMiles;
    const distTo = slot.nextAppointmentDistanceMiles;
    const driveFrom = slot.previousAppointmentTravelTimeMinutes;
    const driveTo = slot.nextAppointmentTravelTimeMinutes;
    const prevLoc = (slot.previousAppointmentLocation || '').toLowerCase();
    const nextLoc = (slot.nextAppointmentLocation || '').toLowerCase();
    const prevIsHome = prevLoc.includes('home') || prevLoc.includes('start of day') || prevLoc.includes('workday start');
    const nextIsHome = nextLoc.includes('home') || nextLoc.includes('end of day') || nextLoc.includes('return to home');

    const closestDist = [
      (!prevIsHome && distFrom != null) ? distFrom : null,
      (!nextIsHome && distTo != null) ? distTo : null,
    ].filter((d): d is number => d != null).sort((a, b) => a - b)[0] ?? null;

    const closestDrive = [
      (!prevIsHome && driveFrom != null) ? driveFrom : null,
      (!nextIsHome && driveTo != null) ? driveTo : null,
    ].filter((d): d is number => d != null).sort((a, b) => a - b)[0] ?? null;

    // How soon (in days) is this slot from today? The Top Choice slot is picked
    // by a weighted blend of how *fast* the booking happens and how *close* it
    // sits to existing work. When the proximity side is weak, we want the
    // reasoning to lead with the speed/timing win instead of awkwardly stating
    // "nearest appointment is 27 miles away" — that distance isn't a *reason*,
    // it's the absence of one. We never expose the algorithm or weights; we
    // just pick honest copy that matches whichever axis actually won.
    const daysFromToday = calendarDaysFromToday(slot.suggestedDate);
    const proximityIsAWin = closestDist != null && closestDist < 15;

    if (slotType === 'business' || slotType === 'closest') {
      // Speed framing — only on Top Choice (`business`). The Proximity card's
      // pitch is route efficiency; layering "fast booking" on top muddies it.
      if (slotType === 'business' && daysFromToday != null) {
        if (daysFromToday <= 0) {
          insights.push('Earliest opening — available today');
        } else if (daysFromToday === 1) {
          insights.push('Earliest opening — available tomorrow');
        } else if (daysFromToday <= 3) {
          insights.push(`Quick turnaround — only ${daysFromToday} days out`);
        } else if (!proximityIsAWin && daysFromToday <= 7) {
          // No proximity win to lean on, but still a soon-ish booking.
          insights.push(`Available within ${daysFromToday} days — soonest open slot`);
        }
      }

      // Proximity framing — only when the distance is *actually* a win (< 15 mi).
      // Saying "27 miles to the nearest stop" reads as an anti-reason, so we
      // intentionally drop those tiers from the bullet list.
      if (proximityIsAWin && closestDist != null && closestDrive != null) {
        if (closestDist < 3) {
          insights.push(`Only ${formatDistance(closestDist, distUnit)} from an existing appointment — barely a detour`);
        } else if (closestDist < 8) {
          insights.push(`Only ${formatDistance(closestDist, distUnit)} from an existing appointment`);
        } else {
          insights.push(`${formatDistance(closestDist, distUnit)} from the nearest appointment on this route`);
        }
      }

      if (distFrom != null && distTo != null && !prevIsHome && !nextIsHome) {
        const totalDist = distFrom + distTo;
        if (totalDist < 10) {
          insights.push(`Fits between two stops with only ${formatDistance(totalDist, distUnit)} of total driving`);
        } else if (totalDist < 25) {
          insights.push(`${formatDistance(totalDist, distUnit)} total between adjacent appointments`);
        }
      }

      if (closestDrive != null && proximityIsAWin && insights.length < 2) {
        if (closestDrive < 8) {
          insights.push(`Under ${Math.ceil(closestDrive)} minutes of drive time added`);
        } else if (closestDrive < 20) {
          insights.push(`About ${Math.round(closestDrive)} minutes of additional drive time`);
        }
      }

      if (slot.gapBeforeLabel && insights.length < 2) {
        insights.push(slot.gapBeforeLabel);
      }

      if (slot.routeScore != null && insights.length < 2) {
        const roundedScore = Math.round(slot.routeScore);
        if (roundedScore >= 90) {
          insights.push(`Route score: ${roundedScore}% — excellent fit for the day`);
        } else if (roundedScore >= 75) {
          insights.push(`Route score: ${roundedScore}% — strong route efficiency`);
        }
      }

      if (insights.length === 0) {
        // Fallback when nothing notable surfaced. For Top Choice we still want a
        // "speed" cue if there's a reasonable date but no proximity / route win
        // to talk about, otherwise default to the generic efficiency line.
        if (slotType === 'business' && daysFromToday != null && daysFromToday <= 14) {
          insights.push('Earliest available slot that still fits the route');
        } else {
          insights.push('Best overall route efficiency for the day');
        }
      }
    }

    if (slotType === 'preference') {
      if (doesSlotMatchPreferredDate(slot) && doesSlotMatchPreferredTimeWindow(slot)) {
        insights.push("Matches the customer's requested date and time window");
      } else if (doesSlotMatchPreferredDate(slot)) {
        insights.push("On the customer's preferred date");
      } else if (doesSlotMatchPreferredTimeWindow(slot)) {
        insights.push("Within the customer's preferred time of day");
      }
      if (proximityIsAWin && closestDist != null) {
        insights.push(`Also only ${formatDistance(closestDist, distUnit)} from an existing appointment`);
      }
    }

    return insights;
  };

  const toggleRouteOverview = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRouteOverviewExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleSlotDetails = (key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSlotDetailsExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const renderRouteOverviewMap = (slot: Routemize_DTOs_SlotSuggestionDTO) => {
    const prevLeg = resolvePrevLeg(slot);
    const nextLeg = resolveNextLeg(slot);
    const driveTimeFrom = prevLeg.minutes ? Math.round(prevLeg.minutes) : null;
    const driveTimeTo = nextLeg.minutes ? Math.round(nextLeg.minutes) : null;
    const distanceFrom = prevLeg.miles != null ? formatDistance(prevLeg.miles, distUnit) : undefined;
    const distanceTo = nextLeg.miles != null ? formatDistance(nextLeg.miles, distUnit) : undefined;
    const fromAddress = slot.previousAppointmentLocation || 'Starting Location';
    const toAddress = slot.nextAppointmentLocation || 'End of Day';

    if (driveTimeFrom === null && driveTimeTo === null) return null;

    return (
      <div className="flex items-start">
        <div className="flex-1 text-center min-w-0">
          <div className="w-7 h-7 mx-auto bg-info-100 rounded-full flex items-center justify-center mb-1">
            <CircleDot className="h-3.5 w-3.5 text-info-600" />
          </div>
          <div className="text-[11px] font-medium text-ink-700">Previous</div>
          <div className="text-[10px] text-ink-400 truncate px-1 mt-0.5" title={fromAddress}>
            {fromAddress.length > 14 ? fromAddress.substring(0, 14) + '...' : fromAddress}
          </div>
          <div className="mt-0.5 text-[11px] font-semibold text-info-600">
            {(slot.previousAppointmentStartTime || slot.previousAppointmentStartTimeUtc)
              ? <>
                  {formatTimeString(slot.previousAppointmentStartTime || slot.previousAppointmentStartTimeUtc || '')}
                  {(slot.previousAppointmentEndTime || slot.previousAppointmentEndTimeUtc) && (
                    <span className="text-ink-400 font-normal"> - {formatTimeString(slot.previousAppointmentEndTime || slot.previousAppointmentEndTimeUtc || '')}</span>
                  )}
                </>
              : 'Start'}
          </div>
        </div>

        <div className="flex flex-col items-center justify-center pt-1.5 px-1 flex-shrink-0" style={{minWidth: '50px'}}>
          <div className="text-[10px] font-semibold text-ink-600">{distanceFrom || '—'}</div>
          <div className="flex items-center my-0.5">
            <div className="w-7 h-0.5 bg-ink-200 rounded"></div>
            <ArrowRight className="h-3 w-3 text-ink-400 -ml-0.5" />
          </div>
          <div className="text-[10px] font-semibold text-ink-600">{driveTimeFrom || '—'} min</div>
        </div>

        <div className="flex-1 text-center min-w-0">
          <div className="w-7 h-7 mx-auto bg-ink-900 rounded-full flex items-center justify-center mb-1 ring-2 ring-ink-200 ring-offset-1">
            <MapPin className="h-3.5 w-3.5 text-white" />
          </div>
          <div className="text-[11px] font-medium text-ink-900">This Appt</div>
          <div className="mt-0.5 text-[11px] font-semibold text-ink-700">
            {slot.startTime ? getDisplaySlotTime(slot.startTime) : ''}
            {slot.endTime && (
              <span className="text-ink-400 font-normal"> - {getDisplaySlotTime(slot.endTime)}</span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-center justify-center pt-1.5 px-1 flex-shrink-0" style={{minWidth: '50px'}}>
          <div className="text-[10px] font-semibold text-ink-600">{distanceTo || '—'}</div>
          <div className="flex items-center my-0.5">
            <div className="w-7 h-0.5 bg-ink-200 rounded"></div>
            <ArrowRight className="h-3 w-3 text-ink-400 -ml-0.5" />
          </div>
          <div className="text-[10px] font-semibold text-ink-600">{driveTimeTo || '—'} min</div>
        </div>

        <div className="flex-1 text-center min-w-0">
          <div className="w-7 h-7 mx-auto bg-ink-100 rounded-full flex items-center justify-center mb-1">
            <Home className="h-3.5 w-3.5 text-ink-500" />
          </div>
          <div className="text-[11px] font-medium text-ink-700">Next</div>
          <div className="text-[10px] text-ink-400 truncate px-1 mt-0.5" title={toAddress}>
            {toAddress.length > 14 ? toAddress.substring(0, 14) + '...' : toAddress}
          </div>
          <div className="mt-0.5 text-[11px] font-semibold text-ink-600">
            {(slot.nextAppointmentStartTime || slot.nextAppointmentStartTimeUtc)
              ? <>
                  {formatTimeString(slot.nextAppointmentStartTime || slot.nextAppointmentStartTimeUtc || '')}
                  {(slot.nextAppointmentEndTime || slot.nextAppointmentEndTimeUtc) && (
                    <span className="text-ink-400 font-normal"> - {formatTimeString(slot.nextAppointmentEndTime || slot.nextAppointmentEndTimeUtc || '')}</span>
                  )}
                </>
              : 'End'}
          </div>
        </div>
      </div>
    );
  };

  const customerPreferenceSlot = useMemo(() => {
    if (availableSlots.length < 2 || !bestForBusinessSlot) return null;
    const candidates = availableSlots.slice(1);

    const typedCustomerSlot = candidates.find((slot) =>
      isCustomerPreferredSlot(slot) && !isSameSlot(bestForBusinessSlot, slot)
        && doesSlotMatchPreferredTimeWindow(slot)
    );
    if (typedCustomerSlot) return typedCustomerSlot;

    const timeMatchingSlot = candidates.find((slot) =>
      !isSameSlot(bestForBusinessSlot, slot) && doesSlotMatchPreferredTimeWindow(slot)
    );
    if (timeMatchingSlot) return timeMatchingSlot;

    return null;
  }, [availableSlots, bestForBusinessSlot, effectivePreferredTime, timeRangeOptions]);

  const shouldShowClosestNext = useMemo(() => {
    if (!closestSlotData || !availableSlots[0]) return false;
    // Suppress only when Top Choice IS the proximity-optimal slot — rendering
    // the exact same card twice in a row reads as a UI bug. When the proximity
    // pick instead overlaps the Customer Preference slot, keep both cards
    // visible so dispatchers on the internal form can compare them side-by-side
    // (each card frames the slot through a different lens — preference vs.
    // route-efficiency).
    if (doSlotsMatchExactly(availableSlots[0], closestSlotData.slot)) return false;
    return true;
  }, [availableSlots, closestSlotData]);

  const isCustomerPreferenceDateStale = useMemo(() => {
    if (!effectivePreferredDate) return true;
    const prefDate = new Date(effectivePreferredDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return prefDate < today;
  }, [effectivePreferredDate]);

  const customerPreferredDateLabel = useMemo(() => {
    if (!effectivePreferredDate) return 'No preferred date';
    const parsedDate = parseLocalDate(effectivePreferredDate) || new Date(`${effectivePreferredDate}T12:00:00`);
    if (Number.isNaN(parsedDate.getTime())) return effectivePreferredDate;
    return parsedDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }, [effectivePreferredDate]);

  const customerPreferredTimeLabel = useMemo(() => {
    if (!effectivePreferredTime) return 'Any time';
    const matched = timeRangeOptions.find(
      (option) => option.code === effectivePreferredTime || option.text === effectivePreferredTime
    );
    if (matched?.text) return matched.text;
    if (effectivePreferredTime === 'morning') return 'Morning';
    if (effectivePreferredTime === 'afternoon') return 'Afternoon';
    if (effectivePreferredTime === 'evening') return 'Evening';
    return effectivePreferredTime;
  }, [effectivePreferredTime, timeRangeOptions]);

  // Build a graceful "no availability" state from a scheduling response when no slots come back
  const showNoAvailabilityState = (
    schedulingResponse: Routemize_DTOs_SchedulingResponse,
    formUsersForRequest: Routemize_DTOs_UserSmallDTO[],
    options?: { afterServiceAreaOverride?: boolean },
  ) => {
    const dateLabel = customerPreferredDateLabel;
    const timeLabel = customerPreferredTimeLabel;
    const userName = formUsersForRequest.length === 1
      ? `${formUsersForRequest[0].firstName || ''} ${formUsersForRequest[0].lastName || ''}`.trim() || undefined
      : undefined;

    // Only surface a `detail` that's safe to show a user. Raw backend
    // exceptions (e.g. "Business logic processing failed: Sequence contains no
    // elements", which can happen for an out-of-service-area address) are
    // suppressed so the graceful "No Availability" copy carries the message
    // instead of leaking an internal error.
    let detail = '';
    if (schedulingResponse.errors && schedulingResponse.errors.length > 0) {
      for (const error of schedulingResponse.errors) {
        if (error.technicalDetails && error.technicalDetails.trim() !== '' && !isRawTechnicalError(error.technicalDetails)) {
          detail = error.technicalDetails;
          break;
        }
        if (error.message && !error.message.includes('An error occurred during scheduling') && !isRawTechnicalError(error.message)) {
          detail = error.message;
          break;
        }
      }
      if (!detail && !isRawTechnicalError(schedulingResponse.errors[0].message)) {
        detail = schedulingResponse.errors[0].message || '';
      }
    }
    if (!detail && schedulingResponse.summary && !isRawTechnicalError(schedulingResponse.summary)) {
      detail = schedulingResponse.summary;
    }
    const lowerDetail = detail.toLowerCase();
    const isGenericNoSlotsMessage =
      lowerDetail.includes('no available time slots') ||
      lowerDetail.includes('error occurred while calculating slots');

    setErrors({});
    setAvailableSlots([]);
    setSelectedSlot(null);
    setNoAvailabilityInfo({
      isFull: true,
      dateLabel,
      timeLabel,
      userName,
      detail: detail && !isGenericNoSlotsMessage ? detail : undefined,
      afterServiceAreaOverride: options?.afterServiceAreaOverride,
    });
    setShowAvailableSlots(true);
    setCurrentStep('booking');
    setCompletedSteps(prev => [...prev.filter(s => s !== 'preferences'), 'preferences']);
    setIsRoutemizing(false);
  };

  const additionalSlotCandidates = useMemo(() => {
    const visiblePrimarySlots = [
      bestForBusinessSlot,
      shouldShowClosestNext ? closestSlotData?.slot : undefined,
      !isCustomerPreferenceDateStale ? customerPreferenceSlot || undefined : undefined
    ].filter(Boolean) as Routemize_DTOs_SlotSuggestionDTO[];

    const uniqueSlots: Routemize_DTOs_SlotSuggestionDTO[] = [];
    availableSlots.forEach((slot) => {
      if (visiblePrimarySlots.some((primarySlot) => isSameSlot(primarySlot, slot))) return;
      if (uniqueSlots.some((existingSlot) => isSameSlot(existingSlot, slot))) return;
      uniqueSlots.push(slot);
    });

    return uniqueSlots;
  }, [
    availableSlots,
    bestForBusinessSlot,
    closestSlotData,
    customerPreferenceSlot,
    isCustomerPreferenceDateStale,
    shouldShowClosestNext
  ]);

  const getDisplaySlotTime = (timeValue: string | undefined): string => {
    if (!timeValue) return '';
    return formatTimeString(timeValue);
  };

  // Bucket additional candidates first by *date* (preferred vs. other days) and
  // then by time-of-day within each date group. The previous "single bucket"
  // version always rendered everything under a header that claimed the slots
  // were on the customer's preferred date — even when they weren't, which
  // looked like an off-by-one date bug to dispatchers.
  const additionalSlotsByDateAndTimeOfDay = useMemo(() => {
    type TimeOfDayBuckets = {
      morning: Routemize_DTOs_SlotSuggestionDTO[];
      afternoon: Routemize_DTOs_SlotSuggestionDTO[];
      evening: Routemize_DTOs_SlotSuggestionDTO[];
    };
    const empty = (): TimeOfDayBuckets => ({ morning: [], afternoon: [], evening: [] });
    const result = {
      onPreferredDate: empty(),
      onOtherDates: empty(),
    };

    additionalSlotCandidates.forEach((slot) => {
      const startTime = slot.startTime || '';
      const [hourPart = '0'] = startTime.split(':');
      const hour = parseInt(hourPart, 10);
      const target = doesSlotMatchPreferredDate(slot)
        ? result.onPreferredDate
        : result.onOtherDates;

      if (Number.isNaN(hour) || hour < 12) {
        target.morning.push(slot);
      } else if (hour < 17) {
        target.afternoon.push(slot);
      } else {
        target.evening.push(slot);
      }
    });

    return result;
    // doesSlotMatchPreferredDate is a stable closure over effectivePreferredDate,
    // so depending on additionalSlotCandidates + effectivePreferredDate is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalSlotCandidates, effectivePreferredDate]);

  useEffect(() => {
    if (isOpen && !isLoadingBookingForms && !bookingFormsLoadedRef.current) {
      loadBookingForms().catch(error => {
      });
    }
  }, [isOpen, isLoadingBookingForms]);

  // Preload settings for every booking form so the event-selection step
  // can render accurate durations and event colors before the user picks one.
  useEffect(() => {
    if (!isOpen || bookingForms.length === 0) return;

    const formsNeedingSettings = bookingForms
      .map((form) => form.id)
      .filter((id): id is string => !!id && !bookingFormSettings[id]);

    if (formsNeedingSettings.length === 0) return;

    let cancelled = false;

    Promise.all(
      formsNeedingSettings.map(async (formId) => {
        try {
          const settings = await BookingFormSettingService.getApiAppBookingFormSettingGetByFormId(formId);
          return { formId, settings };
        } catch (error) {
          return null;
        }
      })
    ).then((results) => {
      if (cancelled) return;
      const updates = results.filter((r): r is { formId: string; settings: Routemize_DTOs_BookingFormSettingDto } => !!r && !!r.settings);
      if (updates.length === 0) return;
      setBookingFormSettings((prev) => {
        const next = { ...prev };
        updates.forEach(({ formId, settings }) => {
          if (!next[formId]) {
            next[formId] = settings;
          }
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, bookingForms, bookingFormSettings]);

  useEffect(() => {
    if (!isOpen || !existingRequestId) return;
    let cancelled = false;

    RequestsService.getApiAppRequestsGetById(existingRequestId)
      .then(async (request) => {
        if (cancelled || !request) return;
        setExistingRequestForContact(request);
        if (!preferredDate && request.preferredDate) {
          setPreferredDate(request.preferredDate);
        }
        if (!preferredTime && request.preferredTimeRange) {
          setPreferredTime(request.preferredTimeRange);
        }
        if (request.leadSource && !selectedLeadSource) {
          setSelectedLeadSource(request.leadSource);
        }

        if (request.contactId) {
          setCreatedContactId(request.contactId);
        }
        if (isValidEntityId(request.contactAddressId)) {
          setCreatedContactAddressId(request.contactAddressId);
        } else {
          // Legacy request whose contactAddressId is empty but whose contact has a
          // saved address that is shown as selected. Capture the resolved address id
          // into modal state so a valid service address is carried (ClickUp 86bc0dkw2).
          const resolved = resolveContactAddressFromDto(request.contact, request.contactAddressId);
          if (resolved?.id) {
            setCreatedContactAddressId(resolved.id);
            setSelectedAddressId(resolved.id);
          }
        }

        const addressFields = await resolveRequestAddressFields(
          request.contact,
          request.contactAddressId
        );
        if (cancelled) return;

        if (addressFields.address1) {
          setAddress(addressFields.address1);
        }
        if (addressFields.address2) {
          setAddress2(addressFields.address2);
        }
        if (addressFields.city) {
          setCity(addressFields.city);
        }
        if (addressFields.state) {
          setState(addressFields.state);
        }
        if (addressFields.zipCode) {
          setZipCode(addressFields.zipCode);
        }

        // Pre-fill custom question answers so the manual booking flow shows
        // the customer's existing responses instead of an empty form.
        // customAnswers is keyed by questionId (UUID) since the form rendering
        // looks up values via customAnswers[question.id].
        if (request.answers && request.answers.length > 0) {
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          const answersMap: { [key: string]: string } = {};
          request.answers.forEach((answer) => {
            // Prefer questionId; fall back to answer.question if it looks like a UUID
            const key = answer.questionId
              || (answer.question && uuidRegex.test(answer.question) ? answer.question : undefined);
            if (key && answer.answer != null) {
              answersMap[key] = answer.answer;
            }
          });
          if (Object.keys(answersMap).length > 0) {
            setCustomAnswers((prev) => ({ ...answersMap, ...prev }));
          }
        }
      })
      .catch((error) => {
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, existingRequestId, preferredDate, preferredTime]);

  // Re-read the request from the server and re-resolve its service address.
  // The booking flow creates the request row (InitiateBookingRequest) seconds
  // before the submitted address is mapped to it (UpdateContactAddress). If the
  // operator opened this modal inside that window, the one-time load effect
  // above captured an empty address, and every slot-generation guard would then
  // hard-fail on that stale local state until a manual retry ("wait a few
  // seconds"). Before refusing to generate slots, pull the request again — the
  // address has usually landed by now — and re-seed the same address state the
  // load effect seeds. Returns whether a usable service address is available
  // afterwards plus the resolved values, so the caller can build its PUT/slot
  // payload from them instead of waiting for the async React state setters to
  // flush within the same tick. Single attempt, no polling. (ClickUp 86bc0dn3v)
  const refreshServiceAddressFromServer = async (
    requestId: string
  ): Promise<{ ok: boolean; contactAddressId?: string; fields: RequestAddressFields }> => {
    try {
      const request = await RequestsService.getApiAppRequestsGetById(requestId);
      if (!request) return { ok: false, fields: {} };

      let resolvedContactAddressId: string | undefined;
      if (isValidEntityId(request.contactAddressId)) {
        resolvedContactAddressId = request.contactAddressId;
        setCreatedContactAddressId(request.contactAddressId);
      } else {
        const resolved = resolveContactAddressFromDto(request.contact, request.contactAddressId);
        if (resolved?.id) {
          resolvedContactAddressId = resolved.id;
          setCreatedContactAddressId(resolved.id);
          setSelectedAddressId(resolved.id);
        }
      }

      const fields = await resolveRequestAddressFields(request.contact, request.contactAddressId);
      if (fields.address1) setAddress(fields.address1);
      if (fields.address2) setAddress2(fields.address2);
      if (fields.city) setCity(fields.city);
      if (fields.state) setState(fields.state);
      if (fields.zipCode) setZipCode(fields.zipCode);

      const hasFields = !!(fields.address1 && fields.city && fields.state && fields.zipCode);
      return {
        ok: isValidEntityId(resolvedContactAddressId) || hasFields,
        contactAddressId: resolvedContactAddressId,
        fields,
      };
    } catch {
      return { ok: false, fields: {} };
    }
  };

  // Auto-select event type when prefilledEventTypeId is provided
  useEffect(() => {
    if (prefilledEventTypeId && eventTypes.length > 0 && !eventType) {
      const prefilledEvent = eventTypes.find(type => type.id === prefilledEventTypeId);
      if (prefilledEvent) {
        setEventType(prefilledEvent);
        // Mark event step as completed since it's auto-selected
        if (!completedSteps.includes('event')) {
          setCompletedSteps(prev => [...prev, 'event']);
        }
      }
    }
  }, [prefilledEventTypeId, eventTypes, eventType, completedSteps]);

  // Track if we've auto-triggered routemize for skipToSlotSelection
  const autoRoutemizeTriggeredRef = useRef(false);

  // Load form details on-demand when an event type is selected
  useEffect(() => {
    if (eventType?.id) {
      loadBookingFormDetails(eventType.id).catch(error => {
      });
    }
  }, [eventType?.id]);

  const loadBookingForms = async () => {
    // Prevent duplicate calls
    if (isLoadingBookingForms || bookingFormsLoadedRef.current) {
      return;
    }

    setIsLoadingBookingForms(true);
    try {
      // Only load the list - details will be loaded on-demand when a form is selected.
      // MaxResultCount must be passed explicitly: ABP's default page size is 10,
      // which silently drops forms for tenants with more than 10 booking forms.
      const forms = await BookingFormService.getApiAppBookingFormGetList(undefined, 0, 100);
      setBookingForms(forms?.items || []);
    } catch (error) {
      // Don't throw - just continue with empty state
    } finally {
      // Mark as loaded (success or failure) so a failed call — e.g. an expired/invalid
      // token — doesn't retry forever. isLoadingBookingForms flipping false→true→false
      // re-fires the calling effect every time; without this, a failure never sets the
      // ref and the effect keeps calling loadBookingForms() in a tight loop.
      bookingFormsLoadedRef.current = true;
      setIsLoadingBookingForms(false);
    }
  };

  // Load form details on-demand when a specific form is selected
  // Returns the loaded users array for immediate use (since state updates are async)
  const loadBookingFormDetails = async (formId: string): Promise<Routemize_DTOs_UserSmallDTO[]> => {
    // Skip if already loaded
    if (bookingFormDetails[formId] && bookingFormSettings[formId] && bookingFormUsers[formId]) {
      return bookingFormUsers[formId];
    }

    // Load details, settings, users, and questions in parallel for the selected form
    const [details, settings, users, questions] = await Promise.all([
      // Load full form details
      (async () => {
        try {
          if (!bookingFormDetails[formId]) {
            return await BookingFormService.getApiAppBookingFormGetBookingFormDetail(formId);
          }
          return bookingFormDetails[formId];
        } catch (error) {
          return null;
        }
      })(),
      // Load form settings
      (async () => {
        try {
          if (!bookingFormSettings[formId]) {
            return await BookingFormSettingService.getApiAppBookingFormSettingGetByFormId(formId);
          }
          return bookingFormSettings[formId];
        } catch (error) {
          return {
            timeAtLocationMinutes: 45,
            defaultPostAppointmentBufferMinutes: 0
          } as Routemize_DTOs_BookingFormSettingDto;
        }
      })(),
      // Load assigned users
      (async () => {
        try {
          if (!bookingFormUsers[formId]) {
            return await BookingFormUserService.getApiAppBookingFormUserGetFormUsers(formId);
          }
          return bookingFormUsers[formId];
        } catch (error) {
          return [];
        }
      })(),
      // Load custom questions
      (async () => {
        try {
          if (!bookingFormQuestions[formId]) {
            const fetched = await BookingFormQuestionService.getApiAppBookingFormQuestionGetFormQuestion(formId);
            return (fetched || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
          }
          return bookingFormQuestions[formId];
        } catch (error) {
          return [];
        }
      })()
    ]);

    // Update state with loaded data
    if (details) {
      setBookingFormDetails(prev => ({ ...prev, [formId]: details }));
    }
    if (settings) {
      setBookingFormSettings(prev => ({ ...prev, [formId]: settings }));
    }
    if (users) {
      const filtered = (users as Routemize_DTOs_UserSmallDTO[]).filter(
        (u) => u.id && (assignableUserIds.size === 0 || assignableUserIds.has(u.id))
      );
      setBookingFormUsers(prev => ({ ...prev, [formId]: filtered }));
    }
    if (questions) {
      setBookingFormQuestions(prev => ({ ...prev, [formId]: questions }));
    }

    // Return users for immediate use (state updates are async)
    return users || [];
  };

  // Helper function to get display text for a time range code
  const getTimeRangeDisplay = (code: string) => {
    if (!code) return 'Flexible';
    const option = timeRangeOptions.find(opt => opt.code === code);
    return option ? `${option.code} (${option.text})` : code;
  };

  // Load time range options from Master Data API
  const loadTimeRangeOptions = async () => {
    try {
      setLoadingTimeRanges(true);
      const response = await MasterDataService.getApiAppMasterDataGet(
        'PreferredTimeRanges',
        false
      );

      if (response && response.length > 0 && response[0].items) {
        const options = response[0].items.map(item => ({
          code: item.code || '',
          text: item.text || '',
          helpText: item.helpText || '',
          start: item.extraProperties?.start || '',
          end: item.extraProperties?.end || ''
        }));
        setBaseTimeRangeOptions(options);
      }
    } catch (error) {
      console.error('Failed to load time range options:', error);
      // Fallback to default options
      setBaseTimeRangeOptions([
        { code: 'ANY', text: 'Anytime', helpText: 'Flexible with timing', start: '', end: '' },
        { code: 'MRN', text: 'Morning', helpText: '8AM - 12PM', start: '08:00', end: '12:00' },
        { code: 'AFT', text: 'Afternoon', helpText: '12PM - 5PM', start: '12:00', end: '17:00' },
        { code: 'EVE', text: 'Evening', helpText: '5PM - 8PM', start: '17:00', end: '20:00' }
      ]);
    } finally {
      setLoadingTimeRanges(false);
    }
  };

  useEffect(() => {
    // Load time range options when modal opens
    if (isOpen) {
      loadTimeRangeOptions();
    }
  }, [isOpen]);

  useEffect(() => {
    if (loadingTimeRanges || timeRangeOptions.length === 0) return;

    if (preferredTime) {
      const stillValid = timeRangeOptions.some(
        opt => opt.code === preferredTime || opt.text === preferredTime
      );
      if (stillValid) return;

      // Requests created from the public booking form persist `preferredTimeRange`
      // as a lowercase string ("morning"/"afternoon"/etc.) rather than the master
      // data code, so a naive code/text equality check misses it and we would
      // silently fall through to flex/first below — flipping the operator's view
      // to the wrong time-of-day. Resolve those aliases against the live options
      // so the customer's actual preference is preserved.
      const resolved = resolvePreferredTimeOption(preferredTime, timeRangeOptions);
      if (resolved) {
        setPreferredTime(resolved.code);
        return;
      }
    }

    // Empty or unresolvable preferredTime (e.g. a reopened request that carries
    // an unmatchable raw "HH:mm - HH:mm" range, or no selection yet): fall through
    // to a valid default so the UI always presents a submittable selection and a
    // corrupted/stale request self-heals instead of leaving the operator with no
    // selection and a "Preferred time is required" error.
    const flex = timeRangeOptions.find(opt => isFlexiblePreferredTimeCode(opt.code));
    setPreferredTime(flex?.code ?? timeRangeOptions[0]?.code ?? '');
  }, [loadingTimeRanges, timeRangeOptions, preferredTime]);


  // Debounce search to avoid too many API calls
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleContactSearch = async (query: string) => {
    setContactSearchQuery(query);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (query.length < 2) {
      setSearchedContacts([]);
      return;
    }
    
    // Debounce the search by 300ms
    searchTimeoutRef.current = setTimeout(async () => {
      setSearchingContacts(true);
      try {
        // Search for contacts with the contact service
        const results = await ContactService.getApiAppContactGetList(
          query,           // searchText
          undefined,       // status
          undefined,       // tagIds
          undefined,       // source
          undefined,       // isFavorite
          undefined,       // lastContactDateFrom
          undefined,       // lastContactDateTo
          undefined,       // sorting
          undefined,       // skipCount
          10               // maxResultCount
        );
        
        // Map the results to display format
        const mappedContacts = (results.items || []).map((contact: any) => ({
          id: contact.id,
          name: contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unknown',
          extraProperties: {
            contactInfo: contact,
            addresses: contact.addresses || []
          }
        }));
        
        setSearchedContacts(mappedContacts);
      } catch (error) {
        console.error('Failed to search contacts:', error);
        setSearchedContacts([]);
      } finally {
        setSearchingContacts(false);
      }
    }, 300);
  };
  
  const handleContactSelect = (contact: ContactSearchResult) => {
    setSelectedContact(contact);
    const contactInfo = contact.extraProperties?.contactInfo;
    
    if (contactInfo) {
      // Pre-fill form with contact info
      setFirstName(contactInfo.firstName || '');
      setLastName(contactInfo.lastName || '');
      setEmail(contactInfo.email || '');
      setPhone(formatUSPhone(contactInfo.phoneNumber || ''));
      if (contactInfo.businessName) {
        setIsBusiness(true);
        setBusinessName(contactInfo.businessName);
      } else {
        setIsBusiness(false);
        setBusinessName('');
      }
      
      // If contact has addresses, use the first one
      const addresses = contact.extraProperties?.addresses || [];
      if (addresses.length > 0) {
        const firstAddress = addresses[0];
        setSelectedAddressId(firstAddress.id ?? null);
        setAddress(firstAddress.addressLine1 || '');
        setCity(firstAddress.city || '');
        setState(firstAddress.state || '');
        setZipCode(firstAddress.zipCode || '');
      }
      
      // Pre-fill lead source from contact record if available
      if (contactInfo.leadSource) {
        setSelectedLeadSource(contactInfo.leadSource);
      }
      
      // Store the contact ID for later use
      setCreatedContactId(contact.id || null);
    }
    
    // Clear search
    setContactSearchQuery('');
    setSearchedContacts([]);
  };

  // Check if contact has an existing pending request for the selected booking form
  const checkForExistingRequest = async (contactEmail: string, contactPhone: string, bookingFormId: string) => {
    // Don't check if we already have an existing request ID passed as prop
    if (existingRequestId) {
      return null;
    }

    setCheckingExistingRequest(true);
    try {
      const existingRequest = await RequestsService.getApiAppRequestsGetPendingRequestByBookingFormAndContact(
        bookingFormId,
        contactEmail || undefined,
        contactPhone || undefined
      );

      if (existingRequest && existingRequest.id) {
        setExistingRequestForContact(existingRequest);

        // Automatically use the existing request - set request ID and pre-fill data
        setCreatedRequestId(existingRequest.id || null);

        // IMPORTANT: Also capture the existing contact and address IDs to prevent duplicate creation
        if (existingRequest.contactId) {
          setCreatedContactId(existingRequest.contactId);
        }
        if (existingRequest.contactAddressId) {
          setCreatedContactAddressId(existingRequest.contactAddressId);
        }

        // Pre-fill scheduling preferences from existing request
        // Helper to check if date is valid (not null, empty, default "0001-01-01", or in the past)
        const isValidDate = (dateStr: string | null | undefined): boolean => {
          if (!dateStr || dateStr.trim() === '') return false;
          if (dateStr.startsWith('0001-01-01') || dateStr.startsWith('0001/01/01')) return false;
          // Check if date is in the past
          const date = new Date(dateStr);
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Reset time to start of day
          if (date < today) return false;
          return true;
        };

        if (isValidDate(existingRequest.preferredDate)) {
          setPreferredDate(existingRequest.preferredDate!);
        }
        if (isValidDate(existingRequest.preferredSecondaryDate)) {
          setPreferredSecondaryDate(existingRequest.preferredSecondaryDate!);
        }
        if (existingRequest.preferredTimeRange) {
          setPreferredTime(existingRequest.preferredTimeRange);
        }

        // Pre-fill custom answers if they exist.
        // customAnswers is keyed by questionId (UUID) since the form rendering
        // looks up values via customAnswers[question.id].
        if (existingRequest.answers && existingRequest.answers.length > 0) {
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          const answersMap: { [key: string]: string } = {};
          existingRequest.answers.forEach((answer: any) => {
            const key = answer.questionId
              || (answer.question && uuidRegex.test(answer.question) ? answer.question : undefined);
            if (key && answer.answer != null) {
              answersMap[key] = answer.answer;
            }
          });
          if (Object.keys(answersMap).length > 0) {
            setCustomAnswers(answersMap);
          }
        }

        return existingRequest;
      }
    } catch (error: any) {
      // 404 means no existing request found - this is expected
      if (error?.status !== 404) {
        console.error('Error checking for existing request:', error);
      }
      setExistingRequestForContact(null);
    } finally {
      setCheckingExistingRequest(false);
    }
    return null;
  };

  // Validate drive times against slot constraints
  const validateDriveTimes = async (customerCoords: {lat: number; lng: number}) => {
    if (!slotConstraints) {
      setDriveTimeWarning(null);
      return;
    }

    try {
      // Calculate drive time from previous location to customer
      let driveTimeTo = 0;
      if (prevAppointment?.latitude && prevAppointment?.longitude) {
        // Estimate drive time using haversine distance (simplified)
        const distance = calculateHaversineDistance(
          { lat: prevAppointment.latitude, lng: prevAppointment.longitude },
          customerCoords
        );
        driveTimeTo = Math.round(distance * 2); // Rough estimate: 2 min per mile
      }

      // Calculate drive time from customer to next location
      let driveTimeFrom = 0;
      if (nextAppointment?.latitude && nextAppointment?.longitude) {
        const distance = calculateHaversineDistance(
          customerCoords,
          { lat: nextAppointment.latitude, lng: nextAppointment.longitude }
        );
        driveTimeFrom = Math.round(distance * 2); // Rough estimate: 2 min per mile
      }

      // Check if combined drive time exceeds constraint
      const totalDriveTime = driveTimeTo + driveTimeFrom;
      if (totalDriveTime > slotConstraints.maxCombinedDriveTime) {
        setDriveTimeWarning(
          `⚠️ Warning: Estimated drive time (${driveTimeTo}min to + ${driveTimeFrom}min from = ${totalDriveTime}min total) exceeds the ${slotConstraints.maxCombinedDriveTime}min limit for this ${slotConstraints.appointmentDuration}min appointment slot. This booking may not fit in the available time.`
        );
      } else {
        setDriveTimeWarning(null);
      }
    } catch (error) {
      console.error('Error validating drive times:', error);
      setDriveTimeWarning(null);
    }
  };

  // Helper: Calculate haversine distance between two points in miles
  const calculateHaversineDistance = (
    point1: { lat: number; lng: number },
    point2: { lat: number; lng: number }
  ): number => {
    const R = 3959; // Earth's radius in miles
    const dLat = (point2.lat - point1.lat) * Math.PI / 180;
    const dLng = (point2.lng - point1.lng) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(point1.lat * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180) *
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Cleanup function to delete orphaned request for internal manual bookings
  const cleanupOrphanedRequest = async () => {
    // Only cleanup for internal manual bookings that created a request but no appointment
    if (isManualBooking && createdRequestId && !appointmentCreated) {
      try {
        await RequestsService.deleteApiAppRequestsDelete(createdRequestId);
      } catch (error) {
        console.error('Failed to cleanup orphaned request:', error);
        // Don't show error to user - this is background cleanup
      }
    }
  };

  const resetForm = () => {
    // Step management - respect skip props
    if (skipToSlotSelection) {
      setCurrentStep('booking');
      setCompletedSteps(['customer', 'event', 'preferences']);
      setIsRoutemizing(true);
      autoRoutemizeTriggeredRef.current = false; // Reset to allow re-trigger
      setUserChangedPreferences(false);
    } else if (skipCustomerStep) {
      setCurrentStep('event');
      setCompletedSteps(['customer']);
    } else {
      setCurrentStep('customer');
      setCompletedSteps([]);
    }

    // Customer details - reset to prefilled values if available
    setFirstName(prefilledCustomer?.firstName || '');
    setLastName(prefilledCustomer?.lastName || '');
    setEmail(prefilledCustomer?.email || '');
    setPhone(formatUSPhone(prefilledCustomer?.phone || ''));
    setIsBusiness(false);
    setBusinessName('');
    setAddress(prefilledCustomer?.address || '');
    setAddress2(prefilledCustomer?.address2 || '');
    setCity(prefilledCustomer?.city || '');
    setState(prefilledCustomer?.state || '');
    setZipCode(prefilledCustomer?.zipCode || '');

    // Contact search and selection
    setContactSearchQuery('');
    setSearchingContacts(false);
    setSearchedContacts([]);
    setSelectedContact(null);
    setSelectedAddressId(null);
    setShowNewAddressForm(false);

    // Event type details
    setEventType(null);


    // Routemize preferences - reset to prefilled values if available
    // Don't reset these if we're working with an existing request - keep the values
    if (!existingRequestId) {
      setPreferredDate(prefilledDate || '');
      setPreferredSecondaryDate(prefilledCustomer?.preferredSecondaryDate || '');
      setPreferredTime(prefilledPreferredTime || prefilledStartTime || '');
    } else {
      // For existing requests, always use the prefilled values
      setPreferredDate(prefilledDate || '');
      setPreferredSecondaryDate(prefilledCustomer?.preferredSecondaryDate || '');
      setPreferredTime(prefilledPreferredTime || '');
    }

    setNotes('');
    setAccessCode('');
    setSelectedLeadSource('');

    // Communication preferences - reset to form settings defaults
    setSendSmsConfirmation(selectedFormSettings?.smsConfirmation ?? true);
    setSendEmailConfirmation(selectedFormSettings?.emailConfirmation ?? true);
    setSendSmsReminders(selectedFormSettings?.smsReminder ?? true);
    setSendEmailReminders(selectedFormSettings?.emailReminder ?? true);

    // Routemize flow states
    setIsRoutemizing(false);
    setShowAvailableSlots(false);
    setPendingSlotsData(null);
    setSelectedSlot(null);
    setCreatedRequestId(existingRequestId || null);
    // Preserve contact IDs when skipCustomerStep is true
    setCreatedContactId(skipCustomerStep && prefilledCustomer?.contactId ? prefilledCustomer.contactId : null);
    setCreatedContactAddressId(skipCustomerStep && prefilledCustomer?.contactAddressId ? prefilledCustomer.contactAddressId : null);
    setAppointmentCreated(false);

    // Existing request detection states
    setExistingRequestForContact(null);
    setCheckingExistingRequest(false);

    // Form validation
    setErrors({});
    setBookingAlert(null);
    setIsSubmitting(false);

    // Clear outside service area indicator
    setIsOutsideServiceArea(false);
    setOutsideServiceAreaDetail(null);

    // Clear no availability info
    setNoAvailabilityInfo(null);

    // Custom questions answers
    setCustomAnswers({});

    // Reset booking forms loading guard to allow fresh load on next open
    bookingFormsLoadedRef.current = false;
    geocodedPrefillRef.current = false;

    // Reset revert tracking
    setRequestModifiedForRoutemize(false);
    setOriginalScheduleMethod(null);
    setOriginalStatus(null);
    setRequestStatusMayBeAdvanced(false);
  };

  // Revert the request's scheduleMethod and/or status if we touched it during the
  // Routemize flow but didn't create an appointment. GetSlots advances an existing
  // request into a granular "live booking" status server-side, so abandoning the
  // flow without this restore leaves the request falsely flagged as a live customer
  // booking on the Requests board.
  const revertRequestIfNeeded = async () => {
    const requestIdToRevert = existingRequestId || createdRequestId;
    const touchedRequest = requestModifiedForRoutemize || requestStatusMayBeAdvanced;
    if (!touchedRequest || !requestIdToRevert || appointmentCreated) {
      return;
    }

    try {
      const revertPayload: Routemize_DTOs_UpdateRequestsDTO = {
        id: requestIdToRevert,
      };
      // Only restore scheduleMethod if we captured a REAL original value.
      // Never guess MANUAL: UpdateRequestsDTO.Validate runs unconditionally on
      // scheduleMethod (no Status==Scheduling guard), so a MANUAL payload without
      // PreferredStartTime/EndTime/AssignedUserId is rejected (400). The old
      // `|| MANUAL` fallback fired whenever originalScheduleMethod was null (the
      // typical public-form / preference-only request), the empty catch swallowed
      // the 400, and the request stayed stuck in the ROUTEMIZED live-booking state
      // — surfacing "Preferred time is required" on every later attempt. An omitted
      // scheduleMethod is null server-side, so Validate skips the method branches:
      // a safe no-op restore.
      if (requestModifiedForRoutemize && originalScheduleMethod) {
        revertPayload.scheduleMethod = originalScheduleMethod;
      }
      // Restore the pre-flow status so the request leaves any granular live-booking
      // state that GetSlots may have applied.
      if (requestStatusMayBeAdvanced && originalStatus) {
        revertPayload.status = originalStatus;
      }
      await RequestsService.putApiAppRequestsUpdate(revertPayload);
    } catch (error) {
      // Non-fatal: a failed revert must never block closing the modal, but it
      // must be diagnosable rather than swallowed silently.
      console.error('Failed to revert request after abandoning booking flow:', error);
    }
  };

  const handleClose = async () => {
    await revertRequestIfNeeded();
    resetForm();
    onClose();
  };

  const validateCustomerForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!firstName) newErrors.firstName = 'First name is required';
    else if (!isValidNameLength(firstName)) newErrors.firstName = `First name must be ${MAX_NAME_LENGTH} characters or less`;
    else if (!isValidName(firstName)) newErrors.firstName = 'First name can only contain letters, spaces, hyphens and apostrophes';
    if (!lastName) newErrors.lastName = 'Last name is required';
    else if (!isValidNameLength(lastName)) newErrors.lastName = `Last name must be ${MAX_NAME_LENGTH} characters or less`;
    else if (!isValidName(lastName)) newErrors.lastName = 'Last name can only contain letters, spaces, hyphens and apostrophes';
    if (!email) newErrors.email = 'Email is required';
    else if (!isValidEmail(email)) newErrors.email = 'Please enter a valid email address';
    if (!phone) newErrors.phone = 'Phone number is required';
    if (!address) newErrors.address = 'Address is required';
    if (!city) newErrors.city = 'City is required';
    if (!state) newErrors.state = 'State is required';
    if (!zipCode) newErrors.zipCode = 'Zip code is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateEventForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!eventType) newErrors.eventType = 'Please select a service type';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Pull the most informative human-readable string out of a SchedulingResponse
  // when it reports the address is outside the service area. Falls through the
  // errors collection (preferring technicalDetails) before resorting to summary.
  const extractOutsideServiceAreaDetail = (
    schedulingResponse: Routemize_DTOs_SchedulingResponse,
  ): string => {
    if (schedulingResponse.errors && schedulingResponse.errors.length > 0) {
      for (const error of schedulingResponse.errors) {
        if (error.technicalDetails && error.technicalDetails.trim() !== '') {
          return error.technicalDetails;
        }
        if (error.message && !error.message.includes('An error occurred during scheduling')) {
          return error.message;
        }
      }
      const fallback = schedulingResponse.errors[0].message;
      if (fallback) return fallback;
    }
    return schedulingResponse.summary || '';
  };

  // Mark the request as out-of-service-area on the backend so the dashboard's
  // "Outside Area" badge and other downstream views stay in sync. The write is
  // best-effort — failures here shouldn't block the staff from continuing to
  // book the appointment.
  const persistOutsideServiceAreaFlag = async (requestId: string): Promise<void> => {
    try {
      await RequestsService.putApiAppRequestsUpdate({
        id: requestId,
        rejectionReason: Routemize_Constants_SlotRejectionReason.OUTSIDE_SERVICE_AREA,
      });
    } catch (err) {
      console.warn('Failed to persist OutsideServiceArea flag on request', err);
    }
  };

  /**
   * Promotes the staged slots into the actual booking view. Called by the
   * `SlotDiscoveryAnimation`'s `onComplete` once its counter has finished
   * ticking up to the slot total. Mirrors the inline state-flips the
   * success paths used to do directly before the animation was introduced.
   */
  const applyPendingSlots = () => {
    setPendingSlotsData((current) => {
      if (!current) return null;
      const { slots, preferences, toastInfo, afterServiceAreaOverride, schedulingResponse } = current;

      if (!slots || slots.length === 0) {
        const formUsersForPending = (eventType?.id ? bookingFormUsers[eventType.id] : []) || [];
        if (schedulingResponse) {
          showNoAvailabilityState(schedulingResponse, formUsersForPending, {
            afterServiceAreaOverride: afterServiceAreaOverride ?? false,
          });
        } else {
          setNoAvailabilityInfo({
            isFull: true,
            dateLabel: customerPreferredDateLabel,
            timeLabel: customerPreferredTimeLabel,
            afterServiceAreaOverride: afterServiceAreaOverride ?? false,
          });
          setAvailableSlots([]);
          setSelectedSlot(null);
          setShowAvailableSlots(true);
          setCurrentStep('booking');
          setCompletedSteps((prev) => [
            ...prev.filter((s) => s !== 'preferences'),
            'preferences',
          ]);
          setIsRoutemizing(false);
        }
        return null;
      }

      setNoAvailabilityInfo(null);
      setAvailableSlots(slots);
      setShowAvailableSlots(true);
      setCurrentStep('booking');
      setCompletedSteps((prev) => [
        ...prev.filter((s) => s !== 'preferences'),
        'preferences',
      ]);
      if (slots[0]) {
        setSelectedSlot(slots[0]);
      }
      setOriginalPreferences(preferences);
      if (toastInfo) {
        showInfo(toastInfo);
      }
      setIsRoutemizing(false);
      return null;
    });
  };

  const handleRoutemizeBooking = async () => {
    try {
      setIsRoutemizing(true);
      setNoAvailabilityInfo(null);
      setIsOutsideServiceArea(false);
      setOutsideServiceAreaDetail(null);
      setErrors({});
      // Clear any stale staged slots from a prior run so the animation
      // restarts at phase 1 instead of jumping straight to the counter.
      setPendingSlotsData(null);

      // Determine if we're using an existing request (from prop OR from contact check)
      const usingExistingRequest = existingRequestId || createdRequestId;
      const requestIdToUse = existingRequestId || createdRequestId;

      // If we have an existing request ID, skip request creation and just get slots
      if (usingExistingRequest && requestIdToUse) {
        if (!createdRequestId) {
          setCreatedRequestId(requestIdToUse);
        }

        // Capture the request's original status and scheduleMethod before running any
        // slot generation. GetSlots advances the request into a granular "live booking"
        // status server-side (e.g. SlotsPresented), which would otherwise make the
        // request look like a live customer booking after the operator closes the modal
        // without confirming. We restore these in revertRequestIfNeeded on abandon.
        if (!requestStatusMayBeAdvanced) {
          try {
            const RequestsServiceForFetch = (await import('../../api/services/RequestsService')).RequestsService;
            const currentRequestData = await RequestsServiceForFetch.getApiAppRequestsGetById(requestIdToUse);
            setOriginalScheduleMethod(currentRequestData.scheduleMethod || null);
            setOriginalStatus(currentRequestData.status || null);
          } catch (err) {
          }
          setRequestStatusMayBeAdvanced(true);
        }

        // Update the request if the user changed preferences in the modal.
        // Skip only on the initial auto-trigger when skipToSlotSelection is true
        // (the request already has its data). If the user navigated back via
        // "Change Preferences", userChangedPreferences will be true.
        if (!skipToSlotSelection || userChangedPreferences) {
          // Update the existing request with new preferences and set scheduleMethod to ROUTEMIZED
          const selectedTimeOptionForUpdate = resolveSelectedTimeOption(preferredTime);

          // Never send a ROUTEMIZED update with a null preferred time: the backend
          // requires PreferredTimeRange for that method and would reject the PUT
          // with "Preferred time is required". Bounce the operator back to the
          // preferences step with a clear inline message instead.
          if (!selectedTimeOptionForUpdate) {
            setErrors({ preferredTime: 'Please select a preferred time' });
            setCurrentStep('preferences');
            showError('Please select a preferred time before finding available slots.');
            setIsRoutemizing(false);
            return;
          }

          // Resolve the assignee for the (possibly changed) event type. Smart-
          // assignment forms send the operator's preferred provider; other
          // forms keep the existing assignee when still valid, otherwise fall
          // back to the form's first user — matching the create-path default.
          const targetFormUsers = eventType?.id
            ? (bookingFormUsers[eventType.id] ?? eventType.assignedUsers ?? [])
            : [];
          const targetFormUserIds = targetFormUsers
            .map(u => u.id)
            .filter((id): id is string => Boolean(id));
          let resolvedAssignedUserId: string | undefined;
          if (isSmartAssignmentForm) {
            // When the admin picks "auto-assign" on a smart-assignment form,
            // `preferredAssigneeId` is the empty string. Sending `undefined` here
            // makes the backend skip the assignment (RequestsDomainService:799 treats
            // null/undefined as "leave unchanged"), so a previously-pinned user
            // stays on the row and the re-Routemize call still auto-pins to them.
            // Send Guid.Empty to explicitly clear the column so Smart Assignment
            // runs fresh.
            resolvedAssignedUserId = preferredAssigneeId || '00000000-0000-0000-0000-000000000000';
          } else {
            const existingAssignedId = existingRequestForContact?.assignedUserId || '';
            if (existingAssignedId && targetFormUserIds.includes(existingAssignedId)) {
              resolvedAssignedUserId = existingAssignedId;
            } else if (targetFormUserIds.length > 0) {
              resolvedAssignedUserId = targetFormUserIds[0];
            }
          }

          // bookingFormId is sent to support changing event type during
          // "Change Preferences" before the appointment is booked. The
          // generated UpdateRequestsDTO does not yet expose it (see
          // docs/implementation/internal-change-preferences-backend-spec.md);
          // the intersection cast becomes a no-op once the proxy regenerates.
          const updatePayload = {
            id: requestIdToUse,
            preferredDate: preferredDate || undefined,
            preferredSecondaryDate: preferredSecondaryDate || undefined,
            preferredTimeRange: preferredTime || undefined,
            preferredStartTime: selectedTimeOptionForUpdate?.start || undefined,
            preferredEndTime: selectedTimeOptionForUpdate?.end || undefined,
            scheduleMethod: Routemize_Constants_ScheduleMethod.ROUTEMIZED,
            contactAddressId: createdContactAddressId || undefined,
            addressLine1: address || undefined,
            addressLine2: address2 || undefined,
            city: city || undefined,
            state: state || undefined,
            zipCode: zipCode || undefined,
            bookingFormId: eventType?.id || undefined,
            assignedUserId: resolvedAssignedUserId,
            // Some bookings are created on the fly and only get a lead source
            // once the operator picks one in the form ("customer picks lead
            // source" flows). Write the chosen lead source back onto the
            // request so the appointment/dashboard — and the contact sync
            // below, which reads it back — reflect the selection. Sent as
            // `undefined` when empty so we never wipe an existing lead source
            // (e.g. a discovered pending request that already carries one).
            leadSource: effectiveLeadSource || undefined,
          } as Routemize_DTOs_UpdateRequestsDTO & { bookingFormId?: string };
          // Diagnostic — visible in DevTools so we can confirm the operator's
          // preferred provider is actually reaching the backend before
          // GetSlots runs. Safe to remove once the assignedUserId-honoring
          // path is verified end-to-end.
          // eslint-disable-next-line no-console
          console.debug('[Routemize] PUT requests/update before GetSlots', {
            requestId: requestIdToUse,
            assignedUserId: resolvedAssignedUserId,
            bookingFormId: eventType?.id,
            isSmartAssignmentForm,
            preferredAssigneeId,
          });
          const RequestsServiceImport = (await import('../../api/services/RequestsService')).RequestsService;
          await RequestsServiceImport.putApiAppRequestsUpdate(updatePayload);

          // Persist custom answers via the same DTO/endpoint the public form
          // uses. UpdateRequestsDTO has no `answers` field, so this is the
          // canonical way to save answers on an already-existing request.
          await persistAnswersToRequest(requestIdToUse);

          setRequestModifiedForRoutemize(true);
        }

        // GetSlots hard-fails in the backend DataGatheringPhase when the
        // request has no ContactAddressId. The PUT update above carries
        // the address fields so the backend can backfill, but if the
        // operator left the address blank entirely there is nothing to
        // backfill from — refuse loudly so the bug is visible at booking
        // time rather than producing zero slots with no explanation.
        if ((!createdContactAddressId || createdContactAddressId === '00000000-0000-0000-0000-000000000000')
            && (!address || !city || !state || !zipCode)) {
          // The submitted address may have been mapped to the request just after
          // our one-time load captured empty state (multi-step booking flow,
          // ClickUp 86bc0dn3v). Re-read the request once before failing; the slot
          // call below reads the address straight off the request by id, so a
          // successful re-fetch lets us fall through instead of erroring out.
          const recovered = requestIdToUse
            ? await refreshServiceAddressFromServer(requestIdToUse)
            : { ok: false, fields: {} as RequestAddressFields, contactAddressId: undefined };
          if (!recovered.ok) {
            showError('A complete service address is required before generating slots. Please add an address and try again.');
            setIsRoutemizing(false);
            return;
          }
          // fall through — the request now carries a valid service address
        }

        // Get available slots for the existing request. We pass
        // PreferredUserId when the operator manually overrode the picker
        // on a smart-assignment form so the backend filters slots to that
        // user (mirrors getRescheduleSlots semantics).
        const schedulingResponse = await fetchSlotsWithPreferredUser(requestIdToUse, {
          preferredUserId: isSmartAssignmentForm ? (preferredAssigneeId || undefined) : undefined,
        });

        // Outside-service-area handling: never silently bypass on the first
        // pass. If the backend flags the address as out-of-area, route the
        // operator to the override prompt (the "Outside Service Area" empty
        // state) regardless of whether slots came back. They must explicitly
        // click "Book Anyway (Override)" — `handleBookAnyway` then re-runs
        // slot generation with `bypassServiceArea=true` and shows the slots.
        const outsideForExisting = schedulingResponse.travelDistanceExceededAction !== undefined
          && schedulingResponse.travelDistanceExceededAction !== null;
        if (outsideForExisting) {
          setIsOutsideServiceArea(true);
          setOutsideServiceAreaDetail(extractOutsideServiceAreaDetail(schedulingResponse) || null);
          void persistOutsideServiceAreaFlag(requestIdToUse);
          const formUsersForOutside = (eventType?.id ? bookingFormUsers[eventType.id] : []) || [];
          showNoAvailabilityState(schedulingResponse, formUsersForOutside);
          return;
        }

        const slotsResponse = schedulingResponse.slots || [];

        if (slotsResponse && slotsResponse.length > 0) {
          // Stage the slots — the Slot Discovery animation's `onComplete`
          // will hand off to `applyPendingSlots` to swap in the booking
          // view. Keeping `isRoutemizing` true here keeps the animation
          // visible until the counter has ticked up to the slot total.
          setPendingSlotsData({
            slots: slotsResponse,
            preferences: {
              date: preferredDate,
              secondaryDate: preferredSecondaryDate,
              time: preferredTime,
            },
          });
        } else {
          const formUsersForRequest = (eventType?.id ? bookingFormUsers[eventType.id] : []) || [];
          showNoAvailabilityState(schedulingResponse, formUsersForRequest);
        }

        return;
      }

      // Otherwise, create a new request (for new bookings)
      // Validate we have contactId and bookingFormId
      if (!createdContactId) {
        throw new Error('Contact ID not available. Please go back to customer details.');
      }
      if (!eventType?.id) {
        throw new Error('Booking form not selected. Please go back to event type selection.');
      }

      // Ensure we have a contact address ID (find existing or create new)
      let finalContactAddressId = createdContactAddressId;

      if (!finalContactAddressId && address && city && state && zipCode) {
        const addressId = await findOrCreateContactAddress({
          contactId: createdContactId,
          addressLine1: address,
          addressLine2: address2,
          city,
          state,
          zipCode,
          country
        });
        if (addressId) {
          finalContactAddressId = addressId;
          setCreatedContactAddressId(finalContactAddressId);
        }
      }

      // Refuse to create a Routemize request without a contact address —
      // GetSlots hard-fails in DataGatheringPhase if ContactAddressId is
      // empty, leaving an orphan request and a confused operator.
      if (!finalContactAddressId || finalContactAddressId === '00000000-0000-0000-0000-000000000000') {
        // If a request row already exists (e.g. discovered pending request), the
        // submitted address may have landed on it after our one-time load
        // (multi-step booking flow, ClickUp 86bc0dn3v). Re-read once before
        // failing and adopt the freshly-mapped address id.
        const recovered = requestIdToUse
          ? await refreshServiceAddressFromServer(requestIdToUse)
          : { ok: false, fields: {} as RequestAddressFields, contactAddressId: undefined };
        if (recovered.ok && recovered.contactAddressId) {
          finalContactAddressId = recovered.contactAddressId;
          setCreatedContactAddressId(finalContactAddressId);
        } else {
          throw new Error('A complete service address is required before generating slots. Please add an address and try again.');
        }
      }

      // Now create request with the address ID
      // Use the preferred date from the form (required), with prefilled as fallback
      const finalPreferredDate = preferredDate || prefilledDate || formatToLocalDateString(new Date());
      
      // Ensure form users are loaded - if not, load them now and use the returned data directly
      let formUsers = bookingFormUsers[eventType.id] || [];
      if (formUsers.length === 0) {
        formUsers = await loadBookingFormDetails(eventType.id);
      }

      // The form must have at least one assignable user. We don't, however,
      // require us to PIN to one of them — for smart-assignment forms with
      // the picker on Auto we send `undefined` so the backend's own rotation
      // / territory routing decides who actually gets the booking. The
      // membership check still fires when the form has nobody assigned.
      if (formUsers.length === 0) {
        console.error('No team members found:', {
          eventTypeId: eventType.id,
          bookingFormUsers,
          eventTypeAssignedUsers: eventType.assignedUsers
        });
        throw new Error('No team members assigned to this booking form. Please contact support.');
      }

      // For smart-assignment forms, respect the preferred provider picker:
      //   - empty string ('') = Auto → send undefined → backend rotates.
      //   - specific id        = manual override → send that id.
      // For solo (non-smart) forms there's no rotation — fall back to the
      // first form user (typically the only user).
      const assignedUserId: string | undefined = isSmartAssignmentForm
        ? (preferredAssigneeId || undefined)
        : (formUsers[0].id || undefined);

      // Get the selected time range option to extract start/end times.
      // Resolve code/text/alias so an unnormalized value still maps to a real
      // option instead of silently nulling the start/end companion fields.
      const selectedTimeOption = resolveSelectedTimeOption(preferredTime);

      // A ROUTEMIZED create requires a resolvable preferred time (the backend
      // needs PreferredTimeRange). Block and return the operator to the
      // preferences step rather than sending a null time and hitting
      // "Preferred time is required".
      if (!selectedTimeOption) {
        setErrors({ preferredTime: 'Please select a preferred time' });
        setCurrentStep('preferences');
        showError('Please select a preferred time before finding available slots.');
        setIsRoutemizing(false);
        return;
      }

      const requestData: Routemize_DTOs_CreateRequestsDTO = {
        contactId: createdContactId,
        bookingFormId: eventType.id,
        contactAddressId: finalContactAddressId || undefined,
        preferredDate: finalPreferredDate,
        ...(preferredSecondaryDate && preferredSecondaryDate.trim() !== '' ? { preferredSecondaryDate } : {}),
        preferredTimeRange: preferredTime || null,
        preferredStartTime: selectedTimeOption?.start || null,
        preferredEndTime: selectedTimeOption?.end || null,
        assignedUserId: assignedUserId,
        scheduleMethod: Routemize_Constants_ScheduleMethod.ROUTEMIZED,
        status: Routemize_Constants_RequestStatus.SCHEDULING,
        leadSource: effectiveLeadSource || null,
        origin: Routemize_Constants_RequestOrigin.INTERNAL
      };

      const requestResponse = await RequestsService.postApiAppRequestsCreate(requestData);

      if (!requestResponse.id) {
        throw new Error('Failed to create request - no ID returned');
      }

      setCreatedRequestId(requestResponse.id);

      // Persist custom answers via the public form's DTO/endpoint so the
      // appointment detail view can render them in "Form Responses".
      await persistAnswersToRequest(requestResponse.id);

      // Then get available slots. PreferredUserId is forwarded for
      // smart-assignment forms so the backend can pin slots to the
      // operator's manual override.
      const schedulingResponse = await fetchSlotsWithPreferredUser(requestResponse.id, {
        preferredUserId: isSmartAssignmentForm ? (preferredAssigneeId || undefined) : undefined,
      });

      // Outside-service-area handling — see comment in the existing-request
      // branch above. Never silently bypass on the first pass: if the backend
      // flags the address as out-of-area, route to the override prompt and
      // require an explicit "Book Anyway (Override)" click before slots show.
      const outsideForNew = schedulingResponse.travelDistanceExceededAction !== undefined
        && schedulingResponse.travelDistanceExceededAction !== null;
      if (outsideForNew) {
        setIsOutsideServiceArea(true);
        setOutsideServiceAreaDetail(extractOutsideServiceAreaDetail(schedulingResponse) || null);
        void persistOutsideServiceAreaFlag(requestResponse.id);
        showNoAvailabilityState(schedulingResponse, formUsers);
        return;
      }

      const slotsResponse = schedulingResponse.slots || [];

      if (slotsResponse && slotsResponse.length > 0) {
        // Stage for the Slot Discovery animation — see notes on the
        // existing-request branch above for why we don't flip the booking
        // view directly here anymore.
        setPendingSlotsData({
          slots: slotsResponse,
          preferences: {
            date: preferredDate,
            secondaryDate: preferredSecondaryDate,
            time: preferredTime,
          },
        });
      } else {
        showNoAvailabilityState(schedulingResponse, formUsers);
      }
    } catch (error: any) {
      console.error('❌ Failed to routemize:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));
      console.error('Error body:', error?.body);
      console.error('Error body error:', error?.body?.error);
      console.error('Validation errors:', error?.body?.error?.validationErrors);
      
      let errorMessage = existingRequestId 
        ? 'Failed to find available time slots for this request. Please try again.'
        : 'Failed to find available time slots. Please try again.';
      
      // Handle validation errors array format
      if (error?.body?.error?.validationErrors && Array.isArray(error.body.error.validationErrors)) {
        const validationErrors = error.body.error.validationErrors;
        const errorDetails = validationErrors
          .map((err: any) => {
            if (typeof err === 'object' && err.message) {
              return err.message;
            } else if (typeof err === 'object' && err.members && err.members.length > 0) {
              return `${err.members.join(', ')}: ${err.message || 'Validation failed'}`;
            }
            return JSON.stringify(err);
          })
          .join('; ');
        errorMessage = errorDetails || 'Validation errors occurred';
      } 
      // Handle validation errors object format
      else if (error?.body?.error?.validationErrors && typeof error.body.error.validationErrors === 'object') {
        const validationErrors = error.body.error.validationErrors;
        const errorDetails = Object.entries(validationErrors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ');
        errorMessage = errorDetails;
      } else {
        // Surface the daily appointment-cap message (and avoid raw ABP/EF
        // placeholders) when no field-level validation errors are present.
        errorMessage = resolveSchedulingErrorMessage(error, errorMessage);
      }
      
      surfaceBookingFailure(error, errorMessage, { bounceToExactPicker: false });
      setIsRoutemizing(false);
    }
  };

  // Auto-trigger slot loading when skipToSlotSelection is true and we have all required data
  useEffect(() => {
    // When we have an existing request, we don't need eventType — handleRoutemizeBooking
    // uses the requestId directly to get slots.
    const canTrigger = skipToSlotSelection && isOpen && !autoRoutemizeTriggeredRef.current && !showAvailableSlots &&
      (existingRequestId || eventType);

    if (canTrigger) {
      autoRoutemizeTriggeredRef.current = true;
      // Small delay to ensure all state is properly set
      setTimeout(() => {
        handleRoutemizeBooking();
      }, 100);
    }

    // Reset the ref when modal closes
    if (!isOpen) {
      autoRoutemizeTriggeredRef.current = false;
    }
  }, [skipToSlotSelection, isOpen, eventType, existingRequestId, showAvailableSlots]);

  const handleSlotSelect = (slot: Routemize_DTOs_SlotSuggestionDTO) => {
    const sameSlot = selectedSlot?.id === slot.id;
    setSelectedSlot(slot);
    if (sameSlot) return;
    setBookingAlert(null);
    setErrors((prev) => {
      if (!prev.general) return prev;
      const next = { ...prev };
      delete next.general;
      return next;
    });
  };

  // ── Exact-time picker: derive assignee, window, and overlap state ──
  // The picker piggybacks on the same booking pipeline that consumes a
  // chosen slot. We just need to (a) know who to validate against, (b)
  // know the picked window in real UTC so we can compare against the
  // assignee's calendar, and (c) feed the picked time back through
  // `handleSlotSelect` once it's confirmed.

  // All slots in a single GetSlots response share the same assignee on the
  // non-smart-assignment path; on smart-assignment, the operator's manual
  // preferred-provider pick wins (matches how RoutemizeEngine targets
  // GetSlots). Falling back to the first slot's assignee covers both, and
  // `userDetail.id` is a second fallback for forms where the backend only
  // populates the nested user object.
  const exactPickerAssigneeId = useMemo(() => {
    if (preferredAssigneeId) return preferredAssigneeId;
    const top = availableSlots[0];
    return top?.assignedUserId || top?.userDetail?.id || '';
  }, [preferredAssigneeId, availableSlots]);

  // Duration: prefer the actual span of the top recommended slot because
  // the backend has already applied any per-form duration overrides (e.g.
  // smart-assignment forms can stretch the visit). `eventType.durationMinutes`
  // is a fallback for the no-slots case. The hard-coded 30-min floor was
  // wrong — when the form is a 45-min visit we'd mis-validate against a
  // 30-min window and silently approve overlapping picks.
  const exactPickerDurationMinutes = useMemo(() => {
    const top = availableSlots[0];
    if (top?.startTime && top?.endTime) {
      const startMin = timeStringToMinutes(top.startTime);
      const endMin = timeStringToMinutes(top.endTime);
      if (startMin != null && endMin != null && endMin > startMin) {
        return endMin - startMin;
      }
    }
    if (eventType?.durationMinutes && eventType.durationMinutes > 0) {
      return eventType.durationMinutes;
    }
    return 30;
  }, [availableSlots, eventType?.durationMinutes]);

  // Picked window in real UTC. `tenantLocalToUtcDate` interprets the
  // wall-clock against the tenant's IANA zone so the conversion is
  // correct regardless of the admin's browser timezone. The end time is
  // sourced from the (editable) end-time picker so admins can shorten /
  // extend the visit on the fly; only when end < start (the admin hasn't
  // adjusted it yet) do we fall back to start + default duration.
  const exactPickerWindow = useMemo(() => {
    if (!exactDate || !exactTime || !companyTimezone) return null;
    const startUtc = tenantLocalToUtcDate(exactDate, exactTime, companyTimezone);
    if (Number.isNaN(startUtc.getTime())) return null;

    const startMin = timeStringToMinutes(exactTime);
    const endMin = timeStringToMinutes(exactEndTime);
    const usingFallback = endMin == null || startMin == null || endMin <= startMin;
    const effectiveEndStr = usingFallback
      ? addMinutesToTimeString(exactTime, exactPickerDurationMinutes)
      : exactEndTime;
    const effectiveDuration = usingFallback
      ? exactPickerDurationMinutes
      : (endMin as number) - (startMin as number);

    const endUtc = addMinutes(startUtc, effectiveDuration);
    return { startUtc, endUtc, endTimeStr: effectiveEndStr, durationMinutes: effectiveDuration };
  }, [exactDate, exactTime, exactEndTime, companyTimezone, exactPickerDurationMinutes]);

  const exactPickerIsInPast = useMemo(() => {
    if (!exactPickerWindow) return false;
    return exactPickerWindow.startUtc.getTime() < Date.now();
  }, [exactPickerWindow]);

  // Company schedule entry for the picked date. `null` means we have no
  // schedule data to consult (still loading / not configured), which the
  // guardrail below treats as "no opinion" rather than blocking.
  const exactPickerCompanyDay = useMemo(
    () => getCompanyDayScheduleForDate(workSchedule?.dailySchedules, exactDate),
    [workSchedule?.dailySchedules, exactDate]
  );

  // Load the assignee's own hours for the picked date. Keyed on assignee +
  // date only (not on the picker being open) so collapsing / re-expanding
  // the picker never drops the resolved hours. A request-id ref drops stale
  // responses (mirrors exactValidateRequestRef); a failure falls back to
  // company hours.
  const exactAssigneeDayKey =
    showAvailableSlots && exactPickerAssigneeId && exactDate
      ? `${exactPickerAssigneeId}|${exactDate}`
      : '';

  useEffect(() => {
    if (!exactAssigneeDayKey) {
      exactAssigneeDayRequestRef.current++;
      return;
    }

    const reqId = ++exactAssigneeDayRequestRef.current;
    assigneeWorkWindowsManager
      .fetch(exactPickerAssigneeId, exactDate)
      .then((day) => {
        if (reqId === exactAssigneeDayRequestRef.current) {
          setExactAssigneeDayResult({ key: exactAssigneeDayKey, day });
        }
      })
      .catch((err) => {
        console.warn('Failed to load assignee work hours:', err);
        if (reqId === exactAssigneeDayRequestRef.current) {
          setExactAssigneeDayResult({ key: exactAssigneeDayKey, day: null });
        }
      });
    // exactPickerAssigneeId / exactDate are captured by exactAssigneeDayKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exactAssigneeDayKey]);

  // The assignee's hours for the current assignee + date, or null when there
  // is no assignee or the fetch failed (company-hours fallback). While the
  // fetch for the current key is in flight the hours are "pending": neither
  // the company-hours guardrail nor the time clamp may act on them, or an
  // extended-day time would be clamped / flagged before the real hours land.
  const exactAssigneeDayPending =
    !!exactAssigneeDayKey && exactAssigneeDayResult?.key !== exactAssigneeDayKey;
  const exactAssigneeDay =
    exactAssigneeDayKey && exactAssigneeDayResult?.key === exactAssigneeDayKey
      ? exactAssigneeDayResult.day
      : null;

  // Guardrail: the exact-time picker must not book outside working hours
  // (ClickUp 86bavym40 — bookings landed outside working hours with no
  // availability override, leaving the schedule and Availability out of
  // sync). Two failure modes: the picked date isn't a work day at all, or
  // the picked window spills past the day's hours. When the assignee's own
  // hours are loaded they are the rule — a per-date availability override
  // can extend (or open) the day (ClickUp 86bcj7m6e); otherwise the company
  // schedule is the fallback.
  const exactPickerOutsideHours = useMemo<
    | { kind: 'non-work-day' }
    | { kind: 'outside-window'; hoursLabel: string }
    | null
  >(() => {
    if (exactAssigneeDayPending) return null;
    if (exactAssigneeDay) {
      if (!exactTime || !exactPickerWindow) {
        return exactAssigneeDay.isWorking ? null : { kind: 'non-work-day' };
      }
      const violation = getAssigneeHoursViolation(exactAssigneeDay, exactTime, exactPickerWindow.endTimeStr);
      if (!violation) return null;
      if (violation.kind === 'non-work-day') return violation;
      return {
        kind: 'outside-window',
        hoursLabel: violation.windows
          .map((w) => `${formatTimeString(w.start)} – ${formatTimeString(w.end)}`)
          .join(', '),
      };
    }

    if (!exactPickerCompanyDay) return null;
    if (!exactPickerCompanyDay.isWorkDay) return { kind: 'non-work-day' };

    const { start: companyStart, end: companyEnd } = exactPickerCompanyDay;
    if (!companyStart || !companyEnd) return null;
    if (!exactTime || !exactPickerWindow) return null;

    const companyStartMin = timeStringToMinutes(companyStart);
    const companyEndMin = timeStringToMinutes(companyEnd);
    const pickedStartMin = timeStringToMinutes(exactTime);
    const pickedEndMin = timeStringToMinutes(exactPickerWindow.endTimeStr);
    if (
      companyStartMin == null ||
      companyEndMin == null ||
      pickedStartMin == null ||
      pickedEndMin == null
    ) {
      return null;
    }

    // A wrapped end (end < start) means the window crossed midnight, which
    // is always past close.
    const outside =
      pickedStartMin < companyStartMin ||
      pickedEndMin > companyEndMin ||
      pickedEndMin < pickedStartMin;
    return outside
      ? {
          kind: 'outside-window',
          hoursLabel: `${formatTimeString(companyStart)} – ${formatTimeString(companyEnd)}`,
        }
      : null;
  }, [exactAssigneeDayPending, exactAssigneeDay, exactPickerCompanyDay, exactTime, exactPickerWindow]);

  // Clamp the time-of-day pickers so out-of-hours times can't even be
  // selected. Uses the assignee's hours for that date when loaded (so an
  // extended availability day widens the picker), else that day's company
  // work hours, else the largest work-hour gap in the weekly schedule —
  // never a 24-hour list. Booking-form first/last appointment times can
  // only tighten the window.
  const exactPickerTimeBounds = useMemo(
    () =>
      getExactTimePickerBounds(
        workSchedule?.dailySchedules,
        exactDate,
        {
          start: selectedFormSettings?.firstAppointmentTime,
          end: selectedFormSettings?.lastAppointmentTime,
        },
        exactAssigneeDay ?? undefined
      ),
    [
      workSchedule?.dailySchedules,
      exactDate,
      selectedFormSettings?.firstAppointmentTime,
      selectedFormSettings?.lastAppointmentTime,
      exactAssigneeDay,
    ]
  );

  useEffect(() => {
    // Hold off while the assignee's hours are loading: the company-hours
    // fallback would permanently clamp a time an extended day allows.
    if (exactAssigneeDayPending) return;
    const { minTime, maxTime } = exactPickerTimeBounds;
    if (!minTime && !maxTime) return;
    setExactTime((prev) => {
      if (!prev) return prev;
      const next = clampHHmmToRange(prev, minTime, maxTime);
      return next === prev ? prev : next;
    });
    setExactEndTime((prev) => {
      if (!prev) return prev;
      const next = clampHHmmToRange(prev, minTime, maxTime);
      return next === prev ? prev : next;
    });
  }, [exactAssigneeDayPending, exactPickerTimeBounds.minTime, exactPickerTimeBounds.maxTime]);

  // Seed the picker every time we land on (or back on) the slot-selection
  // screen so the inputs always carry sensible defaults. Anchored to the
  // top recommended slot, then falls back to the customer's preferred date
  // / 9 AM so the picker is usable even when no slots came back.
  useEffect(() => {
    if (!showAvailableSlots) return;
    const recommended = availableSlots[0];
    const fallbackDate = preferredDate || formatToLocalDateString(new Date());
    const seedDate = recommended?.suggestedDate?.split('T')[0] || fallbackDate;
    const seedTime = recommended?.startTime?.slice(0, 5) || '09:00';
    const seedEnd =
      recommended?.endTime?.slice(0, 5) ||
      addMinutesToTimeString(seedTime, exactPickerDurationMinutes);
    setExactExpanded(false);
    setExactDate(seedDate);
    setExactTime(seedTime);
    setExactEndTime(seedEnd);
    exactEndTouchedRef.current = false;
    setExactConflict(null);
    setExactValidating(false);
  }, [showAvailableSlots, availableSlots, preferredDate, exactPickerDurationMinutes]);

  // Auto-shift end time alongside start time so the visit length stays
  // constant unless the admin has explicitly adjusted the end. Without
  // this, dragging the start from 1:00 → 1:30 would silently shorten
  // the appointment by 30 minutes.
  useEffect(() => {
    if (exactEndTouchedRef.current) return;
    if (!exactTime) return;
    const nextEnd = addMinutesToTimeString(exactTime, exactPickerDurationMinutes);
    setExactEndTime((prev) => (prev === nextEnd ? prev : nextEnd));
  }, [exactTime, exactPickerDurationMinutes]);

  const clearExactTimeServerAlert = useCallback(() => {
    setBookingAlert((prev) =>
      prev && isExactTimeConstraintCode(prev.code) ? null : prev,
    );
  }, []);

  // Wrap the end-time setter so any UI change flips the "user touched"
  // flag, freezing future auto-shifts on start-time changes.
  const handleExactEndChange = useCallback((next: string) => {
    exactEndTouchedRef.current = true;
    if (next !== exactEndTime) clearExactTimeServerAlert();
    setExactEndTime(next);
  }, [exactEndTime, clearExactTimeServerAlert]);

  const handleExactStartChange = useCallback((next: string) => {
    if (next !== exactTime) clearExactTimeServerAlert();
    setExactTime(next);
  }, [exactTime, clearExactTimeServerAlert]);

  const handleExactDateChange = useCallback((next: string) => {
    if (next !== exactDate) clearExactTimeServerAlert();
    setExactDate(next);
  }, [exactDate, clearExactTimeServerAlert]);

  // Debounced overlap + drive-time check against the assignee's calendar.
  // Same calendar fetch the recommended-slots screen runs on the backend,
  // plus a geographic feasibility pass so a 10:55 AM finish across town
  // can't silently approve a 10:56 AM start.
  useEffect(() => {
    if (!showAvailableSlots || !exactExpanded) return;
    if (!exactPickerWindow || !exactPickerAssigneeId || !companyTimezone) {
      setExactConflict(null);
      setExactValidating(false);
      return;
    }

    const reqId = ++exactValidateRequestRef.current;
    setExactValidating(true);
    setExactConflict(null);

    const handle = setTimeout(async () => {
      try {
        const dayStartUtc = tenantLocalToUtcDate(exactDate, '00:00', companyTimezone);
        const dayEndUtc = tenantLocalToUtcDate(exactDate, '23:59', companyTimezone);

        // Fetch appointments (source of truth for Routemize bookings) alongside the
        // assignee's merged timeline, which also carries external/Google calendar events
        // with coordinates. Without the timeline, a Google event placed just after the
        // picked window would never surface a drive-time warning pre-submit — the exact
        // blind spot this ticket (86bbe9et4) fixes on the backend. The timeline call is
        // best-effort: if it fails we still validate against appointments.
        const [response, timeline] = await Promise.all([
          AppointmentService.postApiAppAppointmentSearch({
            maxResultCount: 1000,
            filter: {
              startDate: dayStartUtc.toISOString(),
              endDate: dayEndUtc.toISOString(),
              userId: exactPickerAssigneeId,
            },
          } as any),
          AppointmentService.getApiAppAppointmentGetUserTimeline(
            exactPickerAssigneeId,
            undefined, // startLocation
            undefined, // startLatitude
            undefined, // startLongitude
            exactDate, // date (YYYY-MM-DD)
          ).catch(() => null),
        ]);

        if (reqId !== exactValidateRequestRef.current) return;

        const items = (response?.items || []) as Routemize_DTOs_AppointmentListItem[];
        const pickedStartMs = exactPickerWindow.startUtc.getTime();
        const pickedEndMs = exactPickerWindow.endUtc.getTime();
        const pickedCoords = addressCoordinates;

        // Materialize blockers up-front so we can do an overlap pass and
        // then a neighbour-by-drive-time pass without re-iterating raw
        // DTOs at every comparison.
        const blockers = items
          .filter((it) => {
            if (!it.id) return false;
            const status = (it.status as any) ?? '';
            if (status === 'Cancelled' || status === 'NoShow') return false;
            return true;
          })
          .map((it) => {
            // Prefer the UTC field — `meetingStartTime` is the tenant-local
            // wall-clock string and would be misread as UTC by parseBackendDate,
            // throwing off the math by the tenant's offset.
            const start = parseBackendDate(
              (it.meetingStartTimeUtc ?? it.meetingStartTime) as string | undefined,
            );
            const end = parseBackendDate(
              (it.meetingEndTimeUtc ?? it.meetingEndTime) as string | undefined,
            );
            if (!start || !end) return null;
            const name =
              [it.contactFirstName, it.contactLastName].filter(Boolean).join(' ') ||
              it.eventName ||
              'an appointment';
            const coords =
              it.latitude != null && it.longitude != null
                ? { lat: it.latitude, lng: it.longitude }
                : null;
            return { id: it.id!, name, start, end, coords };
          })
          .filter((b): b is NonNullable<typeof b> => !!b);

        // Add external calendar events (Google + Outlook) as blockers so both the overlap pass
        // and the drive-time pass consider them. The timeline emits each external event as a
        // TimelineItem whose appointment.source === 'Google' | 'Outlook', carrying its own
        // coordinates when geocoded. Calendars with "Affects availability" OFF don't block.
        const externalBlockers = (timeline?.items ?? [])
          .map((tItem) => tItem.appointment)
          .filter(
            (a): a is NonNullable<typeof a> =>
              !!a &&
              (a.source === Routemize_Constants_AppointmentSource.GOOGLE ||
                a.source === Routemize_Constants_AppointmentSource.OUTLOOK) &&
              a.affectsAvailability !== false, // calendars with "Affects availability" OFF don't block exact-time picks
          )
          .map((a) => {
            const start = parseBackendDate(a.startTimeUtc ?? a.startTime);
            const end = parseBackendDate(a.endTimeUtc ?? a.endTime);
            if (!start || !end) return null;
            const coords =
              a.latitude != null && a.longitude != null
                ? { lat: a.latitude, lng: a.longitude }
                : null;
            return { id: a.id!, name: a.title || 'a calendar event', start, end, coords };
          })
          .filter((b): b is NonNullable<typeof b> => !!b);

        blockers.push(...externalBlockers);
        blockers.sort((a, b) => a.start.getTime() - b.start.getTime());

        // Pass 1: hard overlap. If the picked window literally intersects
        // an existing appointment we don't even need to think about drive
        // time — it's a flat-out conflict.
        for (const b of blockers) {
          if (pickedStartMs < b.end.getTime() && pickedEndMs > b.start.getTime()) {
            setExactConflict({
              kind: 'overlap',
              withName: b.name,
              withStartIso: b.start.toISOString(),
              withEndIso: b.end.toISOString(),
            });
            return;
          }
        }

        // Pass 2: drive-time feasibility. Find the latest appointment that
        // ends before the picked start and the earliest that starts after
        // the picked end, then make sure the assignee can physically get
        // there (and out) in time. Skipped when we don't have coordinates
        // for either side — without geometry the only honest answer is
        // "no opinion" and we fall back to the overlap check above.
        //
        // Drive times come from Google Maps so this check agrees with the
        // travel times the scheduling engine used to build the recommended
        // slots. When Maps can't answer we stay silent instead of guessing.
        if (pickedCoords) {
          let prev: typeof blockers[number] | null = null;
          let next: typeof blockers[number] | null = null;
          for (const b of blockers) {
            if (b.end.getTime() <= pickedStartMs) {
              if (!prev || b.end.getTime() > prev.end.getTime()) prev = b;
            } else if (b.start.getTime() >= pickedEndMs) {
              if (!next || b.start.getTime() < next.start.getTime()) next = b;
            }
          }

          if (prev?.coords) {
            const driveMin = await fetchDriveMinutes(prev.coords, pickedCoords);
            if (reqId !== exactValidateRequestRef.current) return;
            if (driveMin != null) {
              const earliestArrival = addMinutes(prev.end, driveMin);
              if (earliestArrival.getTime() > pickedStartMs) {
                setExactConflict({
                  kind: 'drive-time',
                  side: 'before',
                  withName: prev.name,
                  withStartIso: prev.start.toISOString(),
                  withEndIso: prev.end.toISOString(),
                  deficitMinutes: getTimeDifferenceInMinutes(
                    exactPickerWindow.startUtc,
                    earliestArrival,
                  ),
                });
                return;
              }
            }
          }

          if (next?.coords) {
            const driveMin = await fetchDriveMinutes(pickedCoords, next.coords);
            if (reqId !== exactValidateRequestRef.current) return;
            if (driveMin != null) {
              const latestDeparture = subtractMinutes(next.start, driveMin);
              if (pickedEndMs > latestDeparture.getTime()) {
                setExactConflict({
                  kind: 'drive-time',
                  side: 'after',
                  withName: next.name,
                  withStartIso: next.start.toISOString(),
                  withEndIso: next.end.toISOString(),
                  deficitMinutes: getTimeDifferenceInMinutes(
                    latestDeparture,
                    exactPickerWindow.endUtc,
                  ),
                });
                return;
              }
            }
          }
        }

        setExactConflict(null);
      } catch (err) {
        // Don't block on transient API failures — the backend create call
        // is the source of truth and will reject true overlaps server-side.
        console.warn('Failed to validate exact-time slot:', err);
        if (reqId === exactValidateRequestRef.current) setExactConflict(null);
      } finally {
        if (reqId === exactValidateRequestRef.current) setExactValidating(false);
      }
    }, 350);

    return () => {
      clearTimeout(handle);
    };
  }, [
    showAvailableSlots,
    exactExpanded,
    exactDate,
    exactPickerWindow,
    exactPickerAssigneeId,
    companyTimezone,
    addressCoordinates,
  ]);

  // Eligible team members for the availability cards: every assignable user on the booking form (86bcwvyqm).
  // In territories mode the customer's territory matches are listed first and the rest are flagged
  // "Outside territory". They are still bookable (the exact-time create only requires form membership), the
  // same as the Preferred provider dropdown's override. Hidden for single-user / non-smart forms.
  const teamAvailabilityEligibility = useMemo(
    () =>
      isSmartAssignmentForm
        ? resolveTeamAvailabilityEligibility(
            smartAssignmentUsers,
            isTerritoriesMode ? territoryRouting?.matchingUsers : null,
          )
        : { userIds: [] as string[], outsideTerritoryUserIds: new Set<string>() },
    [isSmartAssignmentForm, isTerritoriesMode, territoryRouting, smartAssignmentUsers],
  );
  const eligibleAvailabilityUserIds = teamAvailabilityEligibility.userIds;

  // Only worth showing the cards when there's a real choice to make (2+ eligible members).
  const showTeamAvailability = eligibleAvailabilityUserIds.length > 1;

  // Debounced fetch of per-member availability for the chosen window. Keyed on the window + the
  // eligible set; a request-id ref drops stale responses (mirrors exactValidateRequestRef).
  useEffect(() => {
    if (!showAvailableSlots || !exactExpanded || !showTeamAvailability || !exactPickerWindow) {
      setTeamAvailability([]);
      setTeamAvailabilityLoading(false);
      return;
    }

    const reqId = ++teamAvailabilityRequestRef.current;
    setTeamAvailabilityLoading(true);

    const handle = setTimeout(async () => {
      try {
        const members = await teamMemberAvailabilityManager.fetch({
          userIds: eligibleAvailabilityUserIds,
          startUtc: exactPickerWindow.startUtc,
          endUtc: exactPickerWindow.endUtc,
          bookingFormId: selectedForm?.id || undefined,
          latitude: addressCoordinates?.lat,
          longitude: addressCoordinates?.lng,
        });
        if (reqId !== teamAvailabilityRequestRef.current) return;
        setTeamAvailability(members);
      } catch (err) {
        console.warn('Failed to load team member availability:', err);
        if (reqId === teamAvailabilityRequestRef.current) setTeamAvailability([]);
      } finally {
        if (reqId === teamAvailabilityRequestRef.current) setTeamAvailabilityLoading(false);
      }
    }, 350);

    return () => {
      clearTimeout(handle);
    };
  }, [
    showAvailableSlots,
    exactExpanded,
    showTeamAvailability,
    exactPickerWindow,
    eligibleAvailabilityUserIds,
    selectedForm?.id,
    addressCoordinates,
  ]);

  // Selecting an availability card pins that member as the preferred provider (which flows into
  // exactPickerAssigneeId and re-runs the single-assignee validation for the chosen member) and
  // clears any exact-time server alert so the picker re-checks cleanly.
  const handleSelectTeamMember = useCallback((userId: string) => {
    userChangedPreferredAssignee.current = true;
    setPreferredAssigneeId(userId);
    setUserChangedPreferences(true);
    clearExactTimeServerAlert();
  }, [clearExactTimeServerAlert]);

  // Conflict label rendered in tenant-local time so the admin reads the
  // overlap in the customer's wall clock, not the admin's browser zone.
  const exactConflictLabel = useMemo(() => {
    if (!exactConflict) return null;
    const start = parseBackendDate(exactConflict.withStartIso);
    const end = parseBackendDate(exactConflict.withEndIso);
    if (!start || !end) return null;
    return `${formatDateTime(start, companyTimezone)} – ${formatDateTime(end, companyTimezone)}`;
  }, [exactConflict, companyTimezone]);

  const exactCanConfirm =
    !!exactPickerWindow &&
    !exactConflict &&
    !exactValidating &&
    !exactPickerIsInPast &&
    !exactPickerOutsideHours &&
    !exactAssigneeDayPending &&
    !isExactTimeConstraintCode(bookingAlert?.code);

  // True when the slot currently chosen for booking came from the
  // exact-time picker (vs. one of the recommended/alternative cards).
  // Used to badge the picker card so admins can tell at a glance which
  // path produced the sticky-footer time.
  const isExactTimeSlotSelected = !!selectedSlot?.id?.startsWith('exact-time-');
  const exactServerConstraint = isExactTimeConstraintCode(bookingAlert?.code);

  // Compute the actual duration of the currently-selected slot from its
  // start/end times. Falls back to the event type's configured duration
  // string only when the slot doesn't carry valid times. Without this,
  // the confirmation screen would always read the form's default
  // duration even after the admin extended/shortened the visit via the
  // exact-time picker.
  const selectedSlotDurationLabel = useMemo(() => {
    if (selectedSlot?.startTime && selectedSlot?.endTime) {
      const startMin = timeStringToMinutes(selectedSlot.startTime);
      const endMin = timeStringToMinutes(selectedSlot.endTime);
      if (startMin != null && endMin != null && endMin > startMin) {
        return formatDurationMinutes(endMin - startMin, { short: true });
      }
    }
    return eventType?.duration || '';
  }, [selectedSlot?.startTime, selectedSlot?.endTime, eventType?.duration]);

  // Who is actually going to show up. `slot.userDetail` is the richest source
  // but the backend leaves it off on some forms, and exact-time picker slots
  // are synthesized locally with nothing but `assignedUserId` — in both cases
  // we resolve the person out of the form's user list (which also carries the
  // photo) so the slot list and confirmation screen never degrade to a bare
  // "Unassigned". Solo forms with a single user resolve even with no id at all.
  const resolveSlotAssignee = useCallback((
    slot: Routemize_DTOs_SlotSuggestionDTO | null | undefined
  ): PreferredProviderOption | null => {
    if (!slot) return null;

    const formUsers = (eventType?.id ? bookingFormUsers[eventType.id] : undefined)
      ?? eventType?.assignedUsers
      ?? [];
    const detail = slot.userDetail;
    const assigneeId = slot.assignedUserId || detail?.id || '';
    const fromForm = assigneeId
      ? formUsers.find(u => u.id === assigneeId)
      : (formUsers.length === 1 ? formUsers[0] : undefined);

    const resolved: PreferredProviderOption = {
      id: assigneeId || fromForm?.id || '',
      name: detail?.name || fromForm?.name || null,
      firstName: detail?.firstName || fromForm?.firstName || null,
      lastName: detail?.lastName || fromForm?.lastName || null,
      email: detail?.email || fromForm?.email || null,
      photoUrl: fromForm?.photoUrl || detail?.photoUrl || null,
      color: assigneeColorById.get(assigneeId || fromForm?.id || ''),
    };

    const hasIdentity = Boolean(
      resolved.name || resolved.firstName || resolved.lastName || resolved.email
    );
    return hasIdentity ? resolved : null;
  }, [eventType?.id, eventType?.assignedUsers, bookingFormUsers, assigneeColorById]);

  const selectedSlotAssignee = useMemo(
    () => resolveSlotAssignee(selectedSlot),
    [resolveSlotAssignee, selectedSlot]
  );

  // ---------------------------------------------------------------------
  // "Don't see the time you need?" — hand the question to Routy.
  //
  // The engine only ever shows what IS bookable, so a day it silently skips
  // (a weekday switched off, a synced calendar event nobody remembers making)
  // reads as the product being wrong. Rather than teaching every operator how
  // instant booking interacts with a connected calendar, we collect the day
  // they expected and let Routy audit it — and clear the junk blocks.
  // ---------------------------------------------------------------------
  const [showAvailabilityHelp, setShowAvailabilityHelp] = useState(false);

  /**
   * Everyone this service can be assigned to. On a smart-assignment /
   * round-robin form the engine may have skipped the requested day for only one
   * of them, so auditing "the assignee" (whoever happened to win the first
   * slot) answers the wrong question — the operator picks whose calendar to
   * open up instead.
   */
  const availabilityHelpAssignees = useMemo<PreferredProviderOption[]>(() => {
    const formUsers = (eventType?.id ? bookingFormUsers[eventType.id] : undefined)
      ?? eventType?.assignedUsers
      ?? [];
    return formUsers
      .filter(u => !!u.id)
      .map(u => ({
        id: u.id as string,
        name: u.name ?? null,
        firstName: u.firstName ?? null,
        lastName: u.lastName ?? null,
        email: u.email ?? null,
        photoUrl: u.photoUrl ?? null,
        color: assigneeColorById.get(u.id as string),
      }));
  }, [eventType?.id, eventType?.assignedUsers, bookingFormUsers, assigneeColorById]);

  // Display name for the exact-time assignee, so the outside-hours warning
  // names whose hours it checked ("Outside Taylor's hours").
  const exactPickerAssigneeName = useMemo(() => {
    if (!exactPickerAssigneeId) return null;
    const fromSlot = resolveSlotAssignee(availableSlots[0]);
    const person = availabilityHelpAssignees.find(u => u.id === exactPickerAssigneeId)
      ?? (fromSlot?.id === exactPickerAssigneeId ? fromSlot : null);
    if (!person) return null;
    return person.firstName || person.name || person.email || null;
  }, [exactPickerAssigneeId, availabilityHelpAssignees, resolveSlotAssignee, availableSlots]);

  /** The soonest day the engine actually offered, for contrast in the hand-off. */
  const earliestOfferedSlotDate = useMemo(() => {
    const keys = availableSlots
      .map(slot => (slot.suggestedDate ? String(slot.suggestedDate).split('T')[0] : ''))
      .filter(Boolean)
      .sort();
    return keys[0] || '';
  }, [availableSlots]);

  const earliestOfferedSlotLabel = useMemo(() => {
    const parsed = earliestOfferedSlotDate ? parseLocalDate(earliestOfferedSlotDate) : null;
    return parsed ? formatDateMedium(parsed) : '';
  }, [earliestOfferedSlotDate]);

  const handleAvailabilityHelpSubmit = useCallback((choice: AvailabilityHelpChoice) => {
    const customerName = [firstName, lastName].filter(Boolean).join(' ').trim();
    // An explicit pick always wins; otherwise fall back to whoever the engine
    // put on the first slot, which is the only assignee we can infer.
    const picked = choice.assignedUserId
      ? availabilityHelpAssignees.find(u => u.id === choice.assignedUserId)
      : null;
    const assignee = picked ?? resolveSlotAssignee(availableSlots[0]);
    const assigneeName = assignee ? formatProviderName(assignee) : '';
    const requestId = existingRequestId || createdRequestId || '';

    // Everything the audit needs to run without a follow-up question: whose
    // calendar, which day, and what the engine offered instead.
    const message = [
      `I'm trying to book ${customerName || 'a customer'}`,
      requestId ? ` (a pending request, requestId ${requestId})` : '',
      eventType?.name ? ` for a ${eventType.name}` : '',
      ` on ${choice.date}`,
      choice.timeLabel ? ` at ${choice.timeLabel}` : '',
      '. ',
      earliestOfferedSlotDate
        ? `The earliest the booking system offers is ${earliestOfferedSlotDate}. `
        : 'The booking system is not offering any times. ',
      assignee?.id ? `It would be assigned to ${assigneeName} (assignedUserId ${assignee.id}). ` : '',
      // The form id is what lets the audit measure openings against the real
      // visit length and buffers. Without it the card can only report gap
      // lengths, which is the version of this answer that explains nothing.
      eventType?.id ? `The service is bookingFormId ${eventType.id}. ` : '',
      'Run audit_availability on that day (pass the bookingFormId) and show me exactly what is blocking it.',
    ].join('');

    // What the operator actually sees. The message above is full of ids and
    // tool names it needs to do the job — none of which belongs in their chat.
    const display = [
      `Why can't I book ${customerName || 'this customer'}`,
      eventType?.name ? ` for a ${eventType.name}` : '',
      ` on ${choice.date}`,
      choice.timeLabel ? ` at ${choice.timeLabel}` : '',
      '?',
      earliestOfferedSlotDate ? ` The earliest it offers is ${earliestOfferedSlotDate}.` : '',
    ].join('');

    setShowAvailabilityHelp(false);
    askRouty(message, display);
    // Hand the whole flow over to Routy: the chat panel floats above this
    // modal, and leaving a stale slot picker behind it just competes for the
    // operator's attention while they work the problem.
    void handleClose();
    // handleClose is re-created every render (plain function), so it stays out
    // of the dep list — it only ever tears this modal down.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    firstName,
    lastName,
    resolveSlotAssignee,
    availableSlots,
    existingRequestId,
    createdRequestId,
    eventType?.name,
    eventType?.id,
    earliestOfferedSlotDate,
    availabilityHelpAssignees,
    askRouty,
  ]);

  const handleConfirmExactTime = () => {
    if (!exactCanConfirm || !exactPickerWindow) return;
    // Synthesize a SlotSuggestion so the existing handleSlotSelect → sticky
    // footer → Continue → confirmation pipeline can consume it. Only the
    // fields actually read downstream are populated; everything else is
    // left undefined so we don't accidentally surface fake route metrics.
    const synthetic: Routemize_DTOs_SlotSuggestionDTO = {
      id: `exact-time-${exactDate}-${exactTime}`,
      suggestedDate: exactDate,
      startTime: exactTime,
      endTime: exactPickerWindow.endTimeStr,
      assignedUserId: exactPickerAssigneeId || undefined,
      isRecommended: false,
    };
    handleSlotSelect(synthetic);
  };

  const handleRefetchSlots = async () => {
    if (!createdRequestId) return;

    try {
      setIsRoutemizing(true);
      setErrors({});
      setNoAvailabilityInfo(null);

      // First, update the request with the new preferences and ensure scheduleMethod is ROUTEMIZED
      // Get the selected time range option to extract start/end times.
      // Resolve code/text/alias so an unnormalized value still maps to a real
      // option instead of silently nulling the start/end companion fields.
      const selectedTimeOptionForRefetch = resolveSelectedTimeOption(preferredTime);

      // The refetch is a ROUTEMIZED update that requires a resolvable preferred
      // time. Block and return the operator to the preferences step rather than
      // sending a null time and hitting "Preferred time is required".
      if (!selectedTimeOptionForRefetch) {
        setErrors({ preferredTime: 'Please select a preferred time' });
        setCurrentStep('preferences');
        showError('Please select a preferred time before finding available slots.');
        setIsRoutemizing(false);
        return;
      }

      const refetchPayload: Routemize_DTOs_UpdateRequestsDTO = {
        id: createdRequestId,
        // NOTE: Do NOT change status here - the request should remain in its current state
        // (Scheduling/Incomplete or Active) until an appointment is actually booked.
        // Status only changes to History when the appointment is created.
        preferredDate: preferredDate || undefined,
        preferredSecondaryDate: preferredSecondaryDate || undefined,
        preferredTimeRange: preferredTime || undefined,
        preferredStartTime: selectedTimeOptionForRefetch?.start || undefined,
        preferredEndTime: selectedTimeOptionForRefetch?.end || undefined,
        scheduleMethod: Routemize_Constants_ScheduleMethod.ROUTEMIZED,
        contactAddressId: createdContactAddressId || undefined,
        // Include address details so backend can create/update the address
        addressLine1: address || undefined,
        addressLine2: address2 || undefined,
        city: city || undefined,
        state: state || undefined,
        zipCode: zipCode || undefined
      };
      // Same DataGatheringPhase guard as the initial Routemize path:
      // a refetch without either a saved address ID or full local address
      // fields is guaranteed to come back empty. Fail loudly instead.
      if ((!createdContactAddressId || createdContactAddressId === '00000000-0000-0000-0000-000000000000')
          && (!address || !city || !state || !zipCode)) {
        // The submitted address may have been mapped to the request just after
        // our one-time load captured empty state (multi-step booking flow,
        // ClickUp 86bc0dn3v). Re-read once before failing and carry the refreshed
        // values into the PUT below (React state setters have not flushed yet).
        const recovered = createdRequestId
          ? await refreshServiceAddressFromServer(createdRequestId)
          : { ok: false, fields: {} as RequestAddressFields, contactAddressId: undefined };
        if (!recovered.ok) {
          showError('A complete service address is required before generating slots. Please add an address and try again.');
          setIsRoutemizing(false);
          return;
        }
        if (recovered.contactAddressId) refetchPayload.contactAddressId = recovered.contactAddressId;
        if (recovered.fields.address1) refetchPayload.addressLine1 = recovered.fields.address1;
        if (recovered.fields.address2) refetchPayload.addressLine2 = recovered.fields.address2;
        if (recovered.fields.city) refetchPayload.city = recovered.fields.city;
        if (recovered.fields.state) refetchPayload.state = recovered.fields.state;
        if (recovered.fields.zipCode) refetchPayload.zipCode = recovered.fields.zipCode;
      }

      await RequestsService.putApiAppRequestsUpdate(refetchPayload);

      // Then get available slots via the helper (forwards PreferredUserId
      // for smart-assignment manual overrides).
      const schedulingResponse: Routemize_DTOs_SchedulingResponse = await fetchSlotsWithPreferredUser(
        createdRequestId,
        { preferredUserId: isSmartAssignmentForm ? (preferredAssigneeId || undefined) : undefined }
      );

      // Outside-service-area handling — see note in handleRoutemizeBooking.
      // If the refetched preferences land outside the service area, re-route
      // to the override prompt instead of silently showing slots; the operator
      // must explicitly click "Book Anyway (Override)" to bypass.
      if (schedulingResponse.travelDistanceExceededAction) {
        setIsOutsideServiceArea(true);
        setOutsideServiceAreaDetail(extractOutsideServiceAreaDetail(schedulingResponse) || null);
        void persistOutsideServiceAreaFlag(createdRequestId);
        const formUsersForOutsideRefetch = (eventType?.id ? bookingFormUsers[eventType.id] : []) || [];
        showNoAvailabilityState(schedulingResponse, formUsersForOutsideRefetch);
        return;
      } else {
        setIsOutsideServiceArea(false);
        setOutsideServiceAreaDetail(null);
      }

      const slots = schedulingResponse.slots;

      if (slots && slots.length > 0) {
        setNoAvailabilityInfo(null);
        setAvailableSlots(slots);

        if (slots[0]) {
          setSelectedSlot(slots[0]);
        }

        setOriginalPreferences({
          date: preferredDate,
          secondaryDate: preferredSecondaryDate,
          time: preferredTime
        });

        showInfo('Updated slot recommendations based on your new preferences');
      } else {
        const formUsersForRefetch = (eventType?.id ? bookingFormUsers[eventType.id] : []) || [];
        showNoAvailabilityState(schedulingResponse, formUsersForRefetch);
      }
    } catch (error: any) {
      console.error('Failed to refetch slots:', error);
      showError('Failed to update slot recommendations');
    } finally {
      setIsRoutemizing(false);
    }
  };

  // Override the service-area block when staff explicitly chooses to book
  // anyway. Re-runs slot generation with `bypassServiceArea=true` so the
  // backend ignores the configured radius and returns slots like a normal
  // booking. We intentionally keep `isOutsideServiceArea` true (and re-persist
  // the flag) so the appointment/request remains tagged as out-of-area on the
  // dashboard for reporting — the override is about *unblocking* the booking
  // flow, not about pretending the location is in-area.
  const handleBookAnyway = async () => {
    const activeRequestId = existingRequestId || createdRequestId;
    if (!activeRequestId) {
      showError('No request to book against. Please go back and try again.');
      return;
    }

    let keepRoutemizingForAnimation = false;
    try {
      setIsRoutemizing(true);
      setErrors({});
      setNoAvailabilityInfo(null);
      // Tear down the override-prompt UI so the loading spinner can render.
      // The spinner's gate is `isRoutemizing && !showAvailableSlots`; because
      // the prompt was reached via `showNoAvailabilityState`, `showAvailableSlots`
      // is still true and the spinner would otherwise stay hidden during the
      // bypass re-fetch. Also clear any stale slots/selection from a prior pass.
      setShowAvailableSlots(false);
      setAvailableSlots([]);
      setSelectedSlot(null);
      // Clear any stale staged slots so the Slot Discovery animation
      // restarts from phase 1 rather than jumping into the counter reveal.
      setPendingSlotsData(null);

      // Resolve ContactAddressId for the bypass fetch. The initial Routemize
      // pass may have backfilled the address on the server via inline PUT
      // fields while local React state still holds a null/empty GUID — mirror
      // the same resolution + guard used by handleRefetchSlots.
      let contactAddressIdForOverride = isValidEntityId(createdContactAddressId)
        ? createdContactAddressId
        : (isValidEntityId(existingRequestForContact?.contactAddressId)
          ? existingRequestForContact.contactAddressId
          : null);

      if (!contactAddressIdForOverride && createdContactId && address && city && state && zipCode) {
        const addressId = await findOrCreateContactAddress({
          contactId: createdContactId,
          addressLine1: address,
          addressLine2: address2,
          city,
          state,
          zipCode,
          country,
        });
        if (addressId) {
          contactAddressIdForOverride = addressId;
          setCreatedContactAddressId(addressId);
        }
      }

      let recoveredOverrideFields: RequestAddressFields | undefined;
      if (!isValidEntityId(contactAddressIdForOverride) && (!address || !city || !state || !zipCode)) {
        // The submitted address may have been mapped to the request just after
        // our one-time load captured empty state (multi-step booking flow,
        // ClickUp 86bc0dn3v). Re-read once before failing and adopt the freshly-
        // mapped address for the sync PUT below (state setters have not flushed).
        const recovered = activeRequestId
          ? await refreshServiceAddressFromServer(activeRequestId)
          : { ok: false, fields: {} as RequestAddressFields, contactAddressId: undefined };
        if (!recovered.ok) {
          showError('A complete service address is required before generating slots. Please add an address and try again.');
          setIsRoutemizing(false);
          return;
        }
        if (recovered.contactAddressId) {
          contactAddressIdForOverride = recovered.contactAddressId;
        }
        recoveredOverrideFields = recovered.fields;
      }

      // Keep the request row in sync before bypassing the service-area check.
      await RequestsService.putApiAppRequestsUpdate({
        id: activeRequestId,
        contactAddressId: contactAddressIdForOverride || undefined,
        addressLine1: address || recoveredOverrideFields?.address1 || undefined,
        addressLine2: address2 || recoveredOverrideFields?.address2 || undefined,
        city: city || recoveredOverrideFields?.city || undefined,
        state: state || recoveredOverrideFields?.state || undefined,
        zipCode: zipCode || recoveredOverrideFields?.zipCode || undefined,
      });

      const schedulingResponse = await fetchSlotsWithPreferredUser(activeRequestId, {
        preferredUserId: isSmartAssignmentForm ? (preferredAssigneeId || undefined) : undefined,
        bypassServiceArea: true,
      });

      // Keep the out-of-area flag visible/persisted regardless — the operator
      // is overriding the *block*, not the fact that this address is outside
      // their radius.
      setIsOutsideServiceArea(true);
      const detail = extractOutsideServiceAreaDetail(schedulingResponse);
      if (detail) setOutsideServiceAreaDetail(detail);
      void persistOutsideServiceAreaFlag(activeRequestId);

      const slots = schedulingResponse.slots || [];
      const formUsersForOverride = (eventType?.id ? bookingFormUsers[eventType.id] : []) || [];

      if (slots.length > 0) {
        // Same staging-for-animation pattern as the regular success paths.
        // The "Service area override applied" toast is deferred until the
        // animation completes so it lands together with the slot reveal.
        keepRoutemizingForAnimation = true;
        setPendingSlotsData({
          slots,
          preferences: {
            date: preferredDate,
            secondaryDate: preferredSecondaryDate,
            time: preferredTime,
          },
          toastInfo: 'Service area override applied — showing available slots',
          afterServiceAreaOverride: true,
          schedulingResponse,
        });
      } else {
        // Even with bypass the backend returned nothing (e.g. assigned user
        // genuinely has no open time). Show a dedicated empty state so the
        // operator knows the override was applied but nothing is bookable.
        showNoAvailabilityState(schedulingResponse, formUsersForOverride, {
          afterServiceAreaOverride: true,
        });
      }
    } catch (error: any) {
      console.error('❌ Failed to override service area:', error);
      const message = error?.body?.error?.message || error?.message || 'Failed to override service area. Please try again.';
      setErrors({ general: message });
      showError(message);
      setNoAvailabilityInfo({
        isFull: true,
        dateLabel: customerPreferredDateLabel,
        timeLabel: customerPreferredTimeLabel,
        detail: message,
        afterServiceAreaOverride: true,
      });
      setAvailableSlots([]);
      setSelectedSlot(null);
      setShowAvailableSlots(true);
      setCurrentStep('booking');
      setCompletedSteps((prev) => [
        ...prev.filter((s) => s !== 'preferences'),
        'preferences',
      ]);
    } finally {
      // Keep the Slot Discovery animation mounted until `applyPendingSlots`
      // runs — clearing isRoutemizing here was leaving a blank "Choose Time
      // Slot" step when slots were staged for the counter reveal.
      if (!keepRoutemizingForAnimation) {
        setIsRoutemizing(false);
      }
    }
  };

  const parseValidationErrorMessage = (error: any): string | null => {
    const validationErrors = error?.body?.error?.validationErrors;
    if (validationErrors && Array.isArray(validationErrors)) {
      const errorDetails = validationErrors
        .map((err: any) => {
          if (typeof err === 'object' && err.message) return err.message;
          if (typeof err === 'object' && err.members && err.members.length > 0) {
            return `${err.members.join(', ')}: ${err.message || 'Validation failed'}`;
          }
          return JSON.stringify(err);
        })
        .join('; ');
      return errorDetails || 'Validation errors occurred';
    }
    if (validationErrors && typeof validationErrors === 'object') {
      return Object.entries(validationErrors)
        .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
        .join('; ');
    }
    return null;
  };

  /**
   * Turn an API failure into the inline booking callout. Exact-time constraint
   * codes bounce the operator back to the picker so the warning sits next to
   * the time they just chose, instead of a raw code in the modal footer.
   */
  const surfaceBookingFailure = (
    error: any,
    fallback: string,
    options?: { bounceToExactPicker?: boolean },
  ) => {
    const creditCode = error?.error?.code ?? error?.body?.error?.code;
    if (creditCode === 'Credits:InsufficientBalance') {
      const msg =
        error?.error?.data?.Message ||
        error?.body?.error?.data?.Message ||
        'Insufficient credits to create appointment. Please contact your administrator to purchase more credits.';
      const alert: SchedulingAlert = {
        title: 'Insufficient credits',
        message: msg,
        tone: 'danger',
        code: creditCode,
        blocksContinue: false,
      };
      setBookingAlert(alert);
      setErrors({ general: msg });
      showError(msg);
      return;
    }

    const alert = resolveSchedulingAlert(error, parseValidationErrorMessage(error) || fallback);
    setBookingAlert(alert);
    setErrors({ general: alert.message });

    const bounce =
      options?.bounceToExactPicker === true ||
      (options?.bounceToExactPicker !== false &&
        isExactTimeConstraintCode(alert.code) &&
        alert.blocksContinue);

    if (bounce) {
      setCurrentStep('booking');
      setShowAvailableSlots(true);
      setExactExpanded(true);
    } else {
      showError(alert.message);
    }
  };

  // Manual booking flow - create appointment directly at specified time/user
  const handleManualBookingCreate = async () => {
    if (isCreatingRef.current) return;
    isCreatingRef.current = true;
    try {
      setIsSubmitting(true);
      showInfo('Creating appointment...');

      // Validate we have all required data
      if (!createdContactId) {
        throw new Error('Contact ID not available. Please go back to customer details.');
      }
      if (!eventType?.id) {
        throw new Error('Booking form not selected. Please go back to event type selection.');
      }
      if (!assignedUserId) {
        throw new Error('Assigned user not specified.');
      }
      if (!prefilledDate || !prefilledStartTime || !prefilledEndTime) {
        throw new Error('Time slot not specified.');
      }

      // Ensure we have a contact address ID
      let finalContactAddressId = createdContactAddressId;

      if (!finalContactAddressId && address && city && state && zipCode) {
        const addressId = await findOrCreateContactAddress({
          contactId: createdContactId,
          addressLine1: address,
          addressLine2: address2,
          city,
          state,
          zipCode,
          country
        });
        if (addressId) {
          finalContactAddressId = addressId;
          setCreatedContactAddressId(finalContactAddressId);
        }
      }

      // Manual booking skips GetSlots but still creates a Request +
      // Appointment. Persisting either with an empty ContactAddressId
      // breaks downstream routing / address-aware features. Refuse.
      if (!finalContactAddressId || finalContactAddressId === '00000000-0000-0000-0000-000000000000') {
        throw new Error('A complete service address is required before creating an appointment. Please add an address and try again.');
      }

      // For manual booking, use the preselected user or get from booking form users
      let manualFormUsers = bookingFormUsers[eventType.id] || [];
      if (manualFormUsers.length === 0 && !assignedUserId) {
        manualFormUsers = await loadBookingFormDetails(eventType.id);
      }

      const finalAssignedUserId = assignedUserId ||
        (manualFormUsers.length > 0 ? manualFormUsers[0].id : undefined);

      if (!finalAssignedUserId) {
        console.error('No team members found for manual booking:', {
          eventTypeId: eventType.id,
          bookingFormUsers,
          assignedUserId
        });
        throw new Error('No team members assigned to this booking form. Please contact support.');
      }

      // Create request with Manual schedule method
      const requestData: Routemize_DTOs_CreateRequestsDTO = {
        contactId: createdContactId,
        bookingFormId: eventType.id,
        contactAddressId: finalContactAddressId || undefined,
        preferredDate: prefilledDate,
        ...(preferredSecondaryDate && preferredSecondaryDate.trim() !== '' ? { preferredSecondaryDate } : {}),
        preferredStartTime: prefilledStartTime,
        preferredEndTime: prefilledEndTime,
        preferredTimeRange: `${prefilledStartTime} - ${prefilledEndTime}`,
        assignedUserId: finalAssignedUserId,
        scheduleMethod: Routemize_Constants_ScheduleMethod.MANUAL,
        status: Routemize_Constants_RequestStatus.SCHEDULING,
        leadSource: effectiveLeadSource || null,
        origin: Routemize_Constants_RequestOrigin.INTERNAL
      };

      const requestResponse = await RequestsService.postApiAppRequestsCreate(requestData);

      if (!requestResponse.id) {
        throw new Error('Failed to create request - no ID returned');
      }

      // Track the just-created request so cleanupOrphanedRequest (keyed on
      // createdRequestId) can remove it if appointment creation below fails,
      // instead of orphaning a Scheduling request on every failed manual retry.
      setCreatedRequestId(requestResponse.id);

      // Persist custom answers via the public form's DTO/endpoint so the
      // appointment detail view can render them in "Form Responses".
      await persistAnswersToRequest(requestResponse.id);

      // For manual bookings, we need to create the appointment directly
      // Since we don't have a slot suggestion from Routemize, we'll create the appointment
      // using the request and relying on the backend to handle the manual scheduling
      const appointmentData: Routemize_DTOs_CreateAppointmentDTO = {
        requestId: requestResponse.id,
        slotSuggestionId: null, // No slot suggestion for manual bookings
        sendSmsConfirmation: sendSmsConfirmation,
        sendEmailConfirmation: sendEmailConfirmation,
        sendSmsReminders: sendSmsReminders,
        sendEmailReminders: sendEmailReminders,
        accessCode: accessCode?.trim() || null,
        // Staff "Appointment Notes" — saved by the backend as an "Internal Note" EntityNote in the same transaction.
        internalNote: notes.trim() || null,
      };

      // The appointment is created against the just-created request
      // (requestResponse.id above), so the assignedUserId PUT and the
      // status→History update below must target THAT request — not a previously
      // discovered existingRequestId/createdRequestId, which would leave the new
      // request unassigned/stuck and mutate the wrong row on retry.
      const activeRequestId = requestResponse.id;
      if (activeRequestId && selectedSlot?.assignedUserId) {
        try {
          await RequestsService.putApiAppRequestsUpdate({
            id: activeRequestId,
            assignedUserId: selectedSlot.assignedUserId
          });
        } catch (error: any) {
          console.error('❌ Failed to assign user to request:', error);
          console.error('Error details:', {
            message: error?.message,
            body: error?.body,
            status: error?.status,
            errorData: error?.body?.error
          });
          throw new Error('Failed to assign team member to request. Please try again.');
        }
      }

      const appointmentResponse = await AppointmentService.postApiAppAppointmentCreate(appointmentData);

      // Mark appointment as successfully created
      setAppointmentCreated(true);

      // Update request status to History if this was from an existing request
      if (activeRequestId) {
        try {
          // Check current request status first
          const currentRequest = await RequestsService.getApiAppRequestsGetById(activeRequestId);

          // Only update if not already in History status
          if (currentRequest.status !== Routemize_Constants_RequestStatus.HISTORY) {
            await RequestsService.putApiAppRequestsUpdate({
              id: activeRequestId,
              status: Routemize_Constants_RequestStatus.HISTORY
            });
          }
        } catch (error: any) {
          console.error('❌ Failed to update request status:', error);
          // Don't fail the whole operation if status update fails
          // The appointment was created successfully, status update is a secondary concern
        }
      }

      // Call onSave with the created appointment
      onSave(appointmentResponse);

      // Show success toast (only one toast, removed duplicate from RequestDetailsPanel)
      showSuccess('Appointment scheduled successfully!');

      // Reset form and close modal
      resetForm();
      onClose();

    } catch (error: any) {
      console.error('Failed to create manual booking:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));

      // The request was already converted to an appointment (duplicate/retried submit).
      // Nothing left to fix here — surface the message and close, don't ask to pick another time.
      if (isRequestAlreadyBookedError(error)) {
        showError(resolveSchedulingErrorMessage(error, 'This booking has already been scheduled.'));
        resetForm();
        onClose();
        return;
      }

      surfaceBookingFailure(error, 'Failed to create booking. Please try again.');
    } finally {
      setIsSubmitting(false);
      isCreatingRef.current = false;
    }
  };

  const handleCreateAppointment = async () => {
    const activeRequestId = existingRequestId || createdRequestId;
    if (!selectedSlot || !activeRequestId) {
      throw new Error('Missing slot or request information');
    }

    // Update request with assigned user before creating appointment (required by backend).
    // NOTE: We intentionally do NOT write `notes` here. Staff notes live as an
    // EntityNote on the appointment, sent in the create call as
    // `CreateAppointmentDTO.internalNote` (see below). Writing them to `request.notes`
    // as well caused them to render twice in the appointment detail view —
    // once via `appointment.notes` (adminNotes) and once via the EntityNote
    // (internalNotes).
    if (selectedSlot.assignedUserId) {
      try {
        const updatePayload: any = {
          id: activeRequestId,
          assignedUserId: selectedSlot.assignedUserId,
        };
        await RequestsService.putApiAppRequestsUpdate(updatePayload);
      } catch (error: any) {
        console.error('❌ Failed to update request:', error);
        console.error('Error details:', {
          message: error?.message,
          body: error?.body,
          status: error?.status,
          errorData: error?.body?.error
        });
        throw new Error('Failed to update request. Please try again.');
      }
    }

    // Re-persist any final answer edits the user may have made on the
    // confirmation screen before the appointment is created. Mirrors the
    // public flow's "save service details before confirm" sequencing.
    await persistAnswersToRequest(activeRequestId);

    const isManualOverride = !!selectedSlot.id?.startsWith('exact-time-');

    const appointmentData: Routemize_DTOs_CreateAppointmentDTO = {
      requestId: activeRequestId,
      slotSuggestionId: isManualOverride ? null : (selectedSlot.id || null),
      sendSmsConfirmation: sendSmsConfirmation,
      sendEmailConfirmation: sendEmailConfirmation,
      sendSmsReminders: sendSmsReminders,
      sendEmailReminders: sendEmailReminders,
      accessCode: accessCode?.trim() || null,
      // Staff "Appointment Notes": the backend saves it as an "Internal Note" EntityNote in the
      // create's transaction, so it is on the AppointmentCreated / BookingRequestStatusChanged webhooks.
      internalNote: notes.trim() || null,
      bookingFormId: eventType?.id || undefined,
      ...(isManualOverride
        ? {
            manualStartTimeUtc: exactPickerWindow?.startUtc.toISOString(),
            manualEndTimeUtc: exactPickerWindow?.endUtc.toISOString(),
            manualAssignedUserId: exactPickerAssigneeId || undefined,
          }
        : {}),
    };

    const appointmentResponse = await AppointmentService.postApiAppAppointmentCreate(appointmentData);

    // Update request status to History if this was from an existing request
    if (activeRequestId) {
      try {
        // Check current request status first
        const currentRequest = await RequestsService.getApiAppRequestsGetById(activeRequestId);

        // Only update if not already in History status
        if (currentRequest.status !== Routemize_Constants_RequestStatus.HISTORY) {
          await RequestsService.putApiAppRequestsUpdate({
            id: activeRequestId,
            status: Routemize_Constants_RequestStatus.HISTORY
          });
        }
      } catch (error: any) {
        console.error('❌ Failed to update request status:', error);
        // Don't fail the whole operation if status update fails
        // The appointment was created successfully, status update is a secondary concern
      }
    }

    // Call onSave with the created appointment
    onSave(appointmentResponse);

    // Show success toast (only one toast, removed duplicate from RequestDetailsPanel)
    showSuccess('Appointment scheduled successfully!');

    // Reset form and close modal
    resetForm();
    onClose();
  };

  const handleNextStep = async () => {
    if (currentStep === 'customer') {
      if (validateCustomerForm()) {
        // Create or update contact if needed
        let contactId = createdContactId;
        
        if (!contactId) {
          // Create new contact with address included (API requires at least 1 address)
          const contactData: Routemize_DTOs_CreateContactDTO = {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim(),
            phoneNumber: phone.trim(),
            businessName: isBusiness && businessName ? businessName.trim() : null,
            leadSource: effectiveLeadSource || null,
            isActive: true,
            addresses: address && city && state && zipCode ? [{
              addressLine1: address,
              addressLine2: address2 || null,
              city: city,
              state: state,
              zipCode: zipCode,
              isActive: true
            } as any] : []
          };
          
          try {
            const contactResponse = await ContactService.postApiAppContactCreate(contactData);
            contactId = contactResponse.id!;
            setCreatedContactId(contactId);

            // Extract the created address ID from the response
            if (contactResponse.addresses && contactResponse.addresses.length > 0) {
              setCreatedContactAddressId(contactResponse.addresses[0].id || null);
            }
          } catch (error: any) {
            console.error('Failed to create contact:', error);
            console.error('Full error object:', JSON.stringify(error, null, 2));
            console.error('Error body:', error?.body);
            console.error('Error body error:', error?.body?.error);
            console.error('Validation errors:', error?.body?.error?.validationErrors);
            
            // Extract more detailed error message
            let errorMessage = 'Failed to create contact. Please try again.';
            
            // Handle validation errors array format
            if (error?.body?.error?.validationErrors && Array.isArray(error.body.error.validationErrors)) {
              const validationErrors = error.body.error.validationErrors;
              const errorDetails = validationErrors
                .map((err: any) => {
                  if (typeof err === 'object' && err.message) {
                    return err.message;
                  } else if (typeof err === 'object' && err.members && err.members.length > 0) {
                    return `${err.members.join(', ')}: ${err.message || 'Validation failed'}`;
                  }
                  return JSON.stringify(err);
                })
                .join('; ');
              errorMessage = errorDetails || 'Validation errors occurred';
            } 
            // Handle validation errors object format
            else if (error?.body?.error?.validationErrors && typeof error.body.error.validationErrors === 'object') {
              const validationErrors = error.body.error.validationErrors;
              const errorDetails = Object.entries(validationErrors)
                .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                .join('; ');
              errorMessage = errorDetails;
            } else if (error?.body?.error?.message) {
              errorMessage = error.body.error.message;
            } else if (error?.message) {
              errorMessage = error.message;
            }
            
            setErrors({ general: errorMessage });
            showError(errorMessage);
            return;
          }
        } else {
          // Existing contact selected — persist any edits made during the booking flow
          const contactInfo = selectedContact?.extraProperties?.contactInfo;
          const normalizedPhone = phone.replace(/\D/g, '');
          const originalPhone = (contactInfo?.phoneNumber || '').replace(/\D/g, '');
          const trimmedBusinessName = isBusiness && businessName ? businessName.trim() : null;
          const originalBusinessName = contactInfo?.businessName?.trim() || null;
          const hasContactChanges =
            firstName.trim() !== (contactInfo?.firstName || '').trim() ||
            lastName.trim() !== (contactInfo?.lastName || '').trim() ||
            email.trim() !== (contactInfo?.email || '').trim() ||
            normalizedPhone !== originalPhone ||
            trimmedBusinessName !== originalBusinessName ||
            (effectiveLeadSource || null) !== (contactInfo?.leadSource || null);

          if (hasContactChanges) {
            try {
              await ContactService.putApiAppContactUpdate({
                id: contactId,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim(),
                phoneNumber: normalizedPhone || phone.trim(),
                businessName: trimmedBusinessName,
                leadSource: effectiveLeadSource || null,
              });
            } catch (error: any) {
              console.error('Failed to update contact:', error);

              let errorMessage = 'Failed to update contact. Please try again.';

              if (error?.body?.error?.validationErrors && Array.isArray(error.body.error.validationErrors)) {
                const validationErrors = error.body.error.validationErrors;
                errorMessage = validationErrors
                  .map((err: any) => {
                    if (typeof err === 'object' && err.message) {
                      return err.message;
                    } else if (typeof err === 'object' && err.members && err.members.length > 0) {
                      return `${err.members.join(', ')}: ${err.message || 'Validation failed'}`;
                    }
                    return JSON.stringify(err);
                  })
                  .join('; ') || 'Validation errors occurred';
              } else if (error?.body?.error?.validationErrors && typeof error.body.error.validationErrors === 'object') {
                const validationErrors = error.body.error.validationErrors;
                errorMessage = Object.entries(validationErrors)
                  .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
                  .join('; ');
              } else if (error?.body?.error?.message) {
                errorMessage = error.body.error.message;
              } else if (error?.message) {
                errorMessage = error.message;
              }

              setErrors({ general: errorMessage });
              showError(errorMessage);
              return;
            }
          }

          if (showNewAddressForm) {
            // User is adding a new address to existing contact
            // Find existing or create new address
            try {
              if (address && city && state && zipCode && selectedContact?.id) {
                const addressId = await findOrCreateContactAddress({
                  contactId: selectedContact.id,
                  addressLine1: address,
                  addressLine2: address2,
                  city,
                  state,
                  zipCode,
                  country
                });

                if (addressId) {
                  setCreatedContactAddressId(addressId);
                } else {
                  console.error('Failed to find or create contact address - no ID returned');
                  setCreatedContactAddressId(null);
                }
              } else {
                setCreatedContactAddressId(null);
              }
            } catch (error) {
              console.error('Failed to create address for existing contact:', error);
              setCreatedContactAddressId(null);
            }
          } else if (selectedAddressId) {
            setCreatedContactAddressId(selectedAddressId);
          }
        }
        
        setCompletedSteps(prev => [...prev.filter(step => step !== 'customer'), 'customer']);
        if (skipEventStep) {
          setCompletedSteps(prev => [...prev.filter(step => step !== 'customer' && step !== 'event'), 'customer', 'event']);
          if (hasCustomQuestions) {
            setCurrentStep('questions');
          } else {
            setCurrentStep('preferences');
          }
        } else {
          setCurrentStep('event');
        }
      }
    } else if (currentStep === 'event') {
      if (validateEventForm()) {
        // Check for existing pending request for this contact + booking form
        // If found, automatically use it and pre-fill the form data
        if (eventType?.id && email) {
          await checkForExistingRequest(email, phone, eventType.id);
          // Whether existing request found or not, proceed to next step
          // The form data will be pre-filled if existing request was found
        }

        setCompletedSteps(prev => [...prev.filter(step => step !== 'event'), 'event']);
        // Check if the form has custom questions
        if (hasCustomQuestions) {
          setCurrentStep('questions');
        } else {
          // Go to preferences step
          setCurrentStep('preferences');
        }
      }
    } else if (currentStep === 'questions') {
      // Validate custom questions
      if (validateCustomQuestions()) {
        setCompletedSteps(prev => [...prev.filter(step => step !== 'questions'), 'questions']);
        // Go to preferences step
        setCurrentStep('preferences');
      }
    } else if (currentStep === 'preferences') {
      if (validatePreferencesForm()) {
        setCompletedSteps(prev => [...prev.filter(step => step !== 'preferences'), 'preferences']);
        // Create booking based on type (manual or Routemize)
        if (isManualBooking) {
          await handleManualBookingCreate();
        } else {
          handleRoutemizeBooking();
        }
      }
    }
  };

  const validateCustomQuestions = () => {
    const newErrors: {[key: string]: string} = {};
    // Track the first missing required question (in display order) so we can
    // bring it into view. Conditional hide/show can reveal a required
    // follow-up further down the form (or out of stored order), which would
    // otherwise leave the user clicking Continue with no visible change.
    let firstMissingId: string | null = null;

    visibleQuestions.forEach((question) => {
      const qid = question.id || '';
      if (question.isRequired && !customAnswers[qid]) {
        newErrors[qid] = `${question.label} is required`;
        if (!firstMissingId && qid) firstMissingId = qid;
      }
    });

    setErrors(newErrors);

    if (firstMissingId) {
      // Defer until the error banner/highlight has rendered, then scroll the
      // first missing field into view and focus it so the user always sees
      // why they can't advance.
      requestAnimationFrame(() => {
        const el = document.getElementById(`booking-question-${firstMissingId}`);
        if (!el) return;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = el.querySelector<HTMLElement>('input, textarea, select');
        focusable?.focus({ preventScroll: true });
      });
    }

    return Object.keys(newErrors).length === 0;
  };

  const validatePreferencesForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!preferredDate) {
      newErrors.preferredDate = 'Please select a preferred date';
    }

    if (!preferredTime) {
      newErrors.preferredTime = 'Please select a preferred time';
    }

    if (showLeadSourceField && !selectedLeadSource) {
      newErrors.leadSource = 'Please select how they heard about us';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePrevStep = () => {
    if (currentStep === 'event') {
      if (!skipCustomerStep) {
        setCurrentStep('customer');
      }
    } else if (currentStep === 'questions') {
      if (skipEventStep) {
        setCurrentStep('customer');
      } else {
        setCurrentStep('event');
      }
    } else if (currentStep === 'preferences') {
      if (hasCustomQuestions) {
        setCurrentStep('questions');
      } else if (skipEventStep) {
        setCurrentStep('customer');
      } else {
        setCurrentStep('event');
      }
    } else if (currentStep === 'booking') {
      // Don't go back if we skipped directly to slot selection
      if (!skipToSlotSelection) {
        // Go back to preferences
        setCurrentStep('preferences');
        setShowAvailableSlots(false);
        setAvailableSlots([]);
        setSelectedSlot(null);
        setNoAvailabilityInfo(null);
      }
    } else if (currentStep === 'confirmation') {
      // Go back to slot selection
      setCurrentStep('booking');
      // showAvailableSlots should still be true, slots should still be loaded
    }
  };

  const handleSubmit = async () => {
    if (isCreatingRef.current) return;
    isCreatingRef.current = true;
    setIsSubmitting(true);

    try {
      // Show loading toast
      showInfo('Creating appointment...');

      // Always use Routemize
      await handleCreateAppointment();
    } catch (error: any) {
      console.error('❌ Failed to create booking:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));

      // The request was already converted to an appointment (duplicate/retried submit —
      // e.g. a second confirm after the first already succeeded). Nothing left to fix
      // here — surface the message and close, don't ask to pick another time.
      if (isRequestAlreadyBookedError(error)) {
        showError(resolveSchedulingErrorMessage(error, 'This booking has already been scheduled.'));
        resetForm();
        onClose();
        return;
      }

      surfaceBookingFailure(error, 'Failed to create booking. Please try again.');
    } finally {
      setIsSubmitting(false);
      isCreatingRef.current = false;
    }
  };



  if (!isOpen) return null;

  // Step titles and descriptions - refined for clarity
  const stepContent = {
    customer: { title: 'Customer Details', subtitle: 'Who is this appointment for?' },
    event: { title: 'Select Service', subtitle: 'Choose the service type' },
    questions: { title: 'Additional Details', subtitle: 'Complete required information' },
    preferences: { title: 'Scheduling Preferences', subtitle: 'When works best?' },
    booking: { title: 'Choose Time Slot', subtitle: 'Select the optimal appointment time' },
    confirmation: { title: 'Review & Confirm', subtitle: 'Verify details before booking' }
  };

  const currentContent = stepContent[currentStep as keyof typeof stepContent] || stepContent.customer;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-4 md:p-6">
      {/* Refined Backdrop with blur - no onClick to prevent accidental closing while filling customer details */}
      <div 
        className="absolute inset-0 bg-ink-950/60 backdrop-blur-sm transition-opacity duration-300"
      />
      
      {/* Modal Container - Clean, minimal shadow */}
      <div className={`relative w-full bg-surface shadow-2xl flex flex-col transition-all duration-300 ease-smooth animate-scale-in overflow-hidden h-[100dvh] max-h-[100dvh] rounded-none sm:rounded-2xl min-h-0 sm:h-auto sm:max-h-[calc(100dvh-2rem)] ${
        (currentStep === 'booking' || currentStep === 'slot_selection') && showAvailableSlots
          ? 'max-w-3xl'
          : 'max-w-2xl'
      }`}>
        
        {/* Premium Header - Minimal and clean */}
        <div className="flex-shrink-0 px-4 sm:px-6 py-2.5 sm:py-4 border-b border-ink-100 bg-surface">
          <div className="flex items-center gap-4">
            {/* Back button - subtle */}
            {/* Hide back button on first accessible step (customer normally, event when skipped, booking when skipToSlotSelection) */}
            {!noAvailabilityInfo?.isFull && !(currentStep === 'customer' || (currentStep === 'event' && skipCustomerStep) || (currentStep === 'booking' && skipToSlotSelection)) && (!showAvailableSlots || currentStep === 'confirmation') && (
              <IconButton
                icon={ChevronLeft}
                shape="row-action"
                onClick={handlePrevStep}
                aria-label="Previous step"
              />
            )}
            
            {/* Routemize Icon */}
            <img
              src="https://routemize-public-web.s3.us-east-1.amazonaws.com/Icon-iOS-Default-1024x1024%401x.png"
              alt="Routemize"
              className="w-8 h-8 rounded-lg object-contain flex-shrink-0"
            />

            {/* Title section */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-ink-900 tracking-tight">
                  {noAvailabilityInfo?.isFull
                    ? (noAvailabilityInfo.afterServiceAreaOverride ? 'No Slots Available' : 'New Appointment')
                    : currentContent.title}
                </h2>
                {!noAvailabilityInfo?.isFull && (
                <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 bg-accent-50 text-accent-600 text-xs font-medium rounded-full">
                  <Sparkles className="h-3 w-3" />
                  Smart
                </span>
                )}
              </div>
              <p className="text-sm text-ink-500 mt-0.5">
                {noAvailabilityInfo?.isFull
                  ? (noAvailabilityInfo.afterServiceAreaOverride
                    ? 'No open times found, even outside your service area'
                    : 'No availability for selected time')
                  : currentContent.subtitle}
              </p>
            </div>
            
            {/* Close button - refined */}
            <IconButton
              icon={X}
              shape="row-action"
              onClick={() => handleClose()}
              aria-label="Close"
            />
          </div>
        </div>

          {/* Minimal Progress Stepper - Linear/Stripe inspired */}
          {!noAvailabilityInfo?.isFull && (
          <div className="flex-shrink-0 px-6 py-3 border-b border-ink-100 bg-ink-50/50">
            <div className="flex items-center justify-center gap-1">
              {steps.map((step, index) => {
                const isCompleted = completedSteps.includes(step.id);
                const isCurrent = currentStep === step.id;
                const isPast = isCompleted;
                
                return (
                  <React.Fragment key={step.id}>
                    {/* Step indicator */}
                    <div className="flex items-center gap-2">
                      <div className={`
                        flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium transition-all duration-300
                        ${isCompleted 
                          ? 'bg-success-500 text-white' 
                          : isCurrent 
                            ? 'bg-ink-900 text-white' 
                            : 'bg-ink-100 text-ink-400'
                        }
                      `}>
                        {isCompleted ? (
                          <Check className="h-3.5 w-3.5 stroke-[2.5]" />
                        ) : (
                          <span>{index + 1}</span>
                        )}
                      </div>
                      <span className={`
                        hidden sm:block text-xs font-medium transition-colors duration-200
                        ${isCurrent ? 'text-ink-900' : isPast ? 'text-ink-500' : 'text-ink-400'}
                      `}>
                        {step.shortLabel || step.label}
                      </span>
                    </div>
                    
                    {/* Connector line */}
                    {index < steps.length - 1 && (
                      <div className={`
                        w-8 sm:w-12 h-0.5 rounded-full mx-1 transition-colors duration-300
                        ${completedSteps.includes(step.id) ? 'bg-success-500' : 'bg-ink-200'}
                      `} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Customer Details Step - Premium Form Design */}
            {currentStep === 'customer' && (
              <div className="p-5 sm:p-6 md:p-8">
                {/* When coming from an existing request, show locked contact card */}
                {existingRequestId && (firstName || lastName) ? (
                  <div className="mb-6 p-4 bg-ink-50 border border-ink-200 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-ink-100 rounded-full flex items-center justify-center">
                        <User className="h-5 w-5 text-ink-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-ink-900 text-sm">{firstName} {lastName}</div>
                        <div className="flex items-center gap-3 mt-0.5">
                          {email && <span className="text-xs text-ink-500">{email}</span>}
                          {phone && <span className="text-xs text-ink-400">{formatPhoneNumber(phone)}</span>}
                        </div>
                      </div>
                      <span className="px-2 py-0.5 bg-ink-100 text-ink-500 text-[10px] font-semibold uppercase tracking-wide rounded">
                        From request
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Contact Search - Clean Card */}
                    <div className="mb-6">
                      <label className="block text-xs font-semibold text-ink-500 uppercase tracking-wide mb-2">
                        Find Existing Contact
                      </label>
                      <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                        <input
                          type="search"
                          value={contactSearchQuery}
                          onChange={(e) => handleContactSearch(e.target.value)}
                          placeholder="Search by name, email, or phone..."
                          autoComplete="off"
                          autoCorrect="off"
                          autoCapitalize="off"
                          spellCheck={false}
                          className="w-full pl-11 pr-4 py-3 bg-ink-50 border border-ink-200 rounded-xl text-sm placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-ink-900 focus:border-transparent transition-all"
                        />
                        
                        {/* Search Results - Clean dropdown */}
                        {contactSearchQuery.length >= 2 && (
                          <div className="absolute z-50 w-full mt-2 bg-surface border border-ink-200 rounded-xl shadow-xl overflow-hidden">
                            {searchingContacts ? (
                              <div className="p-4 text-center">
                                <div className="w-5 h-5 border-2 border-ink-200 border-t-ink-600 rounded-full animate-spin mx-auto" />
                              </div>
                            ) : searchedContacts.length === 0 ? (
                              <div className="p-4 text-center text-sm text-ink-500">No contacts found</div>
                            ) : (
                              <div className="max-h-60 overflow-y-auto">
                                {searchedContacts.map((contact) => {
                                  const contactInfo = contact.extraProperties?.contactInfo;
                                  const addresses = contact.extraProperties?.addresses || [];
                                  
                                  return (
                                    // eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending
                                    <button
                                      key={contact.id}
                                      onClick={() => handleContactSelect(contact)}
                                      className="w-full text-left px-4 py-3 hover:bg-ink-50 border-b border-ink-100 last:border-b-0 transition-colors"
                                    >
                                      <div className="font-medium text-ink-900 text-sm">
                                        {contactInfo?.firstName} {contactInfo?.lastName}
                                      </div>
                                      {contactInfo?.email && (
                                        <div className="text-xs text-ink-500 mt-0.5">{contactInfo.email}</div>
                                      )}
                                      {addresses.length > 0 && addresses[0].city && (
                                        <div className="text-xs text-ink-400 mt-0.5">
                                          {addresses[0].city}, {addresses[0].state}
                                        </div>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Selected Contact - Minimal card */}
                    {selectedContact && (
                      <div className="mb-6 p-4 bg-success-50 border border-success-200 rounded-xl">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-success-100 rounded-full flex items-center justify-center">
                              <User className="h-5 w-5 text-success-600" />
                            </div>
                            <div>
                              <div className="font-medium text-ink-900 text-sm">{firstName} {lastName}</div>
                              <div className="text-xs text-ink-500">{email}</div>
                            </div>
                          </div>
                          {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                          <button
                            onClick={() => {
                              setSelectedContact(null);
                              setSelectedAddressId(null);
                              setShowNewAddressForm(false);
                              setCreatedContactId(null);
                              setFirstName('');
                              setLastName('');
                              setEmail('');
                              setPhone('');
                              setAddress('');
                              setCity('');
                              setState('');
                              setZipCode('');
                            }}
                            className="p-1.5 text-ink-400 hover:text-ink-600 hover:bg-surface rounded-lg transition-colors"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        
                        {/* Address selection */}
                        {selectedContact.extraProperties?.addresses && selectedContact.extraProperties.addresses.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-success-200">
                            <select
                              value={selectedAddressId || ''}
                              onChange={(e) => {
                                const addressId = e.target.value;
                                if (addressId === 'new') {
                                  setShowNewAddressForm(true);
                                  setSelectedAddressId(null);
                                  setAddress('');
                                  setCity('');
                                  setState('');
                                  setZipCode('');
                                } else {
                                  setShowNewAddressForm(false);
                                  setSelectedAddressId(addressId);
                                  const selected = selectedContact.extraProperties?.addresses?.find(
                                    (a: any) => a.id === addressId
                                  );
                                  if (selected) {
                                    setAddress(selected.addressLine1 || '');
                                    setCity(selected.city || '');
                                    setState(selected.state || '');
                                    setZipCode(selected.zipCode || '');
                                  }
                                }
                              }}
                              className="w-full px-3 py-2 bg-surface border border-success-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ink-900"
                            >
                              <option value="">Select service address</option>
                              {selectedContact.extraProperties.addresses.map((addr: any) => (
                                <option key={addr.id} value={addr.id}>
                                  {addr.addressLine1}, {addr.city}, {addr.state}
                                </option>
                              ))}
                              <option value="new">+ Add new address</option>
                            </select>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Divider */}
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-ink-200"></div>
                      </div>
                      <div className="relative flex justify-center">
                        <span className="px-3 bg-surface text-xs font-medium text-ink-400">
                          {selectedContact ? 'or edit details' : 'or enter new contact'}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                {/* Contact Form - Clean grid layout */}
                <div className="space-y-5">
                  {/* Name and contact rows - hidden when contact is locked from request */}
                  {!(existingRequestId && (firstName || lastName)) && (
                    <>
                      {/* Name row */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-ink-700 mb-1.5">
                            First Name
                          </label>
                          <input
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            placeholder="John"
                            maxLength={MAX_NAME_LENGTH}
                            className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-all focus:outline-none focus:ring-2 focus:ring-ink-900 ${
                              errors.firstName ? 'border-danger-400 bg-danger-50' : 'border-ink-200 hover:border-ink-300'
                            }`}
                          />
                          {errors.firstName && (
                            <p className="mt-1 text-xs text-danger-600">{errors.firstName}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-ink-700 mb-1.5">
                            Last Name
                          </label>
                          <input
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            placeholder="Smith"
                            maxLength={MAX_NAME_LENGTH}
                            className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-all focus:outline-none focus:ring-2 focus:ring-ink-900 ${
                              errors.lastName ? 'border-danger-400 bg-danger-50' : 'border-ink-200 hover:border-ink-300'
                            }`}
                          />
                          {errors.lastName && (
                            <p className="mt-1 text-xs text-danger-600">{errors.lastName}</p>
                          )}
                        </div>
                      </div>

                      {/* Contact row */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold text-ink-700 mb-1.5">
                            Email
                          </label>
                          <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="john@example.com"
                            className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-all focus:outline-none focus:ring-2 focus:ring-ink-900 ${
                              errors.email ? 'border-danger-400 bg-danger-50' : 'border-ink-200 hover:border-ink-300'
                            }`}
                          />
                          {errors.email && (
                            <p className="mt-1 text-xs text-danger-600">{errors.email}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-ink-700 mb-1.5">
                            Phone
                          </label>
                          <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(formatUSPhone(e.target.value))}
                            placeholder="(555) 123-4567"
                            className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-all focus:outline-none focus:ring-2 focus:ring-ink-900 ${
                              errors.phone ? 'border-danger-400 bg-danger-50' : 'border-ink-200 hover:border-ink-300'
                            }`}
                          />
                          {errors.phone && (
                            <p className="mt-1 text-xs text-danger-600">{errors.phone}</p>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Business Toggle */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={isBusiness}
                        onCheckedChange={(c) => {
                          setIsBusiness(c);
                          if (!c) setBusinessName('');
                        }}
                        aria-label="Is business customer"
                      />
                      <span className="text-sm text-ink-700">This is a business</span>
                    </div>

                    {isBusiness && (
                      <div className="animate-fade-in">
                        <label className="block text-xs font-semibold text-ink-700 mb-1.5">
                          <Building2 className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
                          Business Name
                        </label>
                        <input
                          type="text"
                          value={businessName}
                          onChange={(e) => setBusinessName(e.target.value)}
                          placeholder="Company name"
                          className="w-full px-4 py-2.5 border border-ink-200 hover:border-ink-300 rounded-lg text-sm transition-all focus:outline-none focus:ring-2 focus:ring-ink-900"
                        />
                      </div>
                    )}
                  </div>

                  {/* Address Section with Embedded Map */}
                  {(!selectedContact || showNewAddressForm || !selectedAddressId) && (
                    <>
                      <div className="pt-4 border-t border-ink-100">
                        <h4 className="text-xs font-semibold text-ink-500 uppercase tracking-wide mb-4 flex items-center gap-2">
                          <MapPin className="h-3.5 w-3.5" />
                          Service Location
                        </h4>
                        
                        <AddressInputWithMap
                          value={address}
                          address2={address2}
                          city={city}
                          state={state}
                          zipCode={zipCode}
                          coordinates={addressCoordinates}
                          onChange={(addressData) => {
                            setAddress(addressData.address1);
                            if (addressData.address2) {
                              setAddress2(addressData.address2);
                            }
                            setCity(addressData.city);
                            setState(addressData.state);
                            setZipCode(addressData.zipCode);
                            
                            if (addressData.coordinates) {
                              setAddressCoordinates(addressData.coordinates);
                            }
                            
                            if (addressData.coordinates && slotConstraints && (prevAppointment || nextAppointment)) {
                              validateDriveTimes(addressData.coordinates);
                            }
                          }}
                          onInputChange={(value) => {
                            setAddress(value);
                            setAddressCoordinates(null);
                          }}
                          onAddress2Change={(value) => setAddress2(value)}
                          onCityChange={(value) => setCity(value)}
                          onStateChange={(value) => setState(value)}
                          onZipCodeChange={(value) => setZipCode(value)}
                          placeholder="Start typing address..."
                          required
                          error={errors.address}
                          cityError={errors.city}
                          stateError={errors.state}
                          zipCodeError={errors.zipCode}
                          showMap={true}
                          mapHeight={200}
                        />
                        
                        {driveTimeWarning && (
                          <div className="mt-3 p-3 bg-warning-50 border border-warning-200 rounded-lg">
                            <p className="text-xs text-warning-700">{driveTimeWarning}</p>
                          </div>
                        )}

                        {slotConstraints && !driveTimeWarning && (
                          <div className="mt-3 p-3 bg-info-50 border border-info-200 rounded-lg">
                            <p className="text-xs text-info-700">
                              {slotConstraints.appointmentDuration}min appointment · Max {slotConstraints.maxCombinedDriveTime}min drive time
                            </p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Event Type Step - Premium Card Selection */}
            {currentStep === 'event' && (
              <div className="p-6 max-w-2xl mx-auto">
                {errors.eventType && (
                  <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-danger-600 flex-shrink-0" />
                    <p className="text-sm text-danger-700">{errors.eventType}</p>
                  </div>
                )}

                {/* Helper text - explain why some events may be missing */}
                <div className="mb-4 p-3 bg-ink-50 border border-ink-200 rounded-lg flex items-start gap-2">
                  <Info className="h-4 w-4 text-ink-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-ink-600 leading-relaxed">
                    Events with an assigned lead source are reserved for that channel and won't appear here.
                  </p>
                </div>

                <div className="space-y-3">
                  {eventTypes.map((type) => {
                    const bufferTime = bookingFormSettings[type.id]?.defaultPostAppointmentBufferMinutes;
                    const assignedUsers = type.assignedUsers || [];
                    const isRoundRobin = assignedUsers.length > 1;
                    const isSolo = assignedUsers.length === 1;
                    // Show a "Pre-selected" badge on the form that was auto-selected from the
                    // request, but still allow the user to pick a different service.
                    const isPreselected = Boolean(existingRequestId && allowedLeadSourceFormId && type.id === allowedLeadSourceFormId);
                    const isSelected = eventType?.id === type.id;
                    const eventColors = getBookingFormEventColor(type.eventColor);
                    const formAssigneeColors = assigneeColorsByFormId.get(type.id);
                    const toProviderOption = (user: Routemize_DTOs_UserSmallDTO): PreferredProviderOption => ({
                      id: user.id || '',
                      name: user.name,
                      email: user.email,
                      firstName: user.firstName,
                      lastName: user.lastName,
                      photoUrl: user.photoUrl,
                      color: user.id ? formAssigneeColors?.get(user.id) : undefined,
                    });
                    const nextUpTextColor = type.nextUpUserId && type.nextUpUserName
                      ? getAssigneeColors({
                          id: type.nextUpUserId,
                          name: type.nextUpUserName,
                          color: formAssigneeColors?.get(type.nextUpUserId),
                        }).textColor
                      : undefined;

                    return (
                      <div
                        key={type.id}
                        onClick={() => setEventType(type)}
                        className={`
                          group relative p-4 pl-5 rounded-xl border transition-all duration-200 cursor-pointer overflow-hidden
                          ${isSelected 
                            ? 'border-ink-900 bg-ink-50 shadow-sm' 
                            : 'border-ink-200 hover:border-ink-300 hover:bg-ink-50/50 bg-surface'
                          }
                        `}
                      >
                        {eventColors && (
                          <span
                            aria-hidden="true"
                            className="absolute left-0 top-0 bottom-0 w-1.5"
                            style={{ backgroundColor: eventColors.border }}
                          />
                        )}
                        <div className="flex items-start gap-4">
                          {/* Selection indicator - Radio style */}
                          <div className={`
                            flex-shrink-0 w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center transition-all
                            ${isSelected 
                              ? 'border-ink-900 bg-ink-900' 
                              : 'border-ink-300 group-hover:border-ink-400'
                            }
                          `}>
                            {isSelected && <Check className="h-3 w-3 text-white stroke-[3]" />}
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <div className="flex items-center gap-2">
                                  {eventColors && (
                                    <span
                                      aria-hidden="true"
                                      className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-inset"
                                      style={{
                                        backgroundColor: eventColors.bg,
                                        borderColor: eventColors.border,
                                        boxShadow: `inset 0 0 0 1px ${eventColors.border}`,
                                      }}
                                    />
                                  )}
                                  <h4 className="font-semibold text-ink-900 text-base">{type.name}</h4>
                                </div>
                                {type.description && (
                                  <p className="text-sm text-ink-500 mt-0.5 line-clamp-2">{type.description}</p>
                                )}
                              </div>
                              
                              {/* Duration badge + Pre-selected indicator */}
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {isPreselected && (
                                  <div className="flex items-center gap-1 px-2 py-1 bg-success-50 border border-success-200 rounded-lg">
                                    <Check className="h-3 w-3 text-success-600 stroke-[2.5]" />
                                    <span className="text-[11px] font-semibold text-success-700">Pre-selected</span>
                                  </div>
                                )}
                                <div className="px-2.5 py-1 bg-ink-100 rounded-lg">
                                  <span className="text-xs font-semibold text-ink-700">{type.duration}</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Meta info — pill chips wrap cleanly when the
                                card is dense (round-robin + buffer +
                                next-up + smart routing all visible). Each
                                fact gets its own chip with consistent
                                padding so the row stays scannable instead
                                of running together. */}
                            <div className="flex flex-wrap items-center gap-1.5 mt-3">
                              {/* Team members chip — a solo form gets the same
                                  tinted name chip the slot list and
                                  confirmation screen use, so an operator can
                                  tell whose form a card is at a glance. */}
                              {isRoundRobin ? (
                                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-surface/80 border border-ink-200">
                                  <div className="flex -space-x-1.5">
                                    {assignedUsers.slice(0, 3).map((user, idx) => {
                                      const initials = `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase() || (user.name?.[0] ?? '?').toUpperCase();
                                      const seed = user.id || user.name || `${user.firstName ?? ''}${user.lastName ?? ''}`;
                                      const photoUrl = user.photoUrl;
                                      return photoUrl ? (
                                        <img
                                          key={user.id ?? idx}
                                          src={photoUrl}
                                          alt={user.name ?? ''}
                                          title={user.name ?? ''}
                                          className="w-5 h-5 rounded-full border-2 border-white object-cover"
                                          style={{ zIndex: 3 - idx }}
                                        />
                                      ) : (
                                        <div
                                          key={user.id ?? idx}
                                          className="w-5 h-5 rounded-full border-2 border-white flex items-center justify-center"
                                          style={{
                                            zIndex: 3 - idx,
                                            backgroundColor: (user.id ? formAssigneeColors?.get(user.id) : undefined) ?? getAvatarColor(seed),
                                          }}
                                          title={user.name ?? ''}
                                        >
                                          <span className="text-white text-[8px] font-semibold leading-none">
                                            {initials}
                                          </span>
                                        </div>
                                      );
                                    })}
                                    {assignedUsers.length > 3 && (
                                      <div className="w-5 h-5 bg-ink-100 rounded-full border-2 border-white flex items-center justify-center text-[8px] font-semibold text-ink-600">
                                        +{assignedUsers.length - 3}
                                      </div>
                                    )}
                                  </div>
                                  <span className="text-[11px] font-medium text-ink-600">
                                    {assignedUsers.length} team members
                                  </span>
                                </div>
                              ) : isSolo ? (
                                <AssigneeChip assignee={toProviderOption(assignedUsers[0])} />
                              ) : (
                                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-surface/80 border border-ink-200">
                                  <User className="h-3 w-3 text-ink-500" />
                                  <span className="text-[11px] font-medium text-ink-600">Unassigned</span>
                                </div>
                              )}

                              {/* Buffer chip */}
                              {bufferTime != null && bufferTime > 0 && (
                                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-surface/80 border border-ink-200">
                                  <Clock className="h-3 w-3 text-ink-500" />
                                  <span className="text-[11px] font-medium text-ink-600">+{bufferTime}min buffer</span>
                                </div>
                              )}

                              {/* Next-up chip — equal-rotation only.
                                  Territories routes by zip and has no
                                  single "next up" to surface. */}
                              {isRoundRobin && !isTerritoriesMode && type.nextUpUserName && (
                                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-surface/80 border border-ink-200 max-w-full">
                                  <ArrowRight className="h-3 w-3 text-ink-500 flex-shrink-0" />
                                  <span className="text-[11px] text-ink-600 truncate">
                                    Next:{' '}
                                    <span
                                      className="font-semibold text-ink-800"
                                      style={nextUpTextColor ? { color: nextUpTextColor } : undefined}
                                    >
                                      {type.nextUpUserName}
                                    </span>
                                  </span>
                                </div>
                              )}

                              {/* Smart routing chip — accent-tinted so the
                                  feature highlight pops against the
                                  neutral chips. */}
                              {type.isRoutemize && (
                                <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-accent-50 border border-accent-200">
                                  <Sparkles className="h-3 w-3 text-accent-600" />
                                  <span className="text-[11px] font-semibold text-accent-700">Smart routing</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}


            {/* Questions Step - Clean form design */}
            {currentStep === 'questions' && formQuestions.length > 0 && (
              <div className="p-5 sm:p-6 md:p-8">
                {/* When the user clicks Continue with required answers missing,
                    surface a single clear message here. Because conditional
                    follow-ups can be revealed lower on the form (or out of
                    order), the missing field is often off-screen — without this
                    banner + the scroll-to-error in validateCustomQuestions, the
                    Continue click looks like it did nothing and people get
                    stuck staring at blank space. */}
                {Object.keys(errors).some(
                  qid => qid !== 'general' && visibleQuestions.some(q => q.id === qid)
                ) && (
                  <div className="mb-5 p-3 bg-danger-50 border border-danger-200 rounded-lg flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-danger-600 flex-shrink-0" />
                    <p className="text-sm text-danger-700">
                      Please answer the highlighted question
                      {visibleQuestions.filter(q => q.id && errors[q.id]).length > 1 ? 's' : ''} to continue.
                    </p>
                  </div>
                )}
                <div className="space-y-5">
                  {visibleQuestions.map((question, index) => {
                    const parsedOptions = (() => {
                      if (!question.optionsJson) return { options: [] as string[], isRadio: false };
                      try {
                        const parsed = JSON.parse(question.optionsJson);
                        if (Array.isArray(parsed)) return { options: parsed as string[], isRadio: false };
                        if (parsed && Array.isArray(parsed.options)) {
                          return { options: parsed.options as string[], isRadio: parsed.displayType === 'radio' };
                        }
                        return { options: [] as string[], isRadio: false };
                      } catch { return { options: [] as string[], isRadio: false }; }
                    })();
                    const options = parsedOptions.options;
                    const isRadioDisplay = parsedOptions.isRadio;
                    const qType = question.type || 'ShortText';
                    const isShortText = qType === 'ShortText';
                    const isParagraph = qType === 'Paragraph';
                    const isDropdown = qType === 'Dropdown';
                    const isCheckbox = qType === 'Checkbox';
                    
                    return (
                    <div
                      key={question.id}
                      id={`booking-question-${question.id || ''}`}
                      className="space-y-2 scroll-mt-24"
                    >
                      <label className="block text-xs font-semibold text-ink-700">
                        {question.label}
                        {question.isRequired && <span className="text-danger-500 ml-0.5">*</span>}
                      </label>
                      
                      {isShortText && (
                        <input
                          type="text"
                          value={customAnswers[question.id || ''] || ''}
                          onChange={(e) => setCustomAnswers(prev => ({
                            ...prev,
                            [question.id || '']: e.target.value
                          }))}
                          autoFocus={index === 0}
                          className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-all focus:outline-none focus:ring-2 focus:ring-ink-900 ${
                            errors[question.id || ''] ? 'border-danger-400 bg-danger-50' : 'border-ink-200 hover:border-ink-300'
                          }`}
                        />
                      )}
                      
                      {isParagraph && (
                        <textarea
                          value={customAnswers[question.id || ''] || ''}
                          onChange={(e) => setCustomAnswers(prev => ({
                            ...prev,
                            [question.id || '']: e.target.value
                          }))}
                          rows={3}
                          className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-all focus:outline-none focus:ring-2 focus:ring-ink-900 resize-none ${
                            errors[question.id || ''] ? 'border-danger-400 bg-danger-50' : 'border-ink-200 hover:border-ink-300'
                          }`}
                        />
                      )}
                      
                      {isCheckbox && isRadioDisplay && options.length > 0 && (
                        <div className="space-y-2 pt-1">
                          {options.map((option: string, idx: number) => {
                            const currentValue = customAnswers[question.id || ''] || '';
                            const isSelected = currentValue === option;
                            return (
                            <label key={idx} className="flex items-center gap-3 cursor-pointer group">
                              <div className={`
                                w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all
                                ${isSelected
                                  ? 'border-ink-900' 
                                  : 'border-ink-300 group-hover:border-ink-400'
                                }
                              `}>
                                {isSelected && (
                                  <div className="w-2 h-2 rounded-full bg-ink-900" />
                                )}
                              </div>
                              <input
                                type="radio"
                                name={`question-${question.id}`}
                                value={option}
                                checked={isSelected}
                                onChange={() => {
                                  setCustomAnswers(prev => ({
                                    ...prev,
                                    [question.id || '']: option
                                  }));
                                }}
                                className="sr-only"
                              />
                              <span className="text-sm text-ink-700">{option}</span>
                            </label>
                            );
                          })}
                        </div>
                      )}
                      
                      {isCheckbox && !isRadioDisplay && options.length > 0 && (
                        <div className="space-y-2 pt-1">
                          {options.map((option: string, idx: number) => {
                            const currentValues = (customAnswers[question.id || ''] || '').split('||').filter(Boolean);
                            const isChecked = currentValues.includes(option);
                            return (
                            <label key={idx} className="flex items-center gap-3 cursor-pointer group">
                              <div className={`
                                w-4 h-4 rounded border-2 flex items-center justify-center transition-all
                                ${isChecked
                                  ? 'border-ink-900 bg-ink-900' 
                                  : 'border-ink-300 group-hover:border-ink-400'
                                }
                              `}>
                                {isChecked && (
                                  <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 6l3 3 5-5" /></svg>
                                )}
                              </div>
                              <input
                                type="checkbox"
                                value={option}
                                checked={isChecked}
                                onChange={() => {
                                  const updated = isChecked
                                    ? currentValues.filter(v => v !== option)
                                    : [...currentValues, option];
                                  setCustomAnswers(prev => ({
                                    ...prev,
                                    [question.id || '']: updated.join('||')
                                  }));
                                }}
                                className="sr-only"
                              />
                              <span className="text-sm text-ink-700">{option}</span>
                            </label>
                            );
                          })}
                        </div>
                      )}
                      
                      {isDropdown && options.length > 0 && (
                        <select
                          value={customAnswers[question.id || ''] || ''}
                          onChange={(e) => setCustomAnswers(prev => ({
                            ...prev,
                            [question.id || '']: e.target.value
                          }))}
                          className={`w-full px-4 py-2.5 border rounded-lg text-sm transition-all focus:outline-none focus:ring-2 focus:ring-ink-900 ${
                            errors[question.id || ''] ? 'border-danger-400 bg-danger-50' : 'border-ink-200 hover:border-ink-300'
                          }`}
                        >
                          <option value="">Select an option</option>
                          {options.map((option: string, idx: number) => (
                            <option key={idx} value={option}>{option}</option>
                          ))}
                        </select>
                      )}
                      
                      {errors[question.id || ''] && (
                        <p className="text-xs text-danger-600 mt-1">{errors[question.id || '']}</p>
                      )}
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Preferences Step - Clean minimal design */}
            {currentStep === 'preferences' && !isRoutemizing && !showAvailableSlots && (
              <div className="p-5 sm:p-6 md:p-8">
                <div className="space-y-6">
                  {/* Preferred Provider editor — mirrors the reschedule
                      preference editor in EditEventModal so the operator can
                      retarget the request before re-running GetSlots. The
                      provider picker only appears for smart-assignment forms.
                      Event-type swapping is intentionally hidden here for now;
                      cancel and start a new booking to pick a different
                      event type. */}
                  {eventTypes.length > 0 && (
                    <div className="space-y-4 rounded-2xl border border-accent-200 bg-gradient-to-br from-accent-50/60 to-white p-4">
                      {isSmartAssignmentForm && smartAssignmentUsers.length > 0 && (
                        <div>
                          {/* Smart Assignment header — surfaces the tenant
                              config (rotation mode + override toggles +
                              territory map) read from
                              CompanySettings.smartAssignmentConfigJson.
                              The dropdown's Auto option already
                              communicates "next up" / "routes to", so we
                              keep the surrounding chrome lightweight. */}
                          <div className="mb-2">
                            <label className="text-[10px] text-accent-600 uppercase font-semibold tracking-wide flex items-center gap-1.5">
                              <Sparkles className="w-3 h-3 text-accent-500" />Smart Assignment Settings
                            </label>
                          </div>

                          {/* Mode + active overrides as pastel badges. The
                              override badges carry a title so hovering
                              surfaces the same descriptions admins see in
                              the Smart Assignment settings panel — no need
                              to leave the modal to remember what each rule
                              does. */}
                          <div className="flex flex-wrap items-center gap-1.5 mb-3">
                            {isTerritoriesMode ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-teal-50 text-teal-700 border border-teal-200">
                                <MapIcon className="w-3 h-3" /> Territories
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-violet-50 text-violet-700 border border-violet-200">
                                <Sparkles className="w-3 h-3" /> Equal rotation
                              </span>
                            )}
                            {smartOverrideActive && (
                              // eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending
                              <button
                                type="button"
                                onClick={() => setOpenSmartAssignmentExplanation(
                                  isTerritoriesMode
                                    ? 'territory_customer_preference'
                                    : 'equal_rotation_customer_preference'
                                )}
                                aria-label="Explain Smart override"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-colors"
                              >
                                <Check className="w-3 h-3" /> Smart override
                                <HelpCircle className="w-3 h-3 opacity-70" />
                              </button>
                            )}
                            {proximityOverrideActive && (
                              // eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending
                              <button
                                type="button"
                                onClick={() => setOpenSmartAssignmentExplanation(
                                  isTerritoriesMode
                                    ? 'territory_proximity_override'
                                    : 'equal_rotation_proximity_override'
                                )}
                                aria-label="Explain Proximity override"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 hover:border-sky-300 transition-colors"
                              >
                                <Check className="w-3 h-3" /> Proximity · {smartAssignmentPrefs.proximityThresholdMiles} mi
                                <HelpCircle className="w-3 h-3 opacity-70" />
                              </button>
                            )}
                            {isManualPreferredAssignee && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                                <AlertCircle className="w-3 h-3" /> Manual override
                              </span>
                            )}
                          </div>

                          {/* Territories-only routing context. Equal-rotation
                              "next up" intentionally lives only inside the
                              Auto option's subtitle to avoid duplication. */}
                          {isTerritoriesMode && territoryRouting && territoryRouting.matchingUsers.length > 0 && (
                            <div className="flex items-start gap-1.5 mb-2 text-[11.5px] text-ink-600 leading-snug">
                              <Navigation2 className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
                              <span>
                                Zip <span className="font-mono font-semibold text-ink-700">{territoryRouting.customerZip}</span>{' '}
                                {territoryRouting.matchingUsers.length > 1 ? 'round-robins between' : 'routes to'}{' '}
                                <span className="font-semibold text-ink-800">
                                  {territoryRouting.matchingUsers
                                    .map(u => (u?.name || `${u?.firstName ?? ''} ${u?.lastName ?? ''}`.trim() || u?.email || 'Unnamed'))
                                    .join(', ')}
                                </span>
                                {territoryRouting.territories.length > 0 && (
                                  <span className="text-ink-400">
                                    {' '}({territoryRouting.territories
                                      .map((t, i) => getTerritoryDisplayName(t, territoriesData.territories.findIndex(x => x.id === t.id) >= 0 ? territoriesData.territories.findIndex(x => x.id === t.id) : i))
                                      .join(', ')})
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                          {isTerritoriesMode && !territoryRouting && (
                            <div className="flex items-start gap-1.5 mb-2 text-[11.5px] text-ink-500 leading-snug">
                              <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                              <span>
                                {normalizedCustomerZip5
                                  ? <>Zip <span className="font-mono font-semibold">{normalizedCustomerZip5}</span> isn't in any configured territory — Routemize will fall back to standard rotation.</>
                                  : <>Add the customer's address to see which territory their zip routes to.</>}
                              </span>
                            </div>
                          )}

                          {/* Preferred Provider sub-header — labels the
                              dropdown directly so the operator knows the
                              picker is the override surface for the
                              Smart Assignment rules above. */}
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <label className="text-[10px] text-accent-600 uppercase font-semibold tracking-wide flex items-center gap-1.5">
                              <User className="w-3 h-3 text-accent-500" />Preferred Provider
                              <span className="ml-1 normal-case tracking-normal text-[10px] font-medium text-ink-400">
                                · optional override
                              </span>
                            </label>
                            {isManualPreferredAssignee && (
                              // eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending
                              <button
                                type="button"
                                onClick={handleResetPreferredAssigneeToAuto}
                                className="text-[11px] font-medium text-accent-600 hover:text-accent-700 hover:underline flex items-center gap-1"
                              >
                                Use automatic
                              </button>
                            )}
                          </div>

                          <PreferredProviderDropdown
                            value={preferredAssigneeId}
                            options={preferredProviderOptions}
                            onChange={(newId) => {
                              userChangedPreferredAssignee.current = true;
                              setPreferredAssigneeId(newId);
                              setUserChangedPreferences(true);
                            }}
                            showAutoOption={smartAssignmentUsers.length > 1}
                            autoLabel="Auto-assign"
                            autoSubtitle={autoAssignOptionLabel.replace(/^Auto-assign — /, '')}
                            ariaLabel="Preferred provider"
                          />
                          {/* Outside-territory warning — surfaces when the
                              operator picks someone who isn't assigned to
                              the customer's zip territory. The pick is
                              still allowed (this is an explicit override),
                              but the badge makes it obvious that the
                              normal territory routing isn't happening. */}
                          {(() => {
                            if (!isTerritoriesMode) return null;
                            if (!preferredAssigneeId) return null;
                            if (!territoryRouting || territoryRouting.matchingUsers.length === 0) return null;
                            const isMatch = territoryRouting.matchingUsers.some(u => u.id === preferredAssigneeId);
                            if (isMatch) return null;
                            const formatName = (u?: Routemize_DTOs_UserSmallDTO | null) =>
                              (u?.name || `${u?.firstName ?? ''} ${u?.lastName ?? ''}`.trim() || u?.email || 'Unnamed');
                            return (
                              <div className="mt-2 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-[12px] text-amber-800 leading-snug">
                                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                <span>
                                  <span className="font-semibold">Outside territory.</span>{' '}
                                  Zip <span className="font-mono font-semibold">{territoryRouting.customerZip}</span> routes to{' '}
                                  <span className="font-semibold">
                                    {territoryRouting.matchingUsers.map(formatName).join(', ')}
                                  </span>. The provider you picked isn't assigned to this territory.
                                </span>
                              </div>
                            );
                          })()}
                          <p className="mt-1.5 text-[11px] text-ink-400 flex items-start gap-1">
                            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            {preferredAssigneeId === ''
                              ? <>Routemize picks automatically when you press Find Slots. Pick a specific provider only to override the {isTerritoriesMode ? 'territory match' : 'rotation'}.</>
                              : isManualPreferredAssignee
                                ? <>Slots will be searched for this provider only. Press "Use automatic" to let Routemize choose.</>
                                : <>Slots will be searched for this provider only — same as the current automatic pick.</>}
                          </p>
                        </div>
                      )}

                      {/* Solo (non-smart-assignment) forms: still surface
                          the assigned provider so admins can see who'll
                          handle the booking. Read-only — solo events are
                          tied to a single user by design. */}
                      {!isSmartAssignmentForm && eventType?.id && preferredProviderOptions.length > 0 && (
                        <div>
                          <label className="text-[10px] text-accent-600 uppercase font-semibold tracking-wide mb-2 flex items-center gap-1.5">
                            <User className="w-3 h-3 text-accent-500" />Assigned Provider
                          </label>
                          <PreferredProviderDropdown
                            value={preferredProviderOptions[0].id}
                            options={[preferredProviderOptions[0]]}
                            onChange={() => { /* read-only */ }}
                            disabled
                            ariaLabel="Assigned provider"
                          />
                          <p className="mt-1.5 text-[11px] text-ink-400 flex items-start gap-1">
                            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            Solo event — only this provider handles these bookings.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 1: Preferred Date - horizontal scrollable cards */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-ink-900 text-white text-xs font-semibold flex-shrink-0">1</span>
                      <h4 className="text-sm font-semibold text-ink-900">
                        Pick the customer's preferred date
                      </h4>
                    </div>

                    <div className="flex gap-2 overflow-x-auto pt-3 pb-2 -mx-1 px-1 snap-x">
                      {dateCardOptions.map((day) => {
                        const dateKey = formatToLocalDateString(day);
                        const isSelected = preferredDate === dateKey;
                        const requestedDate = existingRequestForContact?.preferredDate?.slice(0, 10);
                        const backupDate = existingRequestForContact?.preferredSecondaryDate?.slice(0, 10);
                        const isPrimaryReq = !!requestedDate && dateKey === requestedDate;
                        const isBackupReq = !!backupDate && dateKey === backupDate;
                        const isToday = dateKey === formatToLocalDateString(new Date());

                        // Selection is shown via a vivid accent border +
                        // soft ring + a "Selected" pill on top — the
                        // previous solid-black fill read as "today" and
                        // confused operators when the actual today and
                        // the selected date were different. Today retains
                        // its own dark pill so both states are visible
                        // independently when they happen to coincide.
                        const badgeText = isToday && isSelected
                          ? 'Today · Selected'
                          : isSelected
                            ? 'Selected'
                            : isToday
                              ? 'Today'
                              : null;
                        const badgeClass = isSelected
                          ? 'bg-accent-600 text-white'
                          : 'bg-ink-900 text-white';
                        return (
                          // eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending
                          <button
                            key={dateKey}
                            type="button"
                            onClick={() => setPreferredDate(dateKey)}
                            className={`
                              relative flex-shrink-0 snap-start w-[68px] py-2.5 px-1 rounded-xl border transition-all duration-200 flex flex-col items-center gap-0.5
                              ${isSelected
                                ? 'border-accent-500 bg-accent-50/60 ring-2 ring-accent-200 shadow-sm'
                                : isPrimaryReq
                                  ? 'border-accent-300 bg-accent-50 hover:border-accent-400'
                                  : isBackupReq
                                    ? 'border-amber-300 bg-amber-50 hover:border-amber-400'
                                    : 'border-ink-200 bg-surface hover:border-ink-300 hover:bg-ink-50/50'
                              }
                            `}
                          >
                            {badgeText && (
                              <span
                                className={`absolute -top-1.5 left-1/2 -translate-x-1/2 px-1.5 py-px rounded-full text-[8px] font-bold uppercase tracking-wide leading-tight whitespace-nowrap ${badgeClass}`}
                              >
                                {badgeText}
                              </span>
                            )}
                            <span className="text-[10px] uppercase tracking-wide font-semibold text-ink-500">
                              {formatWeekdayShort(day)}
                            </span>
                            <span className={`text-lg font-bold leading-tight ${isSelected ? 'text-accent-700' : 'text-ink-900'}`}>
                              {day.getDate()}
                            </span>
                            <span className="text-[10px] uppercase tracking-wide text-ink-500">
                              {formatMonthShort(day)}
                            </span>
                            {(isPrimaryReq || isBackupReq) && !isSelected && (
                              <span className={`mt-0.5 w-1.5 h-1.5 rounded-full ${isPrimaryReq ? 'bg-accent-500' : 'bg-amber-500'}`} />
                            )}
                          </button>
                        );
                      })}

                      {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                      <button
                        type="button"
                        onClick={() => setShowMoreDatesPopup(true)}
                        className="flex-shrink-0 snap-start w-[68px] py-2.5 px-1 rounded-xl border border-dashed border-ink-300 bg-surface text-ink-700 hover:border-ink-500 hover:bg-ink-50 transition-all duration-200 flex flex-col items-center justify-center gap-1"
                      >
                        <Calendar className="h-4 w-4" />
                        <span className="text-[10px] uppercase tracking-wide font-semibold leading-tight text-center">
                          More
                          <br />
                          dates
                        </span>
                      </button>
                    </div>

                    {errors.preferredDate && (
                      <p className="text-xs text-danger-600">{errors.preferredDate}</p>
                    )}

                    {existingRequestForContact && (existingRequestForContact.preferredDate || existingRequestForContact.preferredSecondaryDate) && (
                      <div className="flex items-center gap-4 text-[11px] text-ink-500">
                        {existingRequestForContact.preferredDate && (
                          <span className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-accent-500" />
                            Requested
                          </span>
                        )}
                        {existingRequestForContact.preferredSecondaryDate && (
                          <span className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-amber-500" />
                            Backup
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* More Dates Popup */}
                  {showMoreDatesPopup && (
                    <div
                      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
                      onClick={() => setShowMoreDatesPopup(false)}
                    >
                      <div className="absolute inset-0 bg-ink-950/60 backdrop-blur-sm" />
                      <div
                        className="relative bg-surface shadow-2xl w-full max-w-md flex flex-col rounded-t-2xl sm:rounded-2xl overflow-hidden animate-scale-in max-h-[92vh]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Header */}
                        <div className="flex-shrink-0 px-5 pt-5 pb-3 border-b border-ink-100">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-ink-900 text-white">
                                <Calendar className="h-3.5 w-3.5" />
                              </span>
                              <div>
                                <h5 className="text-sm font-semibold text-ink-900 leading-tight">Select a date</h5>
                                <p className="text-[11px] text-ink-500 leading-tight">
                                  {preferredDate
                                    ? formatDateLong(new Date(preferredDate + 'T00:00:00'))
                                    : 'No date selected yet'}
                                </p>
                              </div>
                            </div>
                            {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                            <button
                              type="button"
                              onClick={() => setShowMoreDatesPopup(false)}
                              className="flex items-center justify-center w-8 h-8 rounded-lg text-ink-400 hover:text-ink-600 hover:bg-ink-50 transition-colors"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {/* Body - scrollable */}
                        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                          {/* Quick presets */}
                          {datePresets.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-500">
                                Quick picks
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {datePresets.map((preset) => {
                                  const presetKey = formatToLocalDateString(preset.date);
                                  const isSelected = preferredDate === presetKey;
                                  return (
                                    // eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending
                                    <button
                                      key={preset.key}
                                      type="button"
                                      onClick={() => {
                                        setPreferredDate(presetKey);
                                        setShowMoreDatesPopup(false);
                                      }}
                                      className={`
                                        flex flex-col items-start gap-0.5 px-3 py-2 rounded-lg border text-left transition-all duration-150
                                        ${isSelected
                                          ? 'border-ink-900 bg-ink-900 text-white'
                                          : 'border-ink-200 bg-surface text-ink-900 hover:border-ink-300 hover:bg-ink-50/60'
                                        }
                                      `}
                                    >
                                      <span className="text-xs font-semibold leading-tight">
                                        {preset.label}
                                      </span>
                                      <span className={`text-[10px] leading-tight ${isSelected ? 'text-white/80' : 'text-ink-500'}`}>
                                        {formatDateMedium(preset.date)}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {/* Calendar */}
                          <div className="space-y-2">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-500">
                              Or pick from calendar
                            </p>
                            <div className="flex justify-center">
                              <DatePicker
                                selected={preferredDate ? new Date(preferredDate + 'T00:00:00') : null}
                                onChange={(date) => {
                                  if (date) {
                                    const year = date.getFullYear();
                                    const month = String(date.getMonth() + 1).padStart(2, '0');
                                    const day = String(date.getDate()).padStart(2, '0');
                                    setPreferredDate(`${year}-${month}-${day}`);
                                    setShowMoreDatesPopup(false);
                                  }
                                }}
                                minDate={new Date()}
                                maxDate={maxDate}
                                filterDate={filterAllowedDates}
                                inline
                                calendarClassName="inline-calendar"
                                dayClassName={(date) => {
                                  if (!existingRequestForContact) return '';
                                  const y = date.getFullYear();
                                  const m = String(date.getMonth() + 1).padStart(2, '0');
                                  const d = String(date.getDate()).padStart(2, '0');
                                  const dateStr = `${y}-${m}-${d}`;
                                  const requestedDate = existingRequestForContact.preferredDate?.slice(0, 10);
                                  const backupDate = existingRequestForContact.preferredSecondaryDate?.slice(0, 10);
                                  if (requestedDate && dateStr === requestedDate) return 'request-preferred-date';
                                  if (backupDate && dateStr === backupDate) return 'request-backup-date';
                                  return '';
                                }}
                              />
                            </div>
                          </div>

                          {/* Legend (only when there's an existing request to highlight) */}
                          {existingRequestForContact && (existingRequestForContact.preferredDate || existingRequestForContact.preferredSecondaryDate) && (
                            <div className="flex items-center justify-center gap-4 text-[11px] text-ink-500 pt-1">
                              {existingRequestForContact.preferredDate && (
                                <span className="flex items-center gap-1.5">
                                  <span className="w-2 h-2 rounded-full bg-accent-500" />
                                  Customer requested
                                </span>
                              )}
                              {existingRequestForContact.preferredSecondaryDate && (
                                <span className="flex items-center gap-1.5">
                                  <span className="w-2 h-2 rounded-full bg-amber-500" />
                                  Backup
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Footer */}
                        <div className="flex-shrink-0 px-5 py-3 border-t border-ink-100 flex items-center justify-end gap-3">
                          <Button variant="ghost" onClick={() => setShowMoreDatesPopup(false)}>
                            Cancel
                          </Button>
                          <Button onClick={() => setShowMoreDatesPopup(false)} disabled={!preferredDate}>
                            Done
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Inline Calendar Styling */}
                  <style>{`
                    .inline-calendar {
                      font-family: inherit !important;
                      border: 1px solid #e5e7eb !important;
                      border-radius: 16px !important;
                      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
                      overflow: hidden !important;
                      background: white !important;
                    }
                    .inline-calendar .react-datepicker__header {
                      background: #1a1a2e !important;
                      border-bottom: none !important;
                      padding: 16px 16px 12px !important;
                      border-radius: 0 !important;
                    }
                    .inline-calendar .react-datepicker__current-month {
                      color: white !important;
                      font-weight: 600 !important;
                      font-size: 1.1rem !important;
                      margin-bottom: 8px !important;
                    }
                    .inline-calendar .react-datepicker__navigation {
                      top: 18px !important;
                    }
                    .inline-calendar .react-datepicker__navigation-icon::before {
                      border-color: white !important;
                      border-width: 2px 2px 0 0 !important;
                    }
                    .inline-calendar .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
                      border-color: rgba(255,255,255,0.7) !important;
                    }
                    /* react-datepicker v8 renders day names inside .react-datepicker__month
                       (no longer inside the dark header), so style them for the white body. */
                    .inline-calendar .react-datepicker__day-names {
                      display: flex !important;
                      justify-content: space-around !important;
                      padding: 8px 12px 4px !important;
                      border-bottom: 1px solid #f3f4f6 !important;
                      margin-bottom: 4px !important;
                    }
                    .inline-calendar .react-datepicker__day-name {
                      color: #6b7280 !important;
                      font-weight: 600 !important;
                      font-size: 0.7rem !important;
                      width: 40px !important;
                      line-height: 1.5 !important;
                      margin: 0 !important;
                      text-transform: uppercase !important;
                      letter-spacing: 0.05em !important;
                    }
                    .inline-calendar .react-datepicker__month {
                      margin: 0 !important;
                      padding: 8px 12px 12px !important;
                    }
                    .inline-calendar .react-datepicker__week {
                      display: flex !important;
                      justify-content: space-around !important;
                    }
                    .inline-calendar .react-datepicker__day {
                      width: 40px !important;
                      height: 40px !important;
                      line-height: 40px !important;
                      margin: 3px 0 !important;
                      border-radius: 12px !important;
                      font-weight: 500 !important;
                      font-size: 0.9rem !important;
                      color: #1a1a2e !important;
                      transition: all 0.15s ease !important;
                    }
                    .inline-calendar .react-datepicker__day:hover {
                      background: #f3f4f6 !important;
                      color: #1a1a2e !important;
                    }
                    .inline-calendar .react-datepicker__day--selected {
                      background: #1a1a2e !important;
                      color: white !important;
                      font-weight: 600 !important;
                    }
                    .inline-calendar .react-datepicker__day--selected:hover {
                      background: #2a2a4e !important;
                      color: white !important;
                    }
                    .inline-calendar .react-datepicker__day--keyboard-selected {
                      background: #e5e7eb !important;
                      color: #1a1a2e !important;
                    }
                    .inline-calendar .react-datepicker__day--today {
                      font-weight: 700 !important;
                      color: #1a1a2e !important;
                      background: transparent !important;
                      border: 2px solid #1a1a2e !important;
                    }
                    .inline-calendar .react-datepicker__day--today.react-datepicker__day--selected {
                      border: none !important;
                      background: #1a1a2e !important;
                      color: white !important;
                    }
                    .inline-calendar .react-datepicker__day--disabled {
                      color: #d1d5db !important;
                      cursor: not-allowed !important;
                    }
                    .inline-calendar .react-datepicker__day--disabled:hover {
                      background: transparent !important;
                    }
                    .inline-calendar .react-datepicker__day--outside-month {
                      color: #9ca3af !important;
                    }
                    /* Request preferred date indicator */
                    .inline-calendar .react-datepicker__day.request-preferred-date {
                      position: relative;
                      background: rgba(99, 102, 241, 0.1) !important;
                      color: #4338ca !important;
                      font-weight: 700 !important;
                    }
                    .inline-calendar .react-datepicker__day.request-preferred-date::after {
                      content: '';
                      position: absolute;
                      bottom: 4px;
                      left: 50%;
                      transform: translateX(-50%);
                      width: 5px;
                      height: 5px;
                      border-radius: 50%;
                      background: #6366f1;
                    }
                    .inline-calendar .react-datepicker__day.request-preferred-date.react-datepicker__day--selected {
                      background: #1a1a2e !important;
                      color: white !important;
                    }
                    .inline-calendar .react-datepicker__day.request-preferred-date.react-datepicker__day--selected::after {
                      background: #6366f1;
                    }
                    /* Request backup date indicator */
                    .inline-calendar .react-datepicker__day.request-backup-date {
                      position: relative;
                      background: rgba(251, 191, 36, 0.12) !important;
                      color: #92400e !important;
                      font-weight: 700 !important;
                    }
                    .inline-calendar .react-datepicker__day.request-backup-date::after {
                      content: '';
                      position: absolute;
                      bottom: 4px;
                      left: 50%;
                      transform: translateX(-50%);
                      width: 5px;
                      height: 5px;
                      border-radius: 50%;
                      background: #f59e0b;
                    }
                    .inline-calendar .react-datepicker__day.request-backup-date.react-datepicker__day--selected {
                      background: #1a1a2e !important;
                      color: white !important;
                    }
                    .inline-calendar .react-datepicker__day.request-backup-date.react-datepicker__day--selected::after {
                      background: #f59e0b;
                    }
                  `}</style>

                  {/* Step 2: Preferred Time - Clean button group */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-ink-900 text-white text-xs font-semibold flex-shrink-0">2</span>
                      <h4 className="text-sm font-semibold text-ink-900">
                        Pick the customer's preferred time
                      </h4>
                    </div>
                    <p className="text-xs text-ink-500 ml-8 -mt-1">
                      Routemize will use this to show you available times across all assignable users of this event.
                    </p>

                    {loadingTimeRanges ? (
                      <div className="flex items-center justify-center py-6">
                        <div className="w-5 h-5 border-2 border-ink-200 border-t-ink-600 rounded-full animate-spin" />
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {[...timeRangeOptions].sort((a, b) => {
                          const aFlex = isFlexiblePreferredTimeCode(a.code);
                          const bFlex = isFlexiblePreferredTimeCode(b.code);
                          if (aFlex && !bFlex) return 1;
                          if (!aFlex && bFlex) return -1;
                          const aMin = a.start ? a.start.split(':').map(Number).reduce((h, m) => h * 60 + m, 0) : 9999;
                          const bMin = b.start ? b.start.split(':').map(Number).reduce((h, m) => h * 60 + m, 0) : 9999;
                          return aMin - bMin;
                        }).map((option) => {
                          const isSelected = preferredTime === option.code;
                          
                          return (
                            // eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending
                            <button
                              key={option.code}
                              type="button"
                              onClick={() => {
                                setPreferredTime(option.code);
                                // Clear the required-preferred-time error once a
                                // selection is made (mirrors the lead source flow).
                                if (errors.preferredTime) {
                                  setErrors((prev) => {
                                    const next = { ...prev };
                                    delete next.preferredTime;
                                    return next;
                                  });
                                }
                                // Nudge the lead source picker into view so users
                                // notice it's still required before submitting.
                                if (showLeadSourceField && leadSourceSectionRef.current) {
                                  // Defer to next frame so the layout settles
                                  // (e.g. selected styles applied) before scrolling.
                                  requestAnimationFrame(() => {
                                    leadSourceSectionRef.current?.scrollIntoView({
                                      behavior: 'smooth',
                                      block: 'center',
                                    });
                                  });
                                }
                              }}
                              className={`
                                relative p-3 rounded-lg border transition-all duration-200 text-left
                                ${isSelected 
                                  ? 'border-ink-900 bg-ink-50' 
                                  : 'border-ink-200 hover:border-ink-300 hover:bg-ink-50/50'
                                }
                              `}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`
                                  w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all
                                  ${isSelected 
                                    ? 'border-ink-900 bg-ink-900' 
                                    : 'border-ink-300'
                                  }
                                `}>
                                  {isSelected && <Check className="h-2.5 w-2.5 text-white stroke-[3]" />}
                                </div>
                                <div>
                                  <p className={`text-sm font-medium ${isSelected ? 'text-ink-900' : 'text-ink-700'}`}>
                                    {option.text || option.code}
                                  </p>
                                  {option.helpText && (
                                    <p className="text-xs text-ink-500">{option.helpText}</p>
                                  )}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {errors.preferredTime && (
                      <p className="text-xs text-danger-600">{errors.preferredTime}</p>
                    )}
                  </div>

                  {/* Lead Source - shown when configured on the booking form.
                      Wrapped in a highlighted card so it doesn't get lost
                      below the calendar/time picker on smaller screens. */}
                  {showLeadSourceField && (
                    <div
                      ref={leadSourceSectionRef}
                      className={`space-y-3 rounded-xl border p-4 transition-colors ${
                        errors.leadSource
                          ? 'border-danger-300 bg-danger-50/40'
                          : selectedLeadSource
                            ? 'border-ink-200 bg-surface'
                            : 'border-amber-300 bg-amber-50/40'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <h4 className="text-sm font-semibold text-ink-900 flex items-center gap-2">
                          <span className={`inline-flex h-7 w-7 items-center justify-center rounded-lg ${
                            selectedLeadSource ? 'bg-ink-100 text-ink-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            <Users className="h-4 w-4" />
                          </span>
                          How Did They Hear About Us?
                          <span className="text-danger-500">*</span>
                        </h4>
                        {!selectedLeadSource && !loadingLeadSources && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-2xs font-semibold rounded-full uppercase tracking-wide">
                            Required
                          </span>
                        )}
                      </div>

                      {loadingLeadSources ? (
                        <div className="flex items-center justify-center py-6">
                          <div className="w-5 h-5 border-2 border-ink-200 border-t-ink-600 rounded-full animate-spin" />
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {availableLeadSources.map((source) => {
                            const isSelected = selectedLeadSource === source.code;
                            const isInternal = isInternalLeadSource(source.helpText);
                            const displayHelpText = stripInternalPrefix(source.helpText);

                            return (
                              // eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending
                              <button
                                key={source.code}
                                type="button"
                                onClick={() => setSelectedLeadSource(source.code || '')}
                                className={`
                                  relative p-3 rounded-lg border transition-all duration-200 text-left
                                  ${isSelected
                                    ? 'border-ink-900 bg-ink-50'
                                    : 'border-ink-200 bg-surface hover:border-ink-300 hover:bg-ink-50/50'
                                  }
                                `}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`
                                    w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0
                                    ${isSelected
                                      ? 'border-ink-900 bg-ink-900'
                                      : 'border-ink-300'
                                    }
                                  `}>
                                    {isSelected && <Check className="h-2.5 w-2.5 text-white stroke-[3]" />}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <p className={`text-sm font-medium ${isSelected ? 'text-ink-900' : 'text-ink-700'}`}>
                                        {source.text || source.code}
                                      </p>
                                      {isInternal && (
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-50 text-amber-600 text-2xs font-medium rounded border border-amber-200 flex-shrink-0">
                                          <EyeOff className="h-2.5 w-2.5" />
                                          Internal
                                        </span>
                                      )}
                                    </div>
                                    {displayHelpText && (
                                      <p className="text-xs text-ink-500 mt-0.5">{displayHelpText}</p>
                                    )}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {errors.leadSource && (
                        <p className="text-xs text-danger-600">{errors.leadSource}</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Loading State — multi-phase "Slot Discovery" animation. Plays
                while GetSlots is in flight (phases 1-4) and once the slots
                arrive the counter ticks up to the total. The booking view is
                gated by the animation's `onComplete` via `applyPendingSlots`,
                mirroring the way `BulkRouteModal` gates its review step. */}
            {isRoutemizing && !showAvailableSlots && (
              <div className="p-6">
                <SlotDiscoveryAnimation
                  slotsFound={pendingSlotsData?.slots.length ?? null}
                  onComplete={applyPendingSlots}
                />
              </div>
            )}

            {/* No Availability - Graceful empty state when assigned user/team is fully booked */}
            {noAvailabilityInfo?.isFull && (
              <div className="p-6 flex items-center justify-center" style={{ minHeight: '400px' }}>
                <div className="w-full max-w-md mx-auto">
                  <div className="text-center space-y-6">
                    <div className="flex justify-center">
                      <div className="w-20 h-20 rounded-full bg-amber-50 border-2 border-amber-200 flex items-center justify-center">
                        {isOutsideServiceArea ? (
                          <MapPin className="h-10 w-10 text-amber-500" />
                        ) : (
                          <Calendar className="h-10 w-10 text-amber-500" />
                        )}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h3 className="text-xl font-bold text-ink-900">
                        {noAvailabilityInfo.afterServiceAreaOverride
                          ? 'No slots available'
                          : isOutsideServiceArea
                            ? 'Outside Service Area'
                            : 'No Availability'}
                      </h3>
                      <p className="text-ink-600 text-sm leading-relaxed">
                        {noAvailabilityInfo.afterServiceAreaOverride
                          ? 'We searched beyond your service area, but there are no open slots for the requested time. Try a different day or time window, or assign another team member if available. This request stays flagged as outside service area on the dashboard.'
                          : isOutsideServiceArea
                            ? (noAvailabilityInfo.detail || 'This address is beyond your configured service radius, so no slots were returned.')
                            : noAvailabilityInfo.userName
                              ? `${noAvailabilityInfo.userName} is fully booked for ${noAvailabilityInfo.dateLabel} during ${noAvailabilityInfo.timeLabel}.`
                              : `There are no open slots for ${noAvailabilityInfo.dateLabel} during ${noAvailabilityInfo.timeLabel}.`}
                      </p>
                      {isOutsideServiceArea && !noAvailabilityInfo.afterServiceAreaOverride && (
                        <p className="text-ink-500 text-xs leading-relaxed">
                          You can still adjust preferences and try again — the request is flagged as outside service area on the dashboard.
                        </p>
                      )}
                    </div>

                    <div className="bg-ink-50 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-lg bg-ink-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Clock className="h-4 w-4 text-ink-500" />
                        </div>
                        <div className="text-left">
                          <p className="text-xs font-medium text-ink-500 uppercase tracking-wide mb-1">Requested</p>
                          <p className="text-sm text-ink-700">
                            {noAvailabilityInfo.dateLabel} · {noAvailabilityInfo.timeLabel}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div className="text-left">
                          <p className="text-sm text-blue-800 font-medium mb-1">Try a different time</p>
                          <p className="text-xs text-blue-700">
                            Pick another day or widen the time window to see more options. You can also assign a different
                            team member if available.
                          </p>
                        </div>
                      </div>
                    </div>

                    {!isOutsideServiceArea && noAvailabilityInfo.detail && !isRawTechnicalError(noAvailabilityInfo.detail) && (
                      <p className="text-xs text-ink-400 leading-relaxed">{noAvailabilityInfo.detail}</p>
                    )}

                    <div className="space-y-3 pt-2">
                      {/* Service-area override — only shown for outside-area cases.
                          Lets the operator force-generate slots ignoring the
                          configured radius. The request stays flagged as out-of-area. */}
                      {isOutsideServiceArea && !noAvailabilityInfo.afterServiceAreaOverride && (
                        // eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending
                        <button
                          onClick={handleBookAnyway}
                          disabled={isRoutemizing}
                          className="w-full bg-ink-900 text-white py-3.5 px-6 rounded-xl hover:bg-ink-800 transition-all font-medium flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <MapPin className="h-5 w-5" />
                          <span>{isRoutemizing ? 'Finding slots…' : 'Book Anyway (Override)'}</span>
                        </button>
                      )}

                      {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                      <button
                        onClick={() => {
                          setNoAvailabilityInfo(null);
                          setShowAvailableSlots(false);
                          setAvailableSlots([]);
                          setSelectedSlot(null);
                          setIsRoutemizing(false);
                          setErrors({});
                          setCurrentStep('preferences');
                          setUserChangedPreferences(true);
                          setCompletedSteps(prev => prev.filter(s => s !== 'preferences' && s !== 'booking'));
                        }}
                        disabled={isRoutemizing}
                        className={`w-full py-3.5 px-6 rounded-xl transition-all font-medium flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed ${
                          isOutsideServiceArea
                            ? 'bg-surface text-ink-700 border border-ink-200 hover:bg-ink-50'
                            : 'bg-ink-900 text-white hover:bg-ink-800'
                        }`}
                      >
                        <Calendar className="h-5 w-5" />
                        <span>Try a Different Time</span>
                      </button>

                      {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                      <button
                        onClick={() => {
                          setNoAvailabilityInfo(null);
                          handleClose();
                        }}
                        className="w-full text-ink-500 py-2.5 px-6 hover:text-ink-700 transition-all font-medium flex items-center justify-center gap-2"
                      >
                        <span>Cancel</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Available Slots - Show on booking step */}
            {currentStep === 'booking' && showAvailableSlots && !noAvailabilityInfo?.isFull && (
              <div className="flex flex-col h-full">
                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto px-4 md:px-8 py-4 md:py-5">
                  {/* Outside service area banner — non-blocking. Staff can still book,
                      but the booking is flagged so they know it's beyond the configured
                      service radius before confirming. */}
                  {isOutsideServiceArea && (
                    <div className="mb-4 max-w-2xl mx-auto rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-4 w-4 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-amber-900">Outside service area</p>
                        <p className="text-xs text-amber-800 leading-relaxed mt-0.5">
                          {outsideServiceAreaDetail || 'This address is beyond your configured service radius.'}
                        </p>
                        <p className="text-[11px] text-amber-700/80 mt-1">
                          You can still book this appointment — the request will be flagged as outside service area on the dashboard.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Customer context — a single, calm breadcrumb. Everything an admin needs
                      to confirm "yes, scheduling the right person on the right day" without
                      pulling the eye away from the recommendations below. */}
                  <div className="mb-4 flex items-center justify-center">
                    <div className="inline-flex items-center gap-2.5 rounded-full border border-ink-200/80 bg-surface px-3 py-1 text-[12px] shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
                      <span className="flex items-center gap-1.5 text-ink-500">
                        <User className="h-3 w-3 text-ink-400" />
                        <span className="font-medium text-ink-800">{firstName} {lastName}</span>
                      </span>
                      <span className="h-3 w-px bg-ink-200" />
                      {/* Explicitly frame the day/time as what the customer asked for on
                          their request, so an off-preference Top Choice below reads as a
                          deliberate trade-off rather than an arbitrary suggestion. */}
                      <span className="flex items-center gap-1.5 text-ink-500">
                        <span className="rounded bg-ink-100 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.05em] text-ink-500">
                          Requested
                        </span>
                        <Calendar className="h-3 w-3 text-ink-400" />
                        <span className="font-medium text-ink-800">{customerPreferredDateLabel}</span>
                        <span className="text-ink-300">·</span>
                        <Clock className="h-3 w-3 text-ink-400" />
                        <span className="font-medium text-ink-800">{customerPreferredTimeLabel}</span>
                      </span>
                    </div>
                  </div>

                  {/* The escape hatch for "but I know that day is open". The
                      picker below only ever shows what IS bookable, so a day
                      the engine skipped looks like a bug from here — this hands
                      the question to Routy, which can name the real blocker
                      and clear it. */}
                  {/* Deliberately loud: operators who need this are already
                      convinced the product is wrong, and a quiet grey link
                      never gets found. */}
                  {isRoutyAvailable && (
                    <div className="mb-4">
                      {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                      <button
                        type="button"
                        onClick={() => setShowAvailabilityHelp(true)}
                        className="group flex w-full items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50/50 px-3.5 py-3 text-left transition-colors hover:border-indigo-300 hover:bg-indigo-50"
                      >
                        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-ink-950">
                          <RoutyMark className="h-7 w-7" bgClassName="bg-transparent" textClassName="text-sm" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-semibold text-ink-900">
                            Don't see the time you need?
                          </span>
                          <span className="block text-xs text-indigo-700">
                            Ask Routy what's blocking that day — and clear it.
                          </span>
                        </span>
                        <ChevronRight className="h-4 w-4 flex-shrink-0 text-indigo-400 transition-transform group-hover:translate-x-0.5" />
                      </button>
                    </div>
                  )}

                {availableSlots.length === 0 ? (
                  <div className="text-center py-12 max-w-md mx-auto">
                    <div className="w-16 h-16 mx-auto mb-4 bg-amber-50 border border-amber-200 rounded-full flex items-center justify-center">
                      <Calendar className="h-8 w-8 text-amber-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-ink-900 mb-2">No slots available</h3>
                    <p className="text-sm text-ink-600 leading-relaxed">
                      {isOutsideServiceArea
                        ? 'There are no open times for this request, even when searching outside your service area. Try a different day or time window.'
                        : 'There are no open times for the selected preferences. Try a different day or time window.'}
                    </p>
                    {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                    <button
                      type="button"
                      onClick={() => {
                        setShowAvailableSlots(false);
                        setAvailableSlots([]);
                        setSelectedSlot(null);
                        setNoAvailabilityInfo(null);
                        setIsRoutemizing(false);
                        setCurrentStep('preferences');
                        setUserChangedPreferences(true);
                        setCompletedSteps((prev) => prev.filter((s) => s !== 'preferences' && s !== 'booking'));
                      }}
                      className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-ink-800 transition-colors"
                    >
                      <Calendar className="h-4 w-4" />
                      Try a Different Time
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4 max-w-2xl mx-auto">
                    {/* SLOT 1: Best for Business (index 0) */}
                    {availableSlots[0] && (() => {
                      const slot = availableSlots[0];
                      const isSelected = selectedSlot?.id === slot.id;
                      const isExpanded = !!slotDetailsExpanded['business'];

                      const slotDate = slot.suggestedDate ? new Date(slot.suggestedDate) : new Date();
                      const formattedDate = slotDate.toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      });
                      const fullAddress = [slot.addressLine1, slot.addressLine2, slot.city, slot.state, slot.zipCode]
                        .filter(Boolean).join(', ') || 'Address not available';
                      const slotAssignee = resolveSlotAssignee(slot);
                      const insights = generateSlotInsights(slot, 'business');
                      const primaryReason = insights[0] || null;
                      // The eyebrow strip above already shows primaryReason, so the
                      // expanded panel only needs the *additional* reasons. This
                      // keeps the user from reading the same line twice.
                      const extraInsights = primaryReason ? insights.slice(1) : insights;
                      const topPrevLegResolved = resolvePrevLeg(slot);
                      const topNextLegResolved = resolveNextLeg(slot);
                      const hasRouteData = topPrevLegResolved.minutes != null
                        || topNextLegResolved.minutes != null;

                      // Preference matching state for Top Choice
                      const topMatchesDate = doesSlotMatchPreferredDate(slot);
                      const topMatchesTime = doesSlotMatchPreferredTimeWindow(slot);
                      const hasPreferenceContext = !!effectivePreferredDate || !!effectivePreferredTime;
                      const topMissesPreference = hasPreferenceContext && (!topMatchesDate || !topMatchesTime);

                      // "Also closest by drive time" — only honest if the Top Choice's total
                      // drive time really is the lowest among all the other suggested slots.
                      // We can't infer this from `!shouldShowClosestNext` alone, because the
                      // dedicated Proximity card also gets suppressed when the *Customer
                      // Preference* slot owns the proximity win — which would otherwise
                      // falsely credit the Top Choice with the lowest drive time.
                      const topTotalDriveMin = (topPrevLegResolved.minutes || 0)
                        + (topNextLegResolved.minutes || 0);
                      const otherSlotsDriveTotals = availableSlots
                        .filter((s) => !doSlotsMatchExactly(s, slot))
                        .map((s) => (resolvePrevLeg(s).minutes || 0)
                          + (resolveNextLeg(s).minutes || 0))
                        .filter((total) => total > 0);
                      const topActuallyHasLowestDrive = topTotalDriveMin > 0
                        && otherSlotsDriveTotals.length > 0
                        && otherSlotsDriveTotals.every((total) => total > topTotalDriveMin);
                      const topAlsoClosest = topActuallyHasLowestDrive
                        && availableSlots.length > 1
                        && (topPrevLegResolved.minutes != null
                          || topNextLegResolved.minutes != null);

                      // Detect the "anchor" case: when the engine picks an early slot on a
                      // day with no (or no earlier) work, the win isn't proximity — it's
                      // that this slot will *become* the anchor that future bookings cluster
                      // around. Calling that out explicitly stops the Top Choice from looking
                      // arbitrary on otherwise-empty days.
                      const topPrevLoc = (slot.previousAppointmentLocation || '').toLowerCase();
                      const topNextLoc = (slot.nextAppointmentLocation || '').toLowerCase();
                      const topPrevIsHome = isPrevStartOfDay(slot)
                        || topPrevLoc.includes('home')
                        || topPrevLoc.includes('start')
                        || topPrevLoc.includes('workday');
                      const topNextIsHome = isNextEndOfDay(slot)
                        || topNextLoc.includes('home')
                        || topNextLoc.includes('end of day')
                        || topNextLoc.includes('return to home')
                        || topNextLoc.includes('ending');
                      const topIsFirstOfDay = topPrevIsHome
                        && (topPrevLegResolved.minutes != null
                          || topNextLegResolved.minutes != null);
                      const topIsOnlyOfDay = topIsFirstOfDay && topNextIsHome;

                      // The "anchor" framing is a genuinely useful routing signal even when
                      // the slot misses the customer's stated preference. In that case the
                      // single `topQualifier` chip is claimed by the preference note, so we
                      // surface the anchor as its own chip alongside it — mirroring the
                      // mobile app, which always shows "Anchors the day" on the Top Choice.
                      const showSeparateAnchorBadge = topIsFirstOfDay && topMissesPreference;

                      const hasMoreDetails = extraInsights.length > 0 || hasRouteData || topMissesPreference || topIsFirstOfDay || topAlsoClosest;

                      // Surface the actual distance to the nearest neighbor — a real
                      // appointment or the day's home/office start/end — so dispatchers
                      // can spot "this is also very close to existing work / home".
                      const topNeighbor = neighborForSlot(slot);
                      const topClosestMiles = topNeighbor?.miles ?? null;
                      const topProximityTier = getProximityTier(topClosestMiles);
                      const topProximityDistance = topNeighbor
                        ? formatDistance(topNeighbor.miles, distUnit)
                        : null;
                      const topProximityPhrase = topNeighbor
                        ? proximityBadgePhrase(topNeighbor)
                        : 'away';
                      const topProximityLabel = topNeighbor && topProximityDistance
                        ? (topNeighbor.kind === 'appointment'
                          ? `${topProximityDistance} from nearest stop`
                          : `${topProximityDistance} ${topProximityPhrase}`)
                        : null;

                      // Pick the single most decision-relevant secondary chip — instead of
                      // stacking 3-4 of them, we surface one signal so the eye lands on the
                      // time first, then on a single qualifier. The rest live in "Details".
                      const topQualifier: { icon: React.ReactNode; label: string; tone: string } | null = topMissesPreference
                        ? {
                            icon: <AlertCircle className="h-3 w-3" />,
                            label: !topMatchesDate && !topMatchesTime
                              ? 'Off customer preference'
                              : !topMatchesDate
                                ? 'Different day than requested'
                                : 'Outside requested time window',
                            tone: 'text-amber-700 bg-amber-50 border border-amber-100',
                          }
                        : topIsFirstOfDay
                          ? {
                              icon: <Sunrise className="h-3 w-3" />,
                              label: topIsOnlyOfDay ? 'Anchors the day' : 'First stop of the day',
                              tone: 'text-indigo-700 bg-indigo-50 border border-indigo-100',
                            }
                          : topAlsoClosest
                            ? {
                                icon: <Navigation2 className="h-3 w-3" />,
                                label: 'Also closest by drive time',
                                tone: 'text-info-700 bg-info-50 border border-info-100',
                              }
                            : (topProximityTier && topProximityLabel
                                ? {
                                    icon: <Navigation2 className="h-3 w-3" />,
                                    label: topProximityLabel,
                                    tone: topProximityTier.badgeClass,
                                  }
                                : null);

                      // Route-quality tier. A high score (>= 85) is the
                      // dominant positive signal we want to celebrate so the
                      // user feels good about picking this slot — it visually
                      // outranks the amber "off customer preference" cue,
                      // which is just an awareness note.
                      const routeScoreRounded = slot.routeScore != null
                        ? Math.round(slot.routeScore)
                        : null;
                      const isExcellentRoute = routeScoreRounded != null && routeScoreRounded >= 85;
                      const isStrongRoute = routeScoreRounded != null && routeScoreRounded >= 70 && routeScoreRounded < 85;

                      return (
                        <div key={slot.id}>
                          <div
                            onClick={() => handleSlotSelect(slot)}
                            className={`
                              group relative rounded-2xl cursor-pointer transition-all duration-200 overflow-hidden bg-surface
                              ${isSelected
                                ? 'ring-2 ring-ink-900 ring-offset-2 ring-offset-white shadow-[0_8px_24px_-12px_rgba(15,23,42,0.25)]'
                                : isExcellentRoute
                                  ? 'border border-emerald-200 hover:border-emerald-300 hover:shadow-[0_6px_20px_-12px_rgba(16,185,129,0.35)]'
                                  : topMissesPreference
                                    ? 'border border-amber-200 hover:border-amber-300 hover:shadow-[0_6px_20px_-12px_rgba(217,119,6,0.35)]'
                                    : 'border border-ink-200 hover:border-ink-300 hover:shadow-[0_6px_20px_-12px_rgba(15,23,42,0.25)]'
                              }
                            `}
                          >
                            {/* Hero accent rail along the left edge — quietly anchors the
                                Top Choice as the visually heavier card. An excellent
                                route score promotes this to emerald even when the
                                slot is off the customer's stated preference, since
                                the routing win is the main reason to pick it. */}
                            <div className={`absolute inset-y-0 left-0 w-1 ${
                              isExcellentRoute
                                ? 'bg-emerald-500'
                                : topMissesPreference
                                  ? 'bg-amber-400'
                                  : 'bg-ink-900'
                            }`} />

                            {/* Eyebrow strip — "Recommended" wordmark + a calm pulse dot.
                                This is the 2-second cue: dark text on white, no neon. */}
                            <div className="px-5 pt-4 pb-1 flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="relative flex h-2 w-2 flex-shrink-0">
                                  <span className={`absolute inline-flex h-full w-full rounded-full ${
                                    !isExcellentRoute && topMissesPreference ? 'bg-amber-400' : 'bg-emerald-400'
                                  } opacity-60 animate-ping`} />
                                  <span className={`relative inline-flex h-2 w-2 rounded-full ${
                                    !isExcellentRoute && topMissesPreference ? 'bg-amber-500' : 'bg-emerald-500'
                                  }`} />
                                </span>
                                <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-900">
                                  Top Choice
                                </span>
                                <span className="text-[11px] text-ink-400 truncate">
                                  {isExcellentRoute
                                    ? (topMissesPreference ? 'excellent route' : 'best fit overall')
                                    : (topMissesPreference ? 'best for routing' : 'best fit overall')}
                                </span>
                                {topProximityTier && topNeighbor && topProximityDistance && (
                                  <span
                                    className={`text-[10px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-1 flex-shrink-0 ${topProximityTier.badgeClass}`}
                                    title={proximityBadgeTitle(topNeighbor, topProximityDistance)}
                                  >
                                    <Navigation2 className="h-2.5 w-2.5" /> {topProximityDistance} {topProximityPhrase}
                                  </span>
                                )}
                                {renderDetourBadge(slot)}
                              </div>
                              {routeScoreRounded != null && (
                                isExcellentRoute ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-semibold text-emerald-700 tabular-nums">
                                    <Sparkles className="h-3 w-3 text-emerald-500" />
                                    <span>Excellent route</span>
                                    <span className="text-emerald-600/70 font-medium">· {routeScoreRounded}/100</span>
                                  </span>
                                ) : isStrongRoute ? (
                                  <span className="hidden sm:inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 tabular-nums">
                                    Route <span className="font-semibold">{routeScoreRounded}</span><span className="text-emerald-500/70">/100</span>
                                  </span>
                                ) : (
                                  <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-medium text-ink-400 tabular-nums">
                                    Route <span className="text-ink-700 font-semibold">{routeScoreRounded}</span><span className="text-ink-400">/100</span>
                                  </span>
                                )
                              )}
                            </div>

                            {/* Date + time hero — the weekday is as scannable as the clock,
                                since Top Choice is often a different day than requested. */}
                            <div className="px-5 pt-2 pb-4 flex items-end gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2 flex-wrap mb-1.5">
                                  <span className="text-xl sm:text-[22px] leading-none font-bold text-ink-900 tracking-tight">
                                    {formatWeekdayLong(slotDate)}
                                  </span>
                                  <span className="text-base sm:text-lg leading-none font-semibold text-ink-600">
                                    {formatMonthDayLong(slotDate)}
                                  </span>
                                  {isCalendarToday(slot.suggestedDate) && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-ink-900 text-white self-center">
                                      Today
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-baseline gap-2 flex-wrap">
                                  <span className="text-3xl sm:text-[34px] leading-none font-bold text-ink-900 tracking-tight tabular-nums">
                                    {getDisplaySlotTime(slot.startTime)}
                                  </span>
                                  <span className="text-ink-300 text-2xl leading-none">–</span>
                                  <span className="text-xl leading-none font-medium text-ink-500 tabular-nums">
                                    {getDisplaySlotTime(slot.endTime)}
                                  </span>
                                </div>
                                <div className="mt-3 flex items-center gap-2 text-[12.5px] text-ink-600 min-w-0">
                                  <AssigneeChip assignee={slotAssignee} size="sm" className="flex-shrink-0" />
                                  <span className="h-1 w-1 rounded-full bg-ink-300 flex-shrink-0" />
                                  <span className="flex items-center gap-1.5 truncate min-w-0">
                                    <MapPin className="h-3.5 w-3.5 text-ink-400 flex-shrink-0" />
                                    <span className="truncate text-ink-500">{fullAddress}</span>
                                  </span>
                                </div>
                              </div>

                              {/* Selection indicator — large, deliberate, on the right where
                                  the eye lands after reading the time. */}
                              <div
                                className={`
                                  w-9 h-9 rounded-full flex items-center justify-center transition-all flex-shrink-0
                                  ${isSelected
                                    ? 'bg-ink-900 text-white shadow-md'
                                    : 'bg-surface border-2 border-ink-200 text-transparent group-hover:border-ink-400'
                                  }
                                `}
                              >
                                <Check className="h-4 w-4 stroke-[3]" />
                              </div>
                            </div>

                            {/* Why-this-pick strip — a single, calm justification.
                                Communicates the trade-off without forcing a click into Details. */}
                            {(primaryReason || topQualifier || showSeparateAnchorBadge) && (
                              <div className="mx-5 mb-4 px-3 py-2 rounded-lg bg-ink-50 border border-ink-100 flex items-center gap-2 flex-wrap">
                                {topQualifier && (
                                  <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded ${topQualifier.tone}`}>
                                    {topQualifier.icon}
                                    {topQualifier.label}
                                  </span>
                                )}
                                {showSeparateAnchorBadge && (
                                  <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded text-indigo-700 bg-indigo-50 border border-indigo-100">
                                    <Sunrise className="h-3 w-3" />
                                    {topIsOnlyOfDay ? 'Anchors the day' : 'First stop of the day'}
                                  </span>
                                )}
                                {primaryReason && (
                                  <span className="text-[12px] text-ink-700 font-medium">
                                    {primaryReason}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Footer actions — kept subdued; admin shouldn't have to think
                                about these unless they specifically want to validate. */}
                            <div className="px-5 pb-4 flex items-center justify-end gap-1">
                              {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openAuditModal(slot, 'business');
                                }}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-ink-500 hover:text-ink-900 hover:bg-ink-100 rounded-md transition-colors"
                                title="Preview this slot on the timeline"
                              >
                                <Eye className="h-3 w-3" />
                                Preview timeline
                              </button>
                              {hasMoreDetails && (
                                // eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending
                                <button
                                  onClick={(e) => toggleSlotDetails('business', e)}
                                  className="inline-flex items-center gap-0.5 px-2 py-1 text-[11px] font-medium text-ink-500 hover:text-ink-900 hover:bg-ink-100 rounded-md transition-colors"
                                  title={isExpanded ? 'Hide details' : 'Why this is the top pick'}
                                >
                                  {isExpanded ? 'Hide reasoning' : 'Why this pick'}
                                  <ChevronRight className={`h-3 w-3 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                                </button>
                              )}
                            </div>

                            {isExpanded && hasMoreDetails && (
                              <div onClick={(e) => e.stopPropagation()} className="px-5 pb-5 border-t border-ink-100 pt-4 space-y-3">
                                {/* Section eyebrow — anchors the panel so the
                                    user knows at a glance what they're reading. */}
                                <div className="flex items-center gap-2">
                                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-400">
                                    Why we picked this
                                  </span>
                                  <span className="flex-1 h-px bg-ink-100" />
                                </div>

                                {/* Trade-off — the most actionable insight when
                                    the slot diverges from what the customer
                                    asked for. Title is short and specific so it
                                    scans in one glance; the eyebrow strip
                                    already conveys "best for routing" context. */}
                                {topMissesPreference && (
                                  <div className="px-3 py-2.5 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-2.5">
                                    <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-[12px] font-semibold text-amber-900 leading-tight">
                                        Doesn't match what the customer asked for
                                      </p>
                                      <p className="text-[11.5px] text-amber-800/90 mt-1 leading-snug">
                                        They requested{' '}
                                        <span className="font-semibold text-amber-900">{customerPreferredDateLabel}</span>
                                        {customerPreferredTimeLabel ? (
                                          <>
                                            {' · '}
                                            <span className="font-semibold text-amber-900">{customerPreferredTimeLabel}</span>
                                          </>
                                        ) : null}
                                        {!topMatchesDate ? (
                                          <>
                                            {'. This slot is on '}
                                            <span className="font-semibold text-amber-900">{formattedDate}</span>
                                          </>
                                        ) : '.'}
                                        {!topMatchesDate && !topMatchesTime ? ' and' : ''}
                                        {!topMatchesTime ? ' falls outside that window' : ''}.
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {/* Unified positive-reasons list — replaces the
                                    old indigo "anchor day" callout and the
                                    green "Why this slot" bullets. Each row's
                                    icon color tells you what kind of reason
                                    it is (anchor / generic insight / proximity)
                                    without forcing three separate boxes. The
                                    primary reason is intentionally omitted
                                    here because it's already shown in the
                                    eyebrow strip above the panel. */}
                                {(topIsFirstOfDay || extraInsights.length > 0 || topAlsoClosest) && (
                                  <div className="px-3 py-2.5 bg-success-50/40 rounded-lg border border-success-100">
                                    <div className="flex items-center gap-1.5 mb-2">
                                      <Sparkles className="h-3 w-3 text-success-600" />
                                      <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-success-800/80">
                                        Why this works
                                      </span>
                                    </div>
                                    <ul className="space-y-1.5">
                                      {topIsFirstOfDay && (
                                        <li className="text-[12px] text-ink-700 flex items-start gap-2 leading-snug">
                                          <Sunrise className="h-3.5 w-3.5 text-indigo-500 mt-0.5 flex-shrink-0" />
                                          <span>
                                            <span className="font-medium text-ink-900">
                                              {topIsOnlyOfDay ? 'Anchors an otherwise-empty day. ' : 'First appointment of the day. '}
                                            </span>
                                            <span className="text-ink-600">
                                              {topIsOnlyOfDay
                                                ? 'Future bookings will route around this stop for tighter, more efficient days.'
                                                : 'Sets the early anchor; later bookings can slot around it to keep drive time down.'}
                                            </span>
                                          </span>
                                        </li>
                                      )}
                                      {extraInsights.map((insight, i) => (
                                        <li key={i} className="text-[12px] text-ink-700 flex items-start gap-2 leading-snug">
                                          <Check className="h-3.5 w-3.5 text-success-500 mt-0.5 flex-shrink-0" />
                                          <span>{insight}</span>
                                        </li>
                                      ))}
                                      {topAlsoClosest && (
                                        <li className="text-[12px] text-ink-700 flex items-start gap-2 leading-snug">
                                          <Navigation2 className="h-3.5 w-3.5 text-info-500 mt-0.5 flex-shrink-0" />
                                          <span>Lowest total drive time of all the suggested options.</span>
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                )}

                                {hasRouteData && (
                                  <div>
                                    <p className="text-[10.5px] font-semibold text-ink-400 uppercase tracking-[0.06em] flex items-center gap-1.5 mb-2">
                                      <Route className="h-3 w-3" /> Route Overview
                                    </p>
                                    {renderRouteOverviewMap(slot)}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* "Alternatives" section heading — separates the recommendation from
                        the trade-off picks below. Renders once if either alternative card
                        will render, so the heading never appears alone. */}
                    {((shouldShowClosestNext && closestSlotData)
                      || (customerPreferenceSlot && !isCustomerPreferenceDateStale)) && (
                      <div className="pt-2 pb-1 flex items-center gap-3">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-400">
                          Alternatives
                        </span>
                        <span className="flex-1 h-px bg-ink-100" />
                        <span className="text-[11px] text-ink-400">
                          Trade off something for something else
                        </span>
                      </div>
                    )}

                    {/* SLOT 2: Closest Next - Minimum Drive Time */}
                    {shouldShowClosestNext && closestSlotData && (() => {
                      const slot = closestSlotData.slot;
                      const isSelected = selectedSlot?.id === slot.id;
                      const isExpanded = !!slotDetailsExpanded['closest'];

                      const slotDate = slot.suggestedDate ? new Date(slot.suggestedDate) : new Date();
                      const formattedDate = slotDate.toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      });
                      const fullAddress = [slot.addressLine1, slot.addressLine2, slot.city, slot.state, slot.zipCode]
                        .filter(Boolean).join(', ') || 'Address not available';
                      const slotAssignee = resolveSlotAssignee(slot);
                      const closestPrevLeg = resolvePrevLeg(slot);
                      const closestNextLeg = resolveNextLeg(slot);
                      const driveTimeFrom = closestPrevLeg.minutes ? Math.round(closestPrevLeg.minutes) : null;
                      const driveTimeTo = closestNextLeg.minutes ? Math.round(closestNextLeg.minutes) : null;
                      const distanceFrom = closestPrevLeg.miles != null ? formatDistance(closestPrevLeg.miles, distUnit) : undefined;
                      const distanceTo = closestNextLeg.miles != null ? formatDistance(closestNextLeg.miles, distUnit) : undefined;
                      const closestInsights = generateSlotInsights(slot, 'closest');
                      const hasRouteData = driveTimeFrom !== null || driveTimeTo !== null;

                      // The "oh that's why" framing: how this slot beats the alternatives.
                      const closestTotalDrive = (closestPrevLeg.minutes || 0)
                        + (closestNextLeg.minutes || 0);
                      const topSlot = availableSlots[0];
                      const topTotalDrive = topSlot
                        ? (resolvePrevLeg(topSlot).minutes || 0)
                          + (resolveNextLeg(topSlot).minutes || 0)
                        : 0;
                      const driveSavingsVsTop = topTotalDrive > 0 && closestTotalDrive > 0
                        ? Math.round(topTotalDrive - closestTotalDrive)
                        : 0;
                      const prevLoc = (slot.previousAppointmentLocation || '').toLowerCase();
                      const nextLoc = (slot.nextAppointmentLocation || '').toLowerCase();
                      const prevIsHome = isPrevStartOfDay(slot) || prevLoc.includes('home') || prevLoc.includes('start') || prevLoc.includes('workday');
                      const nextIsHome = isNextEndOfDay(slot) || nextLoc.includes('home') || nextLoc.includes('end of day') || nextLoc.includes('return to home') || nextLoc.includes('ending');
                      const sandwichedBetweenJobs = !prevIsHome && !nextIsHome
                        && driveTimeFrom != null && driveTimeTo != null;
                      let proximityHook: string;
                      if (driveSavingsVsTop >= 10) {
                        proximityHook = `Saves ~${driveSavingsVsTop} min driving vs Top Choice`;
                      } else if (sandwichedBetweenJobs) {
                        proximityHook = `Tucked between two existing stops · ${driveTimeFrom}+${driveTimeTo} min total`;
                      } else if (driveTimeFrom != null && !prevIsHome && distanceFrom) {
                        proximityHook = `Right after another stop nearby · ${distanceFrom} / ${driveTimeFrom} min`;
                      } else if (driveTimeTo != null && !nextIsHome && distanceTo) {
                        proximityHook = `Right before another stop nearby · ${distanceTo} / ${driveTimeTo} min`;
                      } else {
                        proximityHook = `Lowest drive time available · ${driveTimeFrom ?? driveTimeTo ?? '—'} min · ${distanceFrom ?? distanceTo ?? '—'}`;
                      }
                      const hasMoreDetails = closestInsights.length > 0 || hasRouteData;

                      // Color reservations:
                      //   green  → Top Choice (company-best)
                      //   blue   → Proximity (route-efficiency pick)
                      //   pink   → Customer Preference
                      // The Proximity card itself stays solidly blue so the slot type is
                      // unambiguous at a glance. Distance tiering is surfaced through a
                      // separate small "X.X mi away" sub-badge below, where green/amber
                      // can highlight just-how-close without hijacking the slot identity.
                      const proximityNeighbor = neighborForSlot(slot);
                      const proximityClosestMiles = proximityNeighbor?.miles ?? null;
                      const proximityDistanceTier = getProximityTier(proximityClosestMiles);
                      const proximityDistanceLabel = proximityNeighbor
                        ? formatDistance(proximityNeighbor.miles, distUnit)
                        : null;
                      const proximityPhrase = proximityNeighbor
                        ? proximityBadgePhrase(proximityNeighbor)
                        : 'away';

                      return (
                        <div key={`closest-${slot.id}`}>
                          <div
                            onClick={() => handleSlotSelect(slot)}
                            className={`
                              group relative rounded-xl cursor-pointer transition-all duration-200 overflow-hidden bg-surface
                              ${isSelected
                                ? 'ring-2 ring-ink-900 ring-offset-2 ring-offset-white shadow-[0_8px_24px_-12px_rgba(15,23,42,0.25)]'
                                : 'border border-ink-200 hover:border-ink-300 hover:shadow-[0_4px_14px_-8px_rgba(15,23,42,0.18)]'
                              }
                            `}
                          >
                            {/* Slim accent rail keeps the slot type identifiable in peripheral vision. */}
                            <div className="absolute inset-y-0 left-0 w-0.5 bg-info-400" />

                            <div className="px-4 py-3.5 flex items-center gap-3.5">
                              {/* Selection radio — smaller than Top Choice's, signaling secondary weight. */}
                              <div className={`
                                w-7 h-7 rounded-full flex items-center justify-center transition-all flex-shrink-0
                                ${isSelected
                                  ? 'bg-ink-900 text-white'
                                  : 'bg-surface border-2 border-ink-200 text-transparent group-hover:border-ink-400'
                                }
                              `}>
                                <Check className="h-3.5 w-3.5 stroke-[3]" />
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-info-700 flex items-center gap-1">
                                    <Navigation2 className="h-3 w-3" /> Closest by drive
                                  </span>
                                  {proximityDistanceTier && proximityNeighbor && proximityDistanceLabel && (
                                    <span
                                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${proximityDistanceTier.badgeClass}`}
                                      title={proximityBadgeTitle(proximityNeighbor, proximityDistanceLabel)}
                                    >
                                      {proximityDistanceLabel} {proximityPhrase}
                                    </span>
                                  )}
                                  {renderDetourBadge(slot)}
                                </div>

                                <div className="flex items-baseline gap-2 flex-wrap mb-1">
                                  <span className="text-[15px] font-semibold text-ink-900 tabular-nums">
                                    {formattedDate}
                                  </span>
                                  {isCalendarToday(slot.suggestedDate) && (
                                    <span className="inline-flex items-center px-1.5 py-px rounded-full text-[9px] font-bold uppercase tracking-wide bg-ink-900 text-white self-center">
                                      Today
                                    </span>
                                  )}
                                  <span className="h-1 w-1 rounded-full bg-ink-300" />
                                  <span className="text-[19px] font-bold text-ink-900 tracking-tight tabular-nums leading-none">
                                    {getDisplaySlotTime(slot.startTime)}
                                  </span>
                                  <span className="text-ink-300 text-base leading-none">–</span>
                                  <span className="text-[13.5px] font-medium text-ink-500 tabular-nums leading-none">
                                    {getDisplaySlotTime(slot.endTime)}
                                  </span>
                                </div>

                                {/* Single, decisive trade-off line. The "why pick this instead". */}
                                <div className="text-[12px] text-info-700 font-medium truncate">
                                  {proximityHook}
                                </div>

                                <div className="flex items-center gap-2 mt-1 text-[11.5px] text-ink-500 min-w-0">
                                  <AssigneeChip assignee={slotAssignee} className="flex-shrink-0" />
                                  <span className="h-0.5 w-0.5 rounded-full bg-ink-300 flex-shrink-0" />
                                  <span className="flex items-center gap-1 truncate min-w-0">
                                    <MapPin className="h-3 w-3 text-ink-400 flex-shrink-0" />
                                    <span className="truncate text-ink-500">{fullAddress}</span>
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center gap-0.5 flex-shrink-0 self-start">
                                {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openAuditModal(slot, 'closest');
                                  }}
                                  className="p-1.5 text-ink-400 hover:text-ink-900 hover:bg-ink-100 rounded-md transition-colors"
                                  title="Preview this slot on the timeline"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                                {hasMoreDetails && (
                                  // eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending
                                  <button
                                    onClick={(e) => toggleSlotDetails('closest', e)}
                                    className="px-2 py-1 text-[11px] font-medium text-ink-500 hover:text-ink-900 hover:bg-ink-100 rounded-md transition-colors flex items-center gap-0.5"
                                    title={isExpanded ? 'Hide details' : 'Why this pick'}
                                  >
                                    {isExpanded ? 'Hide' : 'Why'}
                                    <ChevronRight className={`h-3 w-3 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                                  </button>
                                )}
                              </div>
                            </div>

                            {isExpanded && hasMoreDetails && (
                              <div onClick={(e) => e.stopPropagation()} className="px-4 pb-4 border-t border-ink-100 pt-4 space-y-3">
                                {/* Section eyebrow — same anchor used on Top Choice
                                    and Customer Preference so the three expanded
                                    panels feel like part of the same family. */}
                                <div className="flex items-center gap-2">
                                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-400">
                                    Why we picked this
                                  </span>
                                  <span className="flex-1 h-px bg-ink-100" />
                                </div>

                                {(driveSavingsVsTop >= 5 || closestInsights.length > 0) && (
                                  <div className="px-3 py-2.5 bg-info-50/50 rounded-lg border border-info-100">
                                    <div className="flex items-center gap-1.5 mb-2">
                                      <Navigation2 className="h-3 w-3 text-info-600" />
                                      <span className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-info-700">
                                        Why this works
                                      </span>
                                    </div>
                                    <ul className="space-y-1.5">
                                      {driveSavingsVsTop >= 5 && (
                                        <li className="text-[12px] text-ink-700 flex items-start gap-2 leading-snug">
                                          <Navigation2 className="h-3.5 w-3.5 text-info-500 mt-0.5 flex-shrink-0" />
                                          <span>
                                            <span className="font-medium text-ink-900">Saves about {driveSavingsVsTop} min of driving vs the Top Choice. </span>
                                            <span className="text-ink-600 tabular-nums">{closestTotalDrive} min vs {topTotalDrive} min total.</span>
                                          </span>
                                        </li>
                                      )}
                                      {closestInsights.map((insight, i) => (
                                        <li key={i} className="text-[12px] text-ink-700 flex items-start gap-2 leading-snug">
                                          <Check className="h-3.5 w-3.5 text-success-500 mt-0.5 flex-shrink-0" />
                                          <span>{insight}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {hasRouteData && (
                                  <div>
                                    <p className="text-[10.5px] font-semibold text-ink-400 uppercase tracking-[0.06em] flex items-center gap-1.5 mb-2">
                                      <Route className="h-3 w-3" /> Route Overview
                                    </p>
                                    {renderRouteOverviewMap(slot)}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}

                    {/* SLOT 3: Customer Preference (index 1) - Only show if preference date is not stale */}
                    {customerPreferenceSlot && !isCustomerPreferenceDateStale && (() => {
                      const slot = customerPreferenceSlot;
                      const isSelected = selectedSlot?.id === slot.id;
                      const isExpanded = !!slotDetailsExpanded['preference'];

                      const slotDate = slot.suggestedDate ? new Date(slot.suggestedDate) : new Date();
                      const formattedDate = slotDate.toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      });
                      const fullAddress = [slot.addressLine1, slot.addressLine2, slot.city, slot.state, slot.zipCode]
                        .filter(Boolean).join(', ') || 'Address not available';
                      const slotAssignee = resolveSlotAssignee(slot);
                      const prefInsights = generateSlotInsights(slot, 'preference');
                      const prefDateMatches = doesSlotMatchPreferredDate(slot);
                      const prefTimeMatches = doesSlotMatchPreferredTimeWindow(slot);
                      const prefPrevLegResolved = resolvePrevLeg(slot);
                      const prefNextLegResolved = resolveNextLeg(slot);
                      const hasRouteData = prefPrevLegResolved.minutes != null
                        || prefNextLegResolved.minutes != null;
                      const showStaleWarning = !prefDateMatches && !!effectivePreferredDate;

                      // Why is THIS the chosen preference slot? Compare against all other
                      // slots that also match the customer's date & time window.
                      const allPrefMatches = availableSlots.filter(
                        (s) => doesSlotMatchPreferredDate(s) && doesSlotMatchPreferredTimeWindow(s)
                      );
                      const totalPrefMatches = allPrefMatches.length;
                      // Compute drive/route stats relative to the OTHER preference matches
                      // (exclude this slot itself so a 1-slot tie doesn't count as a "win").
                      const otherPrefMatches = allPrefMatches.filter((s) => !isSameSlot(s, slot));
                      const slotTotalDrive = (prefPrevLegResolved.minutes || 0)
                        + (prefNextLegResolved.minutes || 0);
                      const otherDriveTotals = otherPrefMatches.map((s) =>
                        (resolvePrevLeg(s).minutes || 0)
                        + (resolveNextLeg(s).minutes || 0)
                      );
                      const otherRouteScores = otherPrefMatches
                        .map((s) => s.routeScore ?? null)
                        .filter((v): v is number => typeof v === 'number');
                      const minOtherDrive = otherDriveTotals.length
                        ? Math.min(...otherDriveTotals)
                        : null;
                      const maxOtherRoute = otherRouteScores.length
                        ? Math.max(...otherRouteScores)
                        : null;

                      // STRICT wins only — must beat all other matches (not tie). A tie means
                      // there's no honest reason to elevate this specific slot, so we stay quiet.
                      const DRIVE_DELTA_MIN = 5; // require a meaningful margin (minutes)
                      const ROUTE_DELTA_PCT = 5; // require a meaningful margin (route score points)
                      const isStrictlyClosest = minOtherDrive != null
                        && slotTotalDrive > 0
                        && slotTotalDrive < minOtherDrive
                        && (minOtherDrive - slotTotalDrive) >= DRIVE_DELTA_MIN;
                      const slotRouteScore = slot.routeScore ?? null;
                      const isStrictlyBestRoute = slotRouteScore != null
                        && maxOtherRoute != null
                        && slotRouteScore > maxOtherRoute
                        && (slotRouteScore - maxOtherRoute) >= ROUTE_DELTA_PCT;

                      const isOnlyMatch = totalPrefMatches === 1 && prefDateMatches && prefTimeMatches;

                      // Build a one-line headline reason. Only claim a route/drive advantage when
                      // it's actually true — otherwise just describe what the slot is honestly.
                      let preferenceReason = '';
                      if (prefDateMatches && prefTimeMatches) {
                        if (isOnlyMatch) {
                          preferenceReason = "Only slot matching customer's date & time";
                        } else if (isStrictlyBestRoute && isStrictlyClosest) {
                          preferenceReason = `Best route + lowest drive time of ${totalPrefMatches} preference matches`;
                        } else if (isStrictlyBestRoute) {
                          preferenceReason = `Best route among ${totalPrefMatches} preference matches`;
                        } else if (isStrictlyClosest) {
                          preferenceReason = `Lowest drive time among ${totalPrefMatches} preference matches`;
                        } else {
                          preferenceReason = "Matches customer's preferred date & time";
                        }
                      } else if (prefDateMatches) {
                        preferenceReason = "On customer's preferred date";
                      } else if (prefTimeMatches) {
                        preferenceReason = `${customerPreferredTimeLabel} slot · requested date unavailable`;
                      } else {
                        preferenceReason = 'Closest available to customer request';
                      }

                      const hasMoreDetails = prefInsights.length > 0 || hasRouteData || showStaleWarning
                        || isStrictlyBestRoute || isStrictlyClosest || isOnlyMatch;

                      const labelText = prefDateMatches ? 'Customer Preference' : 'Next Best Option';
                      const labelClass = prefDateMatches
                        ? 'text-ink-900 bg-pink-100'
                        : 'text-ink-900 bg-amber-50';
                      const reasonText = preferenceReason;

                      // Highlight when this preference slot is also conveniently close
                      // to a real neighboring appointment — same color tiers as the
                      // dedicated Proximity card so the signal is consistent.
                      const prefNeighbor = neighborForSlot(slot);
                      const prefClosestMiles = prefNeighbor?.miles ?? null;
                      const prefProximityTier = getProximityTier(prefClosestMiles);
                      const prefDistanceLabel = prefNeighbor
                        ? formatDistance(prefNeighbor.miles, distUnit)
                        : null;
                      const prefPhrase = prefNeighbor
                        ? proximityBadgePhrase(prefNeighbor)
                        : 'away';

                      return (
                        <div key={`customer-${slot.id}`}>
                          <div
                            onClick={() => handleSlotSelect(slot)}
                            className={`
                              group relative rounded-xl cursor-pointer transition-all duration-200 overflow-hidden bg-surface
                              ${isSelected
                                ? 'ring-2 ring-ink-900 ring-offset-2 ring-offset-white shadow-[0_8px_24px_-12px_rgba(15,23,42,0.25)]'
                                : 'border border-ink-200 hover:border-ink-300 hover:shadow-[0_4px_14px_-8px_rgba(15,23,42,0.18)]'
                              }
                            `}
                          >
                            {/* Slim accent rail — pink for matching preference, amber when off-date. */}
                            <div className={`absolute inset-y-0 left-0 w-0.5 ${prefDateMatches ? 'bg-pink-400' : 'bg-amber-400'}`} />

                            <div className="px-4 py-3.5 flex items-center gap-3.5">
                              <div className={`
                                w-7 h-7 rounded-full flex items-center justify-center transition-all flex-shrink-0
                                ${isSelected
                                  ? 'bg-ink-900 text-white'
                                  : 'bg-surface border-2 border-ink-200 text-transparent group-hover:border-ink-400'
                                }
                              `}>
                                <Check className="h-3.5 w-3.5 stroke-[3]" />
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <span className={`text-[10.5px] font-semibold uppercase tracking-[0.06em] flex items-center gap-1 ${prefDateMatches ? 'text-pink-700' : 'text-amber-700'}`}>
                                    <Heart className="h-3 w-3" /> {labelText}
                                  </span>
                                  {prefProximityTier && prefNeighbor && prefDistanceLabel && (
                                    <span
                                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-1 ${prefProximityTier.badgeClass}`}
                                      title={proximityBadgeTitle(prefNeighbor, prefDistanceLabel)}
                                    >
                                      <Navigation2 className="h-2.5 w-2.5" /> {prefDistanceLabel} {prefPhrase}
                                    </span>
                                  )}
                                  {renderDetourBadge(slot)}
                                </div>

                                <div className="flex items-baseline gap-2 flex-wrap mb-1">
                                  <span className="text-[15px] font-semibold text-ink-900 tabular-nums">
                                    {formattedDate}
                                  </span>
                                  {isCalendarToday(slot.suggestedDate) && (
                                    <span className="inline-flex items-center px-1.5 py-px rounded-full text-[9px] font-bold uppercase tracking-wide bg-ink-900 text-white self-center">
                                      Today
                                    </span>
                                  )}
                                  <span className="h-1 w-1 rounded-full bg-ink-300" />
                                  <span className="text-[19px] font-bold text-ink-900 tracking-tight tabular-nums leading-none">
                                    {getDisplaySlotTime(slot.startTime)}
                                  </span>
                                  <span className="text-ink-300 text-base leading-none">–</span>
                                  <span className="text-[13.5px] font-medium text-ink-500 tabular-nums leading-none">
                                    {getDisplaySlotTime(slot.endTime)}
                                  </span>
                                </div>

                                <div className={`text-[12px] font-medium truncate ${prefDateMatches ? 'text-pink-700' : 'text-amber-700'}`}>
                                  {reasonText}
                                </div>

                                <div className="flex items-center gap-2 mt-1 text-[11.5px] text-ink-500 min-w-0">
                                  <AssigneeChip assignee={slotAssignee} className="flex-shrink-0" />
                                  <span className="h-0.5 w-0.5 rounded-full bg-ink-300 flex-shrink-0" />
                                  <span className="flex items-center gap-1 truncate min-w-0">
                                    <MapPin className="h-3 w-3 text-ink-400 flex-shrink-0" />
                                    <span className="truncate text-ink-500">{fullAddress}</span>
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center gap-0.5 flex-shrink-0 self-start">
                                {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openAuditModal(slot, 'preference');
                                  }}
                                  className="p-1.5 text-ink-400 hover:text-ink-900 hover:bg-ink-100 rounded-md transition-colors"
                                  title="Preview this slot on the timeline"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </button>
                                {hasMoreDetails && (
                                  // eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending
                                  <button
                                    onClick={(e) => toggleSlotDetails('preference', e)}
                                    className="px-2 py-1 text-[11px] font-medium text-ink-500 hover:text-ink-900 hover:bg-ink-100 rounded-md transition-colors flex items-center gap-0.5"
                                    title={isExpanded ? 'Hide details' : 'Why this pick'}
                                  >
                                    {isExpanded ? 'Hide' : 'Why'}
                                    <ChevronRight className={`h-3 w-3 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                                  </button>
                                )}
                              </div>
                            </div>

                            {isExpanded && hasMoreDetails && (() => {
                              // The card's identity ("CUSTOMER PREFERENCE" + Heart
                              // icon) and the headline strip already tell the user
                              // this slot matches the customer's window, so the
                              // generic "Matches the customer's requested date and
                              // time window" insight is dropped here to avoid
                              // saying it three times.
                              const extraPrefInsights = prefInsights.filter(
                                (ins) => ins !== "Matches the customer's requested date and time window"
                              );
                              const hasPositiveReasons =
                                isOnlyMatch
                                || isStrictlyBestRoute
                                || isStrictlyClosest
                                || extraPrefInsights.length > 0;
                              const accentTextClass = prefDateMatches ? 'text-pink-700' : 'text-amber-700';
                              const accentIconClass = prefDateMatches ? 'text-pink-500' : 'text-amber-500';
                              const accentBoxClass = prefDateMatches
                                ? 'bg-pink-50/50 border-pink-100'
                                : 'bg-amber-50/50 border-amber-100';

                              return (
                                <div onClick={(e) => e.stopPropagation()} className="px-4 pb-4 border-t border-ink-100 pt-4 space-y-3">
                                  {/* Section eyebrow — same anchor as Top Choice so
                                      the panels feel like part of the same family. */}
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-400">
                                      Why we picked this
                                    </span>
                                    <span className="flex-1 h-px bg-ink-100" />
                                  </div>

                                  {/* Trade-off banner — only when the customer's
                                      requested date isn't available. Kept loud
                                      because that's the actionable detail. */}
                                  {showStaleWarning && (
                                    <div className="px-3 py-2.5 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-2.5">
                                      <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                                      <div className="min-w-0">
                                        <p className="text-[12px] font-semibold text-amber-900 leading-tight">
                                          Customer's requested date isn't available
                                        </p>
                                        <p className="text-[11.5px] text-amber-800/90 mt-1 leading-snug">
                                          They asked for{' '}
                                          <span className="font-semibold text-amber-900">{customerPreferredDateLabel}</span>
                                          {'. '}
                                          {prefTimeMatches
                                            ? <>This is the next available <span className="font-semibold text-amber-900">{customerPreferredTimeLabel.toLowerCase()}</span> slot.</>
                                            : 'This is the nearest available option.'}
                                        </p>
                                      </div>
                                    </div>
                                  )}

                                  {/* Unified positive-reasons list — preserves the
                                      pink "preference" identity but follows the
                                      Top Choice structure: small subhead with an
                                      icon, then a scannable bullet list where the
                                      icon color tells you the type of reason. */}
                                  {hasPositiveReasons && (
                                    <div className={`px-3 py-2.5 rounded-lg border ${accentBoxClass}`}>
                                      <div className="flex items-center gap-1.5 mb-2">
                                        <Heart className={`h-3 w-3 ${accentIconClass}`} />
                                        <span className={`text-[10.5px] font-semibold uppercase tracking-[0.06em] ${accentTextClass}`}>
                                          Why this works
                                        </span>
                                      </div>
                                      <ul className="space-y-1.5">
                                        {isOnlyMatch && (
                                          <li className="text-[12px] text-ink-700 flex items-start gap-2 leading-snug">
                                            <Heart className={`h-3.5 w-3.5 ${accentIconClass} mt-0.5 flex-shrink-0`} />
                                            <span>
                                              <span className="font-medium text-ink-900">Only slot matching the customer's date and time window. </span>
                                              <span className="text-ink-600">No other option fits both at once.</span>
                                            </span>
                                          </li>
                                        )}
                                        {isStrictlyBestRoute && maxOtherRoute != null && (
                                          <li className="text-[12px] text-ink-700 flex items-start gap-2 leading-snug">
                                            <Sparkles className="h-3.5 w-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                                            <span>
                                              <span className="font-medium text-ink-900">Best route score of the preference matches. </span>
                                              <span className="text-ink-600 tabular-nums">{Math.round(slot.routeScore as number)}% vs {Math.round(maxOtherRoute)}% for the next-best option.</span>
                                            </span>
                                          </li>
                                        )}
                                        {isStrictlyClosest && minOtherDrive != null && (
                                          <li className="text-[12px] text-ink-700 flex items-start gap-2 leading-snug">
                                            <Navigation2 className="h-3.5 w-3.5 text-info-500 mt-0.5 flex-shrink-0" />
                                            <span>
                                              <span className="font-medium text-ink-900">Lowest drive time of the preference matches. </span>
                                              <span className="text-ink-600 tabular-nums">{Math.round(slotTotalDrive)} min vs {Math.round(minOtherDrive)} min for the next-best.</span>
                                            </span>
                                          </li>
                                        )}
                                        {extraPrefInsights.map((insight, i) => (
                                          <li key={i} className="text-[12px] text-ink-700 flex items-start gap-2 leading-snug">
                                            <Check className="h-3.5 w-3.5 text-success-500 mt-0.5 flex-shrink-0" />
                                            <span>{insight}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {hasRouteData && (
                                    <div>
                                      <p className="text-[10.5px] font-semibold text-ink-400 uppercase tracking-[0.06em] flex items-center gap-1.5 mb-2">
                                        <Route className="h-3 w-3" /> Route Overview
                                      </p>
                                      {renderRouteOverviewMap(slot)}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Additional slots (index 2+) — Also Available, split by date so
                        the section header never lies about whether these slots are on the
                        customer's preferred date. */}
                    {additionalSlotCandidates.length > 0 && (
                      <>
                        {([
                          {
                            groupKey: 'onPreferredDate' as const,
                            header: effectivePreferredDate
                              ? `More time slots on ${customerPreferredDateLabel}`
                              : 'More time slots',
                          },
                          {
                            groupKey: 'onOtherDates' as const,
                            header: effectivePreferredDate
                              ? 'Other available days'
                              : 'Other available time slots',
                          },
                        ]).flatMap((group) => {
                          const groupBuckets = additionalSlotsByDateAndTimeOfDay[group.groupKey];
                          const groupTotal = groupBuckets.morning.length
                            + groupBuckets.afternoon.length
                            + groupBuckets.evening.length;
                          if (groupTotal === 0) return [] as React.ReactNode[];

                          return [
                            <div key={`${group.groupKey}-header`} className="mt-6 mb-3 flex items-center gap-3">
                              <p className="text-[12px] font-semibold text-ink-700">{group.header}</p>
                              <span className="flex-1 h-px bg-ink-100" />
                              <span className="text-[11px] text-ink-400 tabular-nums">{groupTotal} {groupTotal === 1 ? 'slot' : 'slots'}</span>
                            </div>,
                            ...([
                              { key: 'morning', label: 'Morning' },
                              { key: 'afternoon', label: 'Afternoon' },
                              { key: 'evening', label: 'Evening' }
                            ] as const).map((section) => {
                              const sectionSlots = groupBuckets[section.key];
                              if (sectionSlots.length === 0) return null;

                          return (
                            <div key={`${group.groupKey}-${section.key}`} className="mb-4 last:mb-0">
                              <p className="text-[10.5px] font-semibold text-ink-400 uppercase tracking-[0.08em] mb-2">
                                {section.label}
                              </p>
                              <div className="space-y-1.5">
                                {sectionSlots.map((slot) => {
                                  const isSelected = selectedSlot?.id === slot.id;
                                  const additionalKey = `additional-${slot.id ?? `${slot.suggestedDate}-${slot.startTime}-${slot.assignedUserId || ''}`}`;
                                  const isExpanded = !!slotDetailsExpanded[additionalKey];
                                  const slotDate = slot.suggestedDate ? new Date(slot.suggestedDate) : new Date();
                                  const formattedDate = slotDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                                  const slotAssignee = resolveSlotAssignee(slot);
                                  const matchesPref = doesSlotMatchCustomerPreferenceFully(slot);
                                  const matchesTimeOnly = !matchesPref && doesSlotMatchPreferredDate(slot) && doesSlotMatchPreferredTimeWindow(slot) && effectivePreferredTime;
                                  const fullAddress = [slot.addressLine1, slot.addressLine2, slot.city, slot.state, slot.zipCode]
                                    .filter(Boolean).join(', ') || 'Address not available';
                                  const addlPrevLeg = resolvePrevLeg(slot);
                                  const addlNextLeg = resolveNextLeg(slot);
                                  const driveTimeFrom = addlPrevLeg.minutes ? Math.round(addlPrevLeg.minutes) : null;
                                  const driveTimeTo = addlNextLeg.minutes ? Math.round(addlNextLeg.minutes) : null;
                                  const distanceFrom = addlPrevLeg.miles != null ? formatDistance(addlPrevLeg.miles, distUnit) : undefined;
                                  const distanceTo = addlNextLeg.miles != null ? formatDistance(addlNextLeg.miles, distUnit) : undefined;
                                  const slotInsights = generateSlotInsights(slot, matchesPref ? 'preference' : 'business');
                                  const hasRouteData = driveTimeFrom !== null || driveTimeTo !== null;
                                  const hasMoreDetails = slotInsights.length > 0 || hasRouteData;
                                  const driveSummary = hasRouteData
                                    ? `${driveTimeFrom ?? driveTimeTo ?? '—'} min · ${distanceFrom ?? distanceTo ?? '—'}`
                                    : null;

                                  return (
                                    <div
                                      key={slot.id}
                                      onClick={() => handleSlotSelect(slot)}
                                      className={`
                                        group relative rounded-lg cursor-pointer transition-all duration-150 overflow-hidden bg-surface
                                        ${isSelected
                                          ? 'ring-2 ring-ink-900 ring-offset-1 ring-offset-white border-transparent'
                                          : matchesPref
                                            ? 'border border-pink-200 hover:border-pink-300 hover:bg-pink-50/40'
                                            : 'border border-ink-100 hover:border-ink-200 hover:bg-ink-50/60'
                                        }
                                      `}
                                    >
                                      <div className="px-3 py-2.5 flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-3 min-w-0">
                                          <div className={`
                                            w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all
                                            ${isSelected
                                              ? 'bg-ink-900 text-white'
                                              : 'border-2 border-ink-200 text-transparent group-hover:border-ink-400'
                                            }
                                          `}>
                                            <Check className="h-3 w-3 stroke-[3]" />
                                          </div>
                                          <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <p className="text-[13px] font-semibold text-ink-900 tabular-nums">
                                                <span className="text-ink-700 font-medium">{formattedDate}</span>
                                                {isCalendarToday(slot.suggestedDate) && (
                                                  <span className="ml-1.5 inline-flex items-center px-1.5 py-px rounded-full text-[9px] font-bold uppercase tracking-wide bg-ink-900 text-white align-middle">
                                                    Today
                                                  </span>
                                                )}
                                                <span className="text-ink-300 mx-1.5">·</span>
                                                {getDisplaySlotTime(slot.startTime)}
                                                <span className="text-ink-400 font-normal mx-1">–</span>
                                                <span className="text-ink-500 font-medium">{getDisplaySlotTime(slot.endTime)}</span>
                                              </p>
                                              {matchesPref && (
                                                <span className="text-[10px] font-semibold text-pink-700 bg-pink-50 border border-pink-100 px-1.5 py-0.5 rounded flex items-center gap-1">
                                                  <Heart className="h-2.5 w-2.5" />
                                                  Matches preference
                                                </span>
                                              )}
                                              {matchesTimeOnly && (
                                                <span className="text-[10px] font-medium text-pink-600 bg-pink-50 px-1.5 py-0.5 rounded">
                                                  {customerPreferredTimeLabel} window
                                                </span>
                                              )}
                                              {renderDetourBadge(slot)}
                                            </div>
                                            <div className="flex items-center gap-2 mt-0.5 text-[11.5px] text-ink-500 min-w-0">
                                              <AssigneeChip assignee={slotAssignee} className="flex-shrink-0" />
                                              {driveSummary && (
                                                <>
                                                  <span className="h-0.5 w-0.5 rounded-full bg-ink-300 flex-shrink-0" />
                                                  <span className="flex items-center gap-1 flex-shrink-0 text-ink-500 tabular-nums">
                                                    <Navigation2 className="h-3 w-3 text-ink-400" />
                                                    {driveSummary}
                                                  </span>
                                                </>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-0.5 flex-shrink-0">
                                          {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openAuditModal(slot, matchesPref ? 'preference' : 'business');
                                            }}
                                            className="p-1.5 text-ink-400 hover:text-ink-900 hover:bg-ink-100 rounded-md transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
                                            title="Preview this slot on the timeline"
                                          >
                                            <Eye className="h-3.5 w-3.5" />
                                          </button>
                                          {hasMoreDetails && (
                                            // eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending
                                            <button
                                              onClick={(e) => toggleSlotDetails(additionalKey, e)}
                                              className="p-1.5 text-ink-400 hover:text-ink-900 hover:bg-ink-100 rounded-md transition-colors"
                                              title={isExpanded ? 'Hide details' : 'Show details'}
                                            >
                                              <ChevronRight className={`h-3.5 w-3.5 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                                            </button>
                                          )}
                                        </div>
                                      </div>

                                      {isExpanded && hasMoreDetails && (
                                        <div onClick={(e) => e.stopPropagation()} className="px-3 pb-3 border-t border-ink-100 pt-3 space-y-3">
                                          <div className="flex items-start gap-1.5 text-xs text-ink-500">
                                            <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                                            <span>{fullAddress}</span>
                                          </div>
                                          {slotInsights.length > 0 && (
                                            <div className={`px-3 py-2 rounded-lg border ${
                                              matchesPref ? 'bg-pink-50/60 border-pink-100' : 'bg-success-50/60 border-success-100'
                                            }`}>
                                              <p className={`text-[11px] font-semibold mb-1 flex items-center gap-1.5 ${
                                                matchesPref ? 'text-pink-700' : 'text-success-700'
                                              }`}>
                                                {matchesPref ? <Heart className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
                                                Why this slot
                                              </p>
                                              <ul className="space-y-0.5">
                                                {slotInsights.map((insight, i) => (
                                                  <li key={i} className={`text-[11px] flex items-start gap-1.5 ${
                                                    matchesPref ? 'text-pink-700/80' : 'text-success-700/80'
                                                  }`}>
                                                    <span className={`mt-0.5 flex-shrink-0 ${matchesPref ? 'text-pink-400' : 'text-success-400'}`}>•</span>
                                                    {insight}
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                          {hasRouteData && (
                                            <div>
                                              <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-wide flex items-center gap-1.5 mb-2">
                                                <Route className="h-3 w-3" /> Route Overview
                                              </p>
                                              {renderRouteOverviewMap(slot)}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        }),
                          ];
                        })}
                      </>
                    )}

                    {/* Only 1 slot available */}
                    {availableSlots.length === 1 && (
                      <p className="text-center text-sm text-ink-500 py-2">
                        Only one slot available for this request.
                      </p>
                    )}
                  </div>
                )}

                {/* Exact-time override — admin-only escape hatch when the
                    recommended slots don't fit (e.g. customer asks for
                    1:00 PM specifically). Lives outside the
                    `availableSlots.length === 0` ternary so it's also a
                    fallback when no slots came back at all.

                    TEMPORARILY DISABLED while the backend manual-override
                    fields ship. Tracked in ClickUp 86ba7j4p2 (spec at
                    react/docs/implementation/exact-time-appointment-booking-backend-spec.md).
                    Flip EXACT_TIME_PICKER_ENABLED back to true once the
                    DTO ships and `npm run generate-api` is rerun. */}
                {(() => {
                  const EXACT_TIME_PICKER_ENABLED = true;

                  if (!EXACT_TIME_PICKER_ENABLED) {
                    return (
                      <div className="mt-4 max-w-2xl mx-auto">
                        <div
                          className="rounded-xl border border-ink-200 bg-ink-50/60 overflow-hidden opacity-75 cursor-not-allowed"
                          aria-disabled="true"
                        >
                          <div className="flex items-center justify-between gap-3 px-4 py-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-ink-100 text-ink-400 ring-1 ring-ink-200 flex-shrink-0">
                                <Pencil className="h-3.5 w-3.5" />
                              </span>
                              <div className="min-w-0">
                                <p className="text-[13px] font-semibold text-ink-700">
                                  Pick an exact start time
                                </p>
                                <p className="text-[11px] text-ink-500 leading-snug">
                                  Need 1:00 PM on the dot? This is coming in a future release — for now please pick from the recommended slots above.
                                </p>
                              </div>
                            </div>
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-500 bg-surface border border-ink-200 rounded-full px-2 py-0.5 flex-shrink-0">
                              Coming soon
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                <div className="mt-4 max-w-2xl mx-auto">
                  <div
                    className={`rounded-xl border overflow-hidden transition-colors ${
                      isExactTimeSlotSelected
                        ? 'border-blue-300 bg-blue-50/40 ring-1 ring-blue-200'
                        : 'border-ink-200 bg-ink-50/60'
                    }`}
                  >
                    {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                    <button
                      type="button"
                      onClick={() => setExactExpanded((v) => !v)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-ink-100/70 transition-colors"
                    >
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-blue-50 text-blue-600 ring-1 ring-blue-200 flex-shrink-0">
                          <Pencil className="h-3.5 w-3.5" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold text-ink-900">
                            Or pick an exact start time
                          </p>
                          <p className="text-[11px] text-ink-500 leading-snug">
                            {isExactTimeSlotSelected
                              ? `Booking ${formatTimeString(exactTime)} on this day — tap to adjust.`
                              : 'Need 1:00 PM on the dot? Set a specific time — we\u2019ll keep it within the assignee\u2019s working hours and check their slot is open.'}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`text-[11px] font-semibold uppercase tracking-wider transition-colors flex-shrink-0 ${
                          exactExpanded || isExactTimeSlotSelected ? 'text-blue-700' : 'text-ink-400'
                        }`}
                      >
                        {exactExpanded ? 'Hide' : isExactTimeSlotSelected ? 'Selected' : 'Custom'}
                      </span>
                    </button>

                      {exactExpanded && (
                        <div className="px-4 pb-4 pt-1 border-t border-ink-200/70 bg-surface space-y-3">
                          {!exactPickerAssigneeId && (
                            <p className="text-[11.5px] text-amber-800 leading-relaxed bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                              Pick a preferred provider first so we know whose
                              calendar to check before booking an exact time.
                            </p>
                          )}

                          <div className="space-y-3 pt-3">
                            <label className="flex flex-col gap-1.5">
                              <span className="text-[10.5px] font-semibold text-ink-500 uppercase tracking-wider">
                                Date
                              </span>
                              <input
                                type="date"
                                value={exactDate}
                                onChange={(e) => handleExactDateChange(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-ink-200 rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                              />
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                              <div className="flex flex-col gap-1.5">
                                <span className="text-[10.5px] font-semibold text-ink-500 uppercase tracking-wider">
                                  Start time
                                </span>
                                <TimeOfDayPicker
                                  value={exactTime}
                                  onChange={handleExactStartChange}
                                  ariaLabel="Pick an exact start time"
                                  minTime={exactPickerTimeBounds.minTime}
                                  maxTime={exactPickerTimeBounds.maxTime}
                                />
                              </div>
                              <div className="flex flex-col gap-1.5">
                                <span className="text-[10.5px] font-semibold text-ink-500 uppercase tracking-wider flex items-center justify-between gap-2">
                                  <span>End time</span>
                                  {exactEndTouchedRef.current && (
                                    // eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending
                                    <button
                                      type="button"
                                      onClick={() => {
                                        exactEndTouchedRef.current = false;
                                        clearExactTimeServerAlert();
                                        setExactEndTime(
                                          addMinutesToTimeString(exactTime, exactPickerDurationMinutes),
                                        );
                                      }}
                                      className="text-[10px] font-medium text-blue-600 hover:text-blue-800 normal-case tracking-normal"
                                    >
                                      Reset to {exactPickerDurationMinutes}m
                                    </button>
                                  )}
                                </span>
                                <TimeOfDayPicker
                                  value={exactEndTime}
                                  onChange={handleExactEndChange}
                                  ariaLabel="Pick an exact end time"
                                  minTime={exactPickerTimeBounds.minTime}
                                  maxTime={exactPickerTimeBounds.maxTime}
                                />
                              </div>
                            </div>
                          </div>

                          <div
                            className={`rounded-lg border px-3 py-2.5 flex items-center justify-between gap-3 ${
                              exactConflict
                                ? 'border-danger-200 bg-danger-50'
                                : exactServerConstraint
                                  ? bookingAlert?.tone === 'danger'
                                    ? 'border-danger-200 bg-danger-50'
                                    : 'border-warning-200 bg-warning-50'
                                  : exactPickerOutsideHours
                                    ? 'border-warning-200 bg-warning-50'
                                    : exactPickerIsInPast
                                      ? 'border-warning-200 bg-warning-50'
                                      : exactValidating
                                        ? 'border-ink-200 bg-ink-50'
                                        : exactPickerWindow
                                          ? 'border-success-200 bg-success-50'
                                          : 'border-ink-200 bg-ink-50'
                            }`}
                          >
                            <div className="min-w-0">
                              <p className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-500">
                                New time
                              </p>
                              <p className="text-[14px] font-semibold text-ink-900 tabular-nums truncate">
                                {exactTime && exactPickerWindow
                                  ? `${formatTimeString(exactTime)} – ${formatTimeString(exactPickerWindow.endTimeStr)}`
                                  : 'Pick a date and time'}
                              </p>
                              <p className="text-[11px] text-ink-500">
                                {(() => {
                                  const dur =
                                    exactPickerWindow?.durationMinutes ?? exactPickerDurationMinutes;
                                  const overridden =
                                    exactPickerWindow &&
                                    exactPickerWindow.durationMinutes !== exactPickerDurationMinutes;
                                  const durLabel = `${dur} min${
                                    overridden ? ` (default ${exactPickerDurationMinutes})` : ''
                                  }`;
                                  return exactDate
                                    ? `${formatDateLong(parseLocalDate(exactDate) || new Date())} · ${durLabel}`
                                    : durLabel;
                                })()}
                              </p>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {exactValidating ? (
                                <>
                                  <Loader2 className="h-4 w-4 text-ink-500 animate-spin" />
                                  <span className="text-[11px] font-semibold text-ink-500">Checking…</span>
                                </>
                              ) : exactConflict ? (
                                <>
                                  <AlertTriangle className="h-4 w-4 text-danger-600" />
                                  <span className="text-[11px] font-semibold text-danger-700">Conflict</span>
                                </>
                              ) : exactServerConstraint ? (
                                <>
                                  <AlertTriangle className={`h-4 w-4 ${bookingAlert?.tone === 'danger' ? 'text-danger-600' : 'text-warning-600'}`} />
                                  <span className={`text-[11px] font-semibold ${bookingAlert?.tone === 'danger' ? 'text-danger-700' : 'text-warning-700'}`}>
                                    {bookingAlert?.title ?? 'Can’t book'}
                                  </span>
                                </>
                              ) : exactPickerOutsideHours ? (
                                <>
                                  <AlertTriangle className="h-4 w-4 text-warning-600" />
                                  <span className="text-[11px] font-semibold text-warning-700">Outside hours</span>
                                </>
                              ) : exactPickerIsInPast ? (
                                <>
                                  <AlertTriangle className="h-4 w-4 text-warning-600" />
                                  <span className="text-[11px] font-semibold text-warning-700">In the past</span>
                                </>
                              ) : exactPickerWindow ? (
                                <>
                                  <CheckCircle2 className="h-4 w-4 text-success-600" />
                                  <span className="text-[11px] font-semibold text-success-700">Slot is open</span>
                                </>
                              ) : null}
                            </div>
                          </div>

                          {showTeamAvailability && exactPickerWindow && (teamAvailabilityLoading || teamAvailability.length > 0) && (
                            <TeamMemberAvailabilityCards
                              members={teamAvailability}
                              selectedUserId={exactPickerAssigneeId || null}
                              loading={teamAvailabilityLoading}
                              onSelect={handleSelectTeamMember}
                              outsideTerritoryUserIds={teamAvailabilityEligibility.outsideTerritoryUserIds}
                            />
                          )}

                          {exactConflict && exactConflictLabel && exactConflict.kind === 'overlap' && (
                            <BookingAlert
                              tone="danger"
                              title="Time already booked"
                              message={`This window overlaps ${exactConflict.withName} (${exactConflictLabel}). Pick a different start time.`}
                            />
                          )}

                          {exactConflict && exactConflictLabel && exactConflict.kind === 'drive-time' && (
                            <BookingAlert
                              tone="warning"
                              title="Not enough drive time"
                              message={`Not enough drive time ${exactConflict.side === 'before' ? 'after' : 'before'} ${exactConflict.withName} (${exactConflictLabel}). The assignee would be short by about ${exactConflict.deficitMinutes} min to ${exactConflict.side === 'before' ? 'drive to this address in time.' : 'leave this address and reach the next stop.'}`}
                            />
                          )}

                          {exactPickerOutsideHours && !exactConflict && (
                            <BookingAlert
                              tone="warning"
                              title={
                                exactAssigneeDay
                                  ? `Outside ${exactPickerAssigneeName ? `${exactPickerAssigneeName}’s` : 'the assignee’s'} hours`
                                  : 'Outside company hours'
                              }
                              message={
                                exactPickerOutsideHours.kind === 'non-work-day'
                                  ? exactAssigneeDay
                                    ? `${exactPickerAssigneeName ?? 'The assignee'} isn’t working on this day. Pick a different date, or update their Availability first.`
                                    : 'This date isn’t a company work day, so exact times can’t be booked on it. Pick a different date, or update the work schedule in Settings first.'
                                  : `Available hours on this day are ${exactPickerOutsideHours.hoursLabel}. Pick a time that starts and ends inside those hours.`
                              }
                            />
                          )}

                          {exactPickerIsInPast && !exactConflict && !exactPickerOutsideHours && (
                            <BookingAlert
                              tone="warning"
                              title="Time is in the past"
                              message="The selected time is already in the past. Pick a future start time."
                            />
                          )}

                          {exactServerConstraint && bookingAlert && !exactConflict && !exactPickerOutsideHours && (
                            <BookingAlert
                              tone={bookingAlert.tone}
                              title={bookingAlert.title}
                              message={bookingAlert.message}
                              hint={bookingAlert.hint}
                            />
                          )}

                        {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                        <button
                          type="button"
                          onClick={handleConfirmExactTime}
                          disabled={!exactCanConfirm || !exactPickerAssigneeId}
                          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          {isExactTimeSlotSelected ? 'Update exact time' : 'Use this exact time'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                  );
                })()}

                {/* Spacer for sticky footer */}
                {selectedSlot && <div className="h-20" />}

                {/* Debug Section - hidden by default; enable with localStorage.setItem('routemize.debug.slots', '1') */}
                {availableSlots.length > 0 && typeof window !== 'undefined' && window.localStorage?.getItem('routemize.debug.slots') === '1' && (
                  <div className="mt-8 border-t border-ink-300 pt-6">
                    {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                    <button
                      onClick={() => setShowDebugSection(!showDebugSection)}
                      className="flex items-center gap-2 text-sm font-medium text-ink-500 hover:text-ink-700 transition-colors"
                    >
                      <Settings className="h-4 w-4" />
                      <span>Debug Information</span>
                      <ChevronRight className={`h-4 w-4 transition-transform ${showDebugSection ? 'rotate-90' : ''}`} />
                    </button>

                    {showDebugSection && (
                      <div className="mt-4 space-y-4">
                        {/* Link to Debug Session */}
                        {availableSlots[0]?.slotRequestId && (
                          <div className="p-4 bg-ink-50 rounded-lg border border-ink-200">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-ink-700">Debug Session</span>
                              <a
                                href={`/admin/scheduling-debug?slotRequestId=${availableSlots[0].slotRequestId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                <span>Open Debug Sessions</span>
                                <ChevronRight className="h-3 w-3" />
                              </a>
                            </div>
                            <div className="text-xs text-ink-500 font-mono">
                              Request ID: {createdRequestId || existingRequestForContact?.id || 'N/A'}
                            </div>
                            <div className="text-xs text-ink-500 font-mono">
                              Slot Request ID: {availableSlots[0].slotRequestId}
                            </div>
                          </div>
                        )}

                        {/* Raw API Response */}
                        <div className="p-4 bg-ink-900 rounded-lg border border-ink-700">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-ink-300">API Response Data</span>
                            {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(JSON.stringify(availableSlots, null, 2));
                              }}
                              className="text-xs text-ink-400 hover:text-white transition-colors"
                            >
                              Copy JSON
                            </button>
                          </div>
                          <pre className="text-xs text-green-400 overflow-x-auto max-h-96 overflow-y-auto font-mono whitespace-pre-wrap">
                            {JSON.stringify(availableSlots, null, 2)}
                          </pre>
                        </div>

                        {/* Slot Summary Table */}
                        <div className="p-4 bg-surface rounded-lg border border-ink-200">
                          <div className="text-sm font-medium text-ink-700 mb-3">Slots Summary</div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-xs">
                              <thead>
                                <tr className="bg-ink-50">
                                  <th className="px-2 py-1 text-left font-medium text-ink-600">#</th>
                                  <th className="px-2 py-1 text-left font-medium text-ink-600">Date</th>
                                  <th className="px-2 py-1 text-left font-medium text-ink-600">Time</th>
                                  <th className="px-2 py-1 text-left font-medium text-ink-600">Score</th>
                                  <th className="px-2 py-1 text-left font-medium text-ink-600">User</th>
                                  <th className="px-2 py-1 text-left font-medium text-ink-600">Prev Travel</th>
                                  <th className="px-2 py-1 text-left font-medium text-ink-600">Next Travel</th>
                                  <th className="px-2 py-1 text-left font-medium text-ink-600">Notes</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-ink-100">
                                {availableSlots.map((slot, index) => (
                                  <tr key={slot.id} className="hover:bg-ink-50">
                                    <td className="px-2 py-1 text-ink-600">{index + 1}</td>
                                    <td className="px-2 py-1 text-ink-900">{slot.suggestedDate?.split('T')[0]}</td>
                                    <td className="px-2 py-1 text-ink-900">{slot.startTime} - {slot.endTime}</td>
                                    <td className="px-2 py-1">
                                      <span className={`px-1.5 py-0.5 rounded text-white ${
                                        (slot.routeScore || 0) >= 80 ? 'bg-green-500' :
                                        (slot.routeScore || 0) >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                      }`}>
                                        {Math.round(slot.routeScore || 0)}%
                                      </span>
                                    </td>
                                    <td className="px-2 py-1 text-ink-600">{slot.userDetail?.name || 'Unassigned'}</td>
                                    <td className="px-2 py-1 text-ink-600">
                                      {slot.previousAppointmentTravelTimeMinutes
                                        ? `${Math.round(slot.previousAppointmentTravelTimeMinutes)}min / ${slot.previousAppointmentDistanceMiles != null ? formatDistance(slot.previousAppointmentDistanceMiles, distUnit) : '—'}`
                                        : 'N/A'}
                                    </td>
                                    <td className="px-2 py-1 text-ink-600">
                                      {slot.nextAppointmentTravelTimeMinutes
                                        ? `${Math.round(slot.nextAppointmentTravelTimeMinutes)}min / ${slot.nextAppointmentDistanceMiles != null ? formatDistance(slot.nextAppointmentDistanceMiles, distUnit) : '—'}`
                                        : 'N/A'}
                                    </td>
                                    <td className="px-2 py-1 text-ink-500 max-w-[200px] truncate" title={slot.meetingNotes || ''}>
                                      {slot.meetingNotes || '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Match Factors */}
                        {availableSlots.some(s => s.matchFactors && s.matchFactors.length > 0) && (
                          <div className="p-4 bg-surface rounded-lg border border-ink-200">
                            <div className="text-sm font-medium text-ink-700 mb-3">Match Factors</div>
                            <div className="space-y-2">
                              {availableSlots.map((slot, index) => (
                                slot.matchFactors && slot.matchFactors.length > 0 && (
                                  <div key={slot.id} className="text-xs">
                                    <span className="font-medium text-ink-700">Slot {index + 1}:</span>
                                    <div className="ml-4 mt-1 space-y-0.5">
                                      {slot.matchFactors.map((factor, fIndex) => (
                                        <div key={fIndex} className="flex items-center gap-2">
                                          <span className={`w-2 h-2 rounded-full ${factor.isMatch ? 'bg-green-500' : 'bg-red-500'}`} />
                                          <span className="text-ink-600">{factor.key}: {factor.label}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Route Metrics */}
                        {availableSlots.some(s => s.routeMetrics && s.routeMetrics.length > 0) && (
                          <div className="p-4 bg-surface rounded-lg border border-ink-200">
                            <div className="text-sm font-medium text-ink-700 mb-3">Route Metrics</div>
                            <div className="space-y-3">
                              {availableSlots.map((slot, index) => (
                                slot.routeMetrics && slot.routeMetrics.length > 0 && (
                                  <div key={slot.id} className="text-xs border-b border-ink-100 pb-2 last:border-0">
                                    <span className="font-medium text-ink-700">Slot {index + 1} ({slot.startTime}):</span>
                                    <div className="ml-4 mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5">
                                      {slot.routeMetrics.map((metric, mIndex) => (
                                        <div key={mIndex} className="flex items-center justify-between">
                                          <span className="text-ink-500">{metric.label || metric.key}:</span>
                                          <span className="text-ink-900 font-mono">{metric.value}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                </div>

                {/* Sticky Footer — appears the moment a slot is chosen.
                    The selected time is mirrored here so the admin's eye has a single anchor
                    to confirm aloud before pressing Continue. Slight elevation keeps it
                    visually separate from the scroll content above. */}
                {selectedSlot && (
                  <div className="sticky bottom-0 left-0 right-0 px-4 sm:px-6 py-3 bg-surface/95 backdrop-blur-md border-t border-ink-200 shadow-[0_-8px_24px_-12px_rgba(15,23,42,0.12)]">
                    {bookingAlert && !(exactExpanded && exactServerConstraint) && (
                      <div className="max-w-3xl mx-auto mb-3">
                        <BookingAlert
                          tone={bookingAlert.tone}
                          title={bookingAlert.title}
                          message={bookingAlert.message}
                          hint={bookingAlert.hint}
                        />
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-3 max-w-3xl mx-auto">
                      {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                      <button
                        onClick={() => {
                          setShowAvailableSlots(false);
                          setAvailableSlots([]);
                          setSelectedSlot(null);
                          setNoAvailabilityInfo(null);
                          setIsRoutemizing(false);
                          setCurrentStep('preferences');
                          setUserChangedPreferences(true);
                          setCompletedSteps(prev => prev.filter(s => s !== 'preferences' && s !== 'booking'));
                        }}
                        className="text-[13px] px-3 py-2 text-ink-500 hover:text-ink-900 hover:bg-ink-50 rounded-lg transition-colors font-medium flex items-center gap-1 flex-shrink-0"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="hidden sm:inline">Change preferences</span>
                        <span className="sm:hidden">Back</span>
                      </button>

                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0">
                          <Check className="h-4 w-4 text-emerald-600 stroke-[2.5]" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-ink-400 leading-none">
                            Booking
                          </div>
                          <div className="font-semibold text-ink-900 text-[14px] truncate tabular-nums mt-0.5">
                            {formatDateMedium(parseLocalDate(selectedSlot.suggestedDate) ?? new Date())}
                            <span className="text-ink-300 mx-1.5">·</span>
                            {getDisplaySlotTime(selectedSlot.startTime)}
                          </div>
                        </div>
                      </div>

                      {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                      <button
                        onClick={() => {
                          if (bookingAlert?.blocksContinue) return;
                          setCurrentStep('confirmation');
                          setCompletedSteps(prev => [...prev.filter(s => s !== 'booking'), 'booking']);
                        }}
                        disabled={!!bookingAlert?.blocksContinue}
                        className="px-4 py-2 bg-ink-900 text-white text-[13px] font-semibold rounded-lg hover:bg-ink-800 active:bg-ink-950 transition-all flex items-center gap-1.5 flex-shrink-0 shadow-[0_4px_12px_-4px_rgba(15,23,42,0.4)] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-ink-900"
                      >
                        <span>Continue</span>
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Confirmation Step - Premium minimal design */}
            {currentStep === 'confirmation' && selectedSlot && (
              <div className="p-4 sm:p-5">
                {/* Outside service area indicator on confirmation — kept compact so it
                    doesn't shout, but still visible right above the confirm button. */}
                {isOutsideServiceArea && (
                  <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-3.5 w-3.5 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-amber-900">Outside service area</p>
                      <p className="text-xs text-amber-800 leading-relaxed mt-0.5">
                        {outsideServiceAreaDetail || 'This address is beyond the configured service radius.'}
                      </p>
                      <p className="text-[11px] text-amber-700/80 mt-1">
                        The request will be flagged as outside service area on the dashboard.
                      </p>
                    </div>
                  </div>
                )}

                {/* Appointment Summary - Hero section. Leads the step: what the
                    operator reads back to the customer on the phone. Optional
                    inputs (notes, access code) sit below in a disclosure so an
                    empty textarea can't push this below the fold. */}
                <div className="text-center mb-4 pb-4 border-b border-ink-100">
                  <div className="text-3xl font-bold text-ink-900 tracking-tight">
                    {getDisplaySlotTime(selectedSlot.startTime)} – {getDisplaySlotTime(selectedSlot.endTime)}
                  </div>
                  <div className="text-ink-500 mt-1">
                    {formatDateFull(parseLocalDate(selectedSlot.suggestedDate) ?? new Date())}
                    {isCalendarToday(selectedSlot.suggestedDate) && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-ink-900 text-white align-middle">
                        Today
                      </span>
                    )}
                  </div>
                  {/* Assigned team member — the first thing a scheduler is
                      asked on the phone ("who's coming?"), so it gets its own
                      card with a photo instead of a chip in the meta row. */}
                  <div className="flex justify-center mt-3">
                    {selectedSlotAssignee ? (() => {
                      // Same tint as the slot chips so the person the operator
                      // picked upstream is visibly the same person here.
                      const { color, textColor } = getAssigneeColors(selectedSlotAssignee);
                      return (
                        <div
                          className="inline-flex items-center gap-3 max-w-full pl-2.5 pr-4 py-2.5 rounded-2xl border-2"
                          style={{ backgroundColor: `${color}14`, borderColor: `${color}59` }}
                        >
                          <ProviderAvatar option={selectedSlotAssignee} size="lg" />
                          <div className="min-w-0 text-left">
                            <div
                              className="text-[10.5px] font-semibold uppercase tracking-[0.08em] leading-none"
                              style={{ color: textColor }}
                            >
                              Assigned to
                            </div>
                            <div className="text-base font-bold text-ink-900 truncate mt-1 leading-tight">
                              {formatProviderName(selectedSlotAssignee)}
                            </div>
                            {selectedSlotAssignee.email && (
                              <div className="text-[11.5px] text-ink-500 truncate">
                                {selectedSlotAssignee.email}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })() : (
                      <div className="inline-flex items-center gap-3 max-w-full pl-2.5 pr-4 py-2.5 rounded-2xl bg-amber-50 border-2 border-amber-200">
                        <div className="w-12 h-12 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-amber-600" />
                        </div>
                        <div className="min-w-0 text-left">
                          <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-amber-700 leading-none">
                            Assigned to
                          </div>
                          <div className="text-base font-bold text-amber-900 truncate mt-1 leading-tight">
                            Not assigned yet
                          </div>
                          <div className="text-[11.5px] text-amber-700 truncate">
                            Smart Assignment picks the team member on booking
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-center gap-3 mt-2.5 text-sm text-ink-500">
                    <span>{selectedSlotDurationLabel}</span>
                    <span>·</span>
                    <span className="text-accent-600 font-medium flex items-center gap-1">
                      <Sparkles className="h-3 w-3" /> Smart Routed
                    </span>
                  </div>
                </div>

                {/* Who / where / what — one compact block instead of three
                    stacked cards, so the whole booking fits on one screen. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 mb-4">
                  <div className="min-w-0">
                    <div className="text-[10.5px] font-semibold text-ink-400 uppercase tracking-[0.06em] mb-1">Customer</div>
                    <div className="text-sm font-semibold text-ink-900 truncate">{firstName} {lastName}</div>
                    <div className="text-[12.5px] text-ink-500 truncate">{email}</div>
                    <div className="text-[12.5px] text-ink-500">{formatPhoneNumber(phone)}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10.5px] font-semibold text-ink-400 uppercase tracking-[0.06em] mb-1">Location</div>
                    <div className="text-sm text-ink-800">{address}</div>
                    <div className="text-[12.5px] text-ink-500">{city}, {state} {zipCode}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10.5px] font-semibold text-ink-400 uppercase tracking-[0.06em] mb-1">Service</div>
                    <div className="text-sm font-semibold text-ink-900 truncate">{eventType?.name}</div>
                  </div>
                  {(existingRequestForContact?.projectDetail || existingRequestForContact?.notes) && (
                    <div className="min-w-0">
                      <div className="flex items-center gap-1 text-[10.5px] font-semibold text-blue-600 uppercase tracking-[0.06em] mb-1">
                        <Info className="w-3 h-3" />
                        Project Details
                        <span className="font-normal normal-case tracking-normal text-blue-400">from request</span>
                      </div>
                      <p className="text-[12.5px] text-ink-700 leading-relaxed whitespace-pre-wrap max-h-24 overflow-y-auto">
                        {existingRequestForContact.projectDetail || existingRequestForContact.notes}
                      </p>
                    </div>
                  )}
                </div>

                {/* Notifications — two columns of slim rows. These are usually
                    left at their defaults, so they shouldn't cost four
                    full-width cards worth of scrolling. */}
                <div className="mb-4">
                  <div className="text-[10.5px] font-semibold text-ink-400 uppercase tracking-[0.06em] mb-2">
                    Notifications
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {([
                      { key: 'sms-confirmation', label: 'SMS Confirmation', icon: MessageSquare, checked: sendSmsConfirmation, set: setSendSmsConfirmation, needsSms: true },
                      { key: 'email-confirmation', label: 'Email Confirmation', icon: Mail, checked: sendEmailConfirmation, set: setSendEmailConfirmation, needsSms: false },
                      { key: 'sms-reminders', label: 'SMS Reminders', icon: Bell, checked: sendSmsReminders, set: setSendSmsReminders, needsSms: true },
                      { key: 'email-reminders', label: 'Email Reminders', icon: Mail, checked: sendEmailReminders, set: setSendEmailReminders, needsSms: false },
                    ] as const).map(({ key, label, icon: Icon, checked, set, needsSms }) => {
                      const blocked = needsSms && !smsProviderConnected;
                      return (
                        <div
                          key={key}
                          onClick={() => {
                            if (blocked) return;
                            userModifiedPrefsRef.current = true;
                            set(!checked);
                          }}
                          className={`flex items-center justify-between gap-2 pl-3 pr-2.5 py-2 rounded-lg border transition-colors ${
                            blocked
                              ? 'border-amber-200 bg-amber-50/50 cursor-not-allowed'
                              : checked
                                ? 'border-success-300 bg-success-50 cursor-pointer'
                                : 'border-ink-200 hover:border-ink-300 cursor-pointer'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${blocked ? 'text-amber-500' : checked ? 'text-success-600' : 'text-ink-400'}`} />
                            <span className={`text-[13px] truncate ${blocked ? 'text-amber-700' : checked ? 'text-success-700 font-medium' : 'text-ink-600'}`}>
                              {label}
                            </span>
                            {blocked && (
                              <span className="text-[10.5px] text-amber-600 flex-shrink-0">no provider</span>
                            )}
                          </div>
                          <Switch
                            size="sm"
                            checked={!blocked && checked}
                            disabled={blocked}
                            tabIndex={-1}
                            aria-hidden="true"
                            className="pointer-events-none flex-shrink-0"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Internal notes + access code — collapsed by default. They're
                    used on a minority of bookings but an empty textarea and
                    input were pushing the appointment itself off screen. */}
                <div className="rounded-xl border border-ink-200 overflow-hidden">
                  {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                  <button
                    type="button"
                    onClick={() => setConfirmationExtrasOpen(o => !o)}
                    aria-expanded={confirmationExtrasOpen}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-ink-50 hover:bg-ink-100 transition-colors text-left"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <FileText className="h-3.5 w-3.5 text-ink-400 flex-shrink-0" />
                      <span className="text-[13px] font-medium text-ink-700">Notes &amp; access code</span>
                      <span className="text-[11.5px] text-ink-400 truncate">
                        {notes.trim() || accessCode.trim()
                          ? [notes.trim() && 'notes added', accessCode.trim() && `code ${accessCode.trim()}`]
                              .filter(Boolean).join(' · ')
                          : 'optional — internal only'}
                      </span>
                    </span>
                    <ChevronRight
                      className={`h-4 w-4 text-ink-400 flex-shrink-0 transition-transform ${confirmationExtrasOpen ? 'rotate-90' : ''}`}
                    />
                  </button>

                  {confirmationExtrasOpen && (
                    <div className="p-3 space-y-3 border-t border-ink-200">
                      <div>
                        <label className="flex items-center gap-1.5 text-[10.5px] font-semibold text-ink-500 uppercase tracking-[0.06em] mb-1.5">
                          Appointment Notes
                          <span className="font-normal normal-case tracking-normal text-ink-400">for your team</span>
                        </label>
                        <textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="What to bring, where to park, special access info..."
                          className="w-full px-3 py-2 text-sm border border-ink-200 rounded-lg bg-surface resize-none focus:outline-none focus:ring-2 focus:ring-ink-400 focus:border-transparent placeholder:text-ink-400 hover:border-ink-300 transition-colors"
                          rows={2}
                        />
                      </div>
                      <div>
                        <label className="flex items-center gap-1.5 text-[10.5px] font-semibold text-ink-500 uppercase tracking-[0.06em] mb-1.5">
                          <KeyRound className="w-3 h-3" />
                          Gate / Access Code
                        </label>
                        <input
                          type="text"
                          value={accessCode}
                          onChange={(e) => setAccessCode(e.target.value)}
                          placeholder="e.g., #1234, *9999, or gate instructions"
                          className="w-full px-3 py-2 text-sm border border-ink-200 rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-ink-400 focus:border-transparent placeholder:text-ink-400 hover:border-ink-300 transition-colors"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer - Clean and consistent */}
          <div className="flex-shrink-0 px-6 py-4 bg-surface border-t border-ink-100">
            {/* Error Display — skip ExactTime codes on the booking step; those
                render in the picker / sticky footer so they sit next to the time. */}
            {(bookingAlert || errors.general) &&
              !noAvailabilityInfo?.isFull &&
              !(currentStep === 'booking' && isExactTimeConstraintCode(bookingAlert?.code)) && (
              <div className="mb-3">
                <BookingAlert
                  tone={bookingAlert?.tone ?? 'danger'}
                  title={bookingAlert?.title ?? "Couldn't complete this step"}
                  message={bookingAlert?.message ?? errors.general}
                  hint={bookingAlert?.hint}
                />
              </div>
            )}

            {/* Hide footer buttons when outside service area or no-availability UI is shown */}
            {noAvailabilityInfo?.isFull ? null : (
            <div className="flex items-center justify-end gap-3">
              {currentStep === 'confirmation' && selectedSlot ? (
                <>
                  <Button variant="ghost" onClick={handlePrevStep}>
                    <ChevronLeft className="h-4 w-4" />
                    Back
                  </Button>
                  <Button onClick={handleSubmit} loading={isSubmitting}>
                    {isSubmitting ? 'Creating...' : (
                      <>
                        <Check className="h-4 w-4" />
                        <span>Confirm Booking</span>
                      </>
                    )}
                  </Button>
                </>
              ) : showAvailableSlots && selectedSlot ? (
                // Sticky footer handles this case - don't show anything here
                null
              ) : showAvailableSlots && !selectedSlot && !noAvailabilityInfo?.isFull ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowAvailableSlots(false);
                    setAvailableSlots([]);
                    setSelectedSlot(null);
                    setNoAvailabilityInfo(null);
                    setIsRoutemizing(false);
                    setCurrentStep('preferences');
                    setUserChangedPreferences(true);
                  }}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Change Preferences
                </Button>
              ) : currentStep === 'preferences' && !isRoutemizing ? (
                <Button onClick={handleNextStep} disabled={!preferredDate || isSubmitting} loading={isSubmitting}>
                  <Sparkles className="h-4 w-4" />
                  <span>{isSubmitting ? 'Creating...' : 'Find Available Slots'}</span>
                </Button>
              ) : isRoutemizing ? (
                // Hide buttons during AI Route Optimization loading
                null
              ) : (
                <Button onClick={handleNextStep}>
                  <span>Continue</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </div>
            )}
          </div>
        </div>

      {/* Loading overlay for checking existing request */}
      {checkingExistingRequest && (
        <div className="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-[60]">
          <div className="bg-surface rounded-xl shadow-2xl p-5 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-ink-200 border-t-ink-900 rounded-full animate-spin"></div>
            <span className="text-sm text-ink-700">Checking existing requests...</span>
          </div>
        </div>
      )}

      {/* Quick Audit Modal */}
      {auditModal?.isOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-60" onClick={() => setAuditModal(null)} />
          <div className="relative bg-surface rounded-2xl shadow-2xl w-full max-w-3xl max-h-[calc(100dvh-2rem)] overflow-hidden flex flex-col">
            <div className={`px-6 py-4 border-b ${
              auditModal.slotType === 'business' ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200' :
              auditModal.slotType === 'preference' ? 'bg-gradient-to-r from-blue-50 to-blue-50 border-blue-200' :
              'bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold mb-2 ${
                    auditModal.slotType === 'business' ? 'bg-green-500 text-white' :
                    auditModal.slotType === 'preference' ? 'bg-blue-500 text-white' :
                    'bg-blue-500 text-white'
                  }`}>
                    {auditModal.slotType === 'business' && <Building2 className="h-3 w-3" />}
                    {auditModal.slotType === 'preference' && <Heart className="h-3 w-3" />}
                    {auditModal.slotType === 'closest' && <Navigation2 className="h-3 w-3" />}
                    {auditModal.slotTypeLabel}
                  </div>
                  <h3 className="text-lg font-bold text-ink-900">Timeline Audit</h3>
                  <p className="text-sm text-ink-600 mt-0.5">{auditModal.slotDateFormatted} - {auditModal.teamMemberName}</p>
                  <p className="text-xs text-ink-500 mt-1">{auditModal.slotTypeDescription}</p>
                </div>
                {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
                <button onClick={() => setAuditModal(null)} className="p-2 hover:bg-surface/50 rounded-lg">
                  <X className="h-5 w-5 text-ink-500" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {auditLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-ink-600">Loading timeline...</span>
                </div>
              ) : (
                <div>
                  {(() => {
                    // Use slot data to build timeline - works even without API timelineData
                    const slot = auditModal.slotData;
                    const hypothetical = auditModal.hypotheticalSlot;
                    
                    // Use resolved addresses from auditModal (fetched from route preferences + user/office data)
                    const startingAddress = auditModal.startingAddress;
                    const endingAddress = auditModal.endingAddress;
                    
                    // Format time for display
                    const formatTimeDisplay = (timeStr: string | null | undefined): string => {
                      if (!timeStr) return '--:--';
                      let hours: number, minutes: number;
                      if (timeStr.includes('T')) {
                        const date = new Date(timeStr);
                        hours = date.getHours();
                        minutes = date.getMinutes();
                      } else {
                        const parts = timeStr.split(':');
                        hours = parseInt(parts[0], 10) || 0;
                        minutes = parseInt(parts[1], 10) || 0;
                      }
                      const period = hours >= 12 ? 'PM' : 'AM';
                      const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
                      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
                    };
                    
                    // Theme colors based on slot type
                    const themeColors = auditModal.slotType === 'business' ? {
                      bg: 'bg-green-50',
                      border: 'border-l-green-400',
                      timeBg: 'bg-green-400',
                      badgeBg: 'bg-green-500',
                      textDark: 'text-green-900',
                      textMedium: 'text-green-700',
                      textLight: 'text-green-100',
                      iconBg: 'bg-green-100',
                      iconColor: 'text-green-600'
                    } : auditModal.slotType === 'preference' ? {
                      bg: 'bg-blue-50',
                      border: 'border-l-blue-400',
                      timeBg: 'bg-blue-400',
                      badgeBg: 'bg-blue-500',
                      textDark: 'text-blue-900',
                      textMedium: 'text-blue-700',
                      textLight: 'text-blue-100',
                      iconBg: 'bg-blue-100',
                      iconColor: 'text-blue-600'
                    } : {
                      bg: 'bg-blue-50',
                      border: 'border-l-blue-400',
                      timeBg: 'bg-blue-400',
                      badgeBg: 'bg-blue-500',
                      textDark: 'text-blue-900',
                      textMedium: 'text-blue-700',
                      textLight: 'text-blue-100',
                      iconBg: 'bg-blue-100',
                      iconColor: 'text-blue-600'
                    };
                    
                    // Build entries from slot data (previous -> new -> next)
                    const hasPrevAppt = slot.previousAppointmentLocation && slot.previousAppointmentLocation !== 'None';
                    const hasNextAppt = slot.nextAppointmentLocation && slot.nextAppointmentLocation !== 'None';
                    const prevIsOfficeOrHome = slot.previousAppointmentLocation?.toLowerCase() === 'office' || 
                                               slot.previousAppointmentLocation?.toLowerCase() === 'home' ||
                                               slot.previousAppointmentLocation?.toLowerCase() === 'starting location';
                    const nextIsOfficeOrHome = slot.nextAppointmentLocation?.toLowerCase() === 'office' || 
                                               slot.nextAppointmentLocation?.toLowerCase() === 'home' ||
                                               slot.nextAppointmentLocation?.toLowerCase() === 'end of day';
                    
                    return (
                      <div>
                        {/* Date Header */}
                        <div className="px-6 py-3 bg-ink-50 border-b border-ink-200">
                          <span className="text-xs font-semibold text-ink-600 uppercase tracking-wide">Schedule for {auditModal.slotDateFormatted}</span>
                        </div>
                        
                        {/* Start of Day */}
                        <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
                          <div className="flex items-start gap-3">
                            <div className="bg-green-100 p-2 rounded-lg">
                              <Home className="h-4 w-4 text-green-700" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-bold text-green-800 uppercase tracking-wide mb-1">Start of Day</div>
                              <div className="text-sm font-medium text-green-900 truncate">{startingAddress}</div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Previous Appointment (if exists and is a real appointment) */}
                        {hasPrevAppt && !prevIsOfficeOrHome && (
                          <>
                            <div className="px-6 py-4 bg-surface border-b border-ink-100">
                              <div className="flex items-start gap-4">
                                <div className="w-20 flex-shrink-0 text-center py-2 px-3 rounded-lg bg-ink-100">
                                  <div className="text-sm font-bold text-ink-900">
                                    {formatTimeDisplay(slot.previousAppointmentStartTime)}
                                  </div>
                                  <div className="text-[10px] text-ink-500">
                                    {formatTimeDisplay(slot.previousAppointmentEndTime)}
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <span className="inline-block text-[10px] font-bold text-ink-500 bg-ink-100 px-2 py-0.5 rounded uppercase tracking-wide mb-1">
                                    Previous
                                  </span>
                                  <div className="font-semibold text-ink-900 truncate">Existing Appointment</div>
                                  <div className="text-xs mt-1 truncate flex items-center gap-1 text-ink-500">
                                    <MapPin className="h-3 w-3 flex-shrink-0" />
                                    {slot.previousAppointmentLocation}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                        
                        {/* Drive Time TO the new appointment */}
                        {slot.previousAppointmentTravelTimeMinutes && slot.previousAppointmentTravelTimeMinutes > 0 && (
                          <div className="px-6 py-3 bg-ink-50 border-y border-ink-200 flex items-center justify-center">
                            <div className="flex items-center gap-3 px-4 py-2 bg-surface rounded-full shadow-sm border border-ink-200">
                              <Navigation2 className="h-4 w-4 text-ink-500" />
                              <span className="text-sm font-semibold text-ink-700">
                                {(() => { const m = Math.round(slot.previousAppointmentTravelTimeMinutes!); return m >= 60 ? `${Math.floor(m / 60)} hr ${m % 60} min` : `${m} min`; })()} drive
                              </span>
                              {slot.previousAppointmentDistanceMiles && (
                                <span className="text-sm text-ink-500">
                                  · {formatDistance(slot.previousAppointmentDistanceMiles, distUnit)}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* NEW APPOINTMENT (the proposed slot) */}
                        <div className={`px-6 py-5 ${themeColors.bg} border-l-4 ${themeColors.border}`}>
                          <div className="flex items-start gap-4">
                            <div className={`w-20 flex-shrink-0 text-center py-2.5 px-3 rounded-lg ${themeColors.timeBg} shadow-sm`}>
                              <div className="text-sm font-bold text-white">
                                {formatTimeDisplay(hypothetical.startTime)}
                              </div>
                              <div className={`text-[10px] ${themeColors.textLight}`}>
                                {formatTimeDisplay(hypothetical.endTime)}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className={`inline-block text-[10px] font-bold text-white ${themeColors.badgeBg} px-2 py-0.5 rounded uppercase tracking-wide mb-1`}>
                                ★ New Appointment
                              </span>
                              <div className={`font-semibold ${themeColors.textDark}`}>
                                {selectedContact ? `${selectedContact.name || 'Customer'}` : 'New Booking'}
                              </div>
                              <div className={`text-xs mt-1 flex items-center gap-1 ${themeColors.textMedium}`}>
                                <MapPin className="h-3 w-3 flex-shrink-0" />
                                <span className="truncate">{hypothetical.address}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Drive Time TO the next appointment */}
                        {slot.nextAppointmentTravelTimeMinutes && slot.nextAppointmentTravelTimeMinutes > 0 && (
                          <div className="px-6 py-3 bg-ink-50 border-y border-ink-200 flex items-center justify-center">
                            <div className="flex items-center gap-3 px-4 py-2 bg-surface rounded-full shadow-sm border border-ink-200">
                              <Navigation2 className="h-4 w-4 text-ink-500" />
                              <span className="text-sm font-semibold text-ink-700">
                                {(() => { const m = Math.round(slot.nextAppointmentTravelTimeMinutes!); return m >= 60 ? `${Math.floor(m / 60)} hr ${m % 60} min` : `${m} min`; })()} drive
                              </span>
                              {slot.nextAppointmentDistanceMiles && (
                                <span className="text-sm text-ink-500">
                                  · {formatDistance(slot.nextAppointmentDistanceMiles, distUnit)}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Next Appointment (if exists and is a real appointment) */}
                        {hasNextAppt && !nextIsOfficeOrHome && (
                          <div className="px-6 py-4 bg-surface border-b border-ink-100">
                            <div className="flex items-start gap-4">
                              <div className="w-20 flex-shrink-0 text-center py-2 px-3 rounded-lg bg-ink-100">
                                <div className="text-sm font-bold text-ink-900">
                                  {formatTimeDisplay(slot.nextAppointmentStartTime)}
                                </div>
                                <div className="text-[10px] text-ink-500">
                                  {formatTimeDisplay(slot.nextAppointmentEndTime)}
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="inline-block text-[10px] font-bold text-ink-500 bg-ink-100 px-2 py-0.5 rounded uppercase tracking-wide mb-1">
                                  Next
                                </span>
                                <div className="font-semibold text-ink-900 truncate">Existing Appointment</div>
                                <div className="text-xs mt-1 truncate flex items-center gap-1 text-ink-500">
                                  <MapPin className="h-3 w-3 flex-shrink-0" />
                                  {slot.nextAppointmentLocation}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* End of Day */}
                        <div className="px-6 py-4 bg-gradient-to-r from-red-50 to-rose-50 border-t border-red-200">
                          <div className="flex items-start gap-3">
                            <div className="bg-red-100 p-2 rounded-lg">
                              <Home className="h-4 w-4 text-red-700" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-bold text-red-800 uppercase tracking-wide mb-1">End of Day</div>
                              <div className="text-sm font-medium text-red-900 truncate">{endingAddress}</div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Route Summary */}
                        <div className="px-6 py-4 bg-ink-50 border-t border-ink-200">
                          <div className="text-xs font-semibold text-ink-600 uppercase tracking-wide mb-3">Route Summary</div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-surface rounded-lg p-3 border border-ink-200">
                              <div className="text-xs text-ink-500 mb-1">Total Drive Time</div>
                              <div className="text-lg font-bold text-ink-900">
                                {(() => { const m = Math.round((slot.previousAppointmentTravelTimeMinutes || 0) + (slot.nextAppointmentTravelTimeMinutes || 0)); return m >= 60 ? `${Math.floor(m / 60)} hr ${m % 60} min` : `${m} min`; })()}
                              </div>
                            </div>
                            <div className="bg-surface rounded-lg p-3 border border-ink-200">
                              <div className="text-xs text-ink-500 mb-1">Total Distance</div>
                              <div className="text-lg font-bold text-ink-900">
                                {formatDistance((slot.previousAppointmentDistanceMiles || 0) + (slot.nextAppointmentDistanceMiles || 0), distUnit)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t bg-surface">
              {/* eslint-disable-next-line no-restricted-syntax -- CreateBookingModal specialized button (state-tinted chip/inline action/list option/brand variant); dedicated primitives pending */}
              <button onClick={() => setAuditModal(null)} className="w-full py-2.5 bg-ink-900 text-white rounded-lg font-medium hover:bg-ink-800">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Smart Assignment rule explanation — opened by clicking a
          Smart override / Proximity badge on the preferences step.
          Mirrors the modal in SmartAssignmentPanel so the rule
          documentation is consistent everywhere. */}
      <SmartAssignmentExplanationModal
        explanationKey={openSmartAssignmentExplanation}
        onClose={() => setOpenSmartAssignmentExplanation(null)}
      />

      <AvailabilityHelpModal
        isOpen={showAvailabilityHelp}
        onClose={() => setShowAvailabilityHelp(false)}
        customerName={[firstName, lastName].filter(Boolean).join(' ').trim() || undefined}
        defaultDate={(effectivePreferredDate || '').split('T')[0] || formatDateKey(new Date())}
        earliestOfferedLabel={earliestOfferedSlotLabel || null}
        assignees={availabilityHelpAssignees}
        defaultAssigneeId={resolveSlotAssignee(availableSlots[0])?.id || ''}
        onSubmit={handleAvailabilityHelpSubmit}
      />
    </div>
  );
};

export default CreateBookingModal;